import type { MessageKey } from "./id";

// Typed against the Indonesian source: a missing or extra key fails the build.
export const en: Record<MessageKey, string> = {
  "app.name": "Zerpid",
  "app.tagline": "One system. One flow. No gaps.",

  "common.save": "Save",
  "common.cancel": "Cancel",
  "common.continue": "Continue",
  "common.skip": "Skip",
  "common.back": "Back",
  "common.close": "Close",
  "common.edit": "Edit",
  "common.add": "Add",
  "common.delete": "Remove",
  "common.retry": "Try again",
  "common.loading": "Loading…",
  "common.loadMore": "Load more",
  "common.actions": "Actions",
  "common.status": "Status",
  "common.name": "Name",
  "common.email": "Email",
  "common.password": "Password",
  "common.language": "Language",
  "common.never": "Never",
  "common.comingSoon": "Coming soon",
  "common.reference": "Reference: {id}",
  "common.optional": "(optional)",

  "lang.id": "Bahasa Indonesia",
  "lang.en": "English",

  "role.owner": "Owner / admin",
  "role.warehouse_manager": "Warehouse manager",
  "role.warehouse_staff": "Warehouse staff",
  "role.finance_cs": "Finance / order admin / CS",

  "plan.trial": "Trial",
  "plan.free": "Free",
  "plan.growth": "Growth",
  "plan.scale": "Scale",
  "plan.enterprise": "Enterprise",

  "field.required": "This field is required.",
  "field.invalid_email": "Enter a valid email address.",
  "field.too_short": "Too short.",
  "field.too_long": "Too long.",
  "field.length": "Length is out of range.",
  "field.invalid": "Invalid value.",
  "field.password_hint": "At least 10 characters.",
  "field.password_mismatch": "Passwords do not match.",

  "error.generic": "Something went wrong on our side. Please try again.",
  "error.network": "Cannot reach the server. Check your connection.",
  "error.unauthorized": "Your session has ended. Please sign in again.",
  "error.session_expired": "Your session has ended. Please sign in again.",
  "error.forbidden": "You do not have access to this page.",
  "error.not_found": "Not found.",
  "error.rate_limited": "Too many attempts. Wait a moment, then try again.",
  "error.validation_failed": "Check the highlighted fields.",
  "error.email_taken": "This email is already registered.",
  "error.invalid_credentials": "Email or password is incorrect.",
  "error.email_unverified": "Your email address is not confirmed yet.",
  "error.invalid_token": "This link is invalid or has expired.",
  "error.no_workspace": "Create or join a company first.",
  "error.membership_inactive": "Your access to this company has been deactivated.",
  "error.already_member": "This person is already a member.",
  "error.last_owner": "A company needs at least one active owner.",
  "error.cannot_disable_self": "You cannot deactivate your own access.",
  "error.warehouse_code_taken": "Another warehouse already uses this code.",
  "error.warehouse_required": "Add a warehouse or skip this step.",
  "error.default_warehouse_archive": "The main warehouse cannot be archived.",
  "error.onboarding_step_mismatch": "This step was already completed. Reload the page.",

  "auth.login.title": "Sign in to Zerpid",
  "auth.login.submit": "Sign in",
  "auth.login.forgot": "Forgot password?",
  "auth.login.noAccount": "No account yet?",
  "auth.login.register": "Sign up",
  "auth.login.unverifiedHelp": "Confirm your email first. We can send the link again.",
  "auth.login.resend": "Resend confirmation email",
  "auth.login.newEyebrow": "October 2026",
  "auth.login.newTitle": "New in Zerpid",
  "auth.login.new1": "Lazada now syncs: orders, stock, prices, returns and shipping labels from your Lazada store.",
  "auth.login.new2": "Write with AI: product descriptions drafted from the product editor, or fixed to match a marketplace's correction notes.",
  "auth.login.new3": "Blibli is ready: connect your store, publish products, and revise the ones Blibli sends back.",
  "auth.login.promoTitle": "No account yet?",
  "auth.login.promoBody": "Join the open beta: free, no credit card, and founding members save 30% for their first 12 months.",
  "auth.login.promoCta": "Sign up free",
  "auth.login.help": "Need help? Write to",

  "auth.register.title": "Create your Zerpid account",
  "auth.register.subtitle": "Join the Zerpid open beta. Your account is ready in minutes.",
  "auth.register.perksTitle": "What open beta members get",
  "auth.register.perk1": "Free during the open beta, no credit card.",
  "auth.register.perk2": "Every module open for the first month, with no order limit.",
  "auth.register.perk3": "First access to Zerpid Chat when it launches: buyer chats from every store in one inbox, being built now.",
  "auth.register.perk4": "Founding members save 30% for their first 12 months.",
  "auth.register.perk5": "Pricing is announced before the beta ends, and your data stays.",
  "auth.register.submit": "Create account",
  "auth.register.haveAccount": "Already have an account?",
  "auth.register.login": "Sign in",
  "auth.register.emailTakenAction": "Sign in with this email",

  "auth.verify.pendingTitle": "Check your email",
  "auth.verify.pendingBody": "We sent a confirmation link to {email}. Open it to continue.",
  "auth.verify.resend": "Resend",
  "auth.verify.resendIn": "Resend in {seconds} seconds",
  "auth.verify.resent": "If that email is registered and not yet confirmed, a new link has been sent.",
  "auth.verify.checking": "Confirming your email…",
  "auth.verify.failedTitle": "This link cannot be used",
  "auth.verify.failedBody": "The confirmation link is invalid or has expired. Sign in to request a new one.",

  "auth.forgot.title": "Forgot password",
  "auth.forgot.body": "Enter your account email. We will send a link to set a new password.",
  "auth.forgot.submit": "Send link",
  "auth.forgot.sent": "If that email is registered, a password reset link has been sent. The link is valid for 1 hour.",

  "auth.reset.title": "Set a new password",
  "auth.reset.newPassword": "New password",
  "auth.reset.confirm": "Repeat new password",
  "auth.reset.submit": "Save password",
  "auth.reset.done": "Your password was changed and all previous sessions were ended. Please sign in.",

  "auth.invite.title": "Join {workspace}",
  "auth.invite.body": "You were invited as {role} with the email {email}.",
  "auth.invite.existingAccount": "This email already has a Zerpid account. Enter that account's password to join.",
  "auth.invite.newAccount": "Choose a password for your new account.",
  "auth.invite.submit": "Accept invitation",
  "auth.invite.invalidTitle": "This invitation cannot be used",
  "auth.invite.invalidBody": "The invitation is invalid, already used, revoked, or expired. Ask the company owner to send a new one.",

  "onboarding.title": "Set up Zerpid",
  "onboarding.step.company": "Company",
  "onboarding.step.marketplace": "Marketplaces",
  "onboarding.step.warehouse": "Warehouse",
  "onboarding.company.title": "Company or brand name",
  "onboarding.company.body": "Your team sees this name. You can change it any time in Settings.",
  "onboarding.company.label": "Company / brand name",
  "onboarding.marketplace.title": "Connect marketplaces",
  "onboarding.marketplace.body": "Orders and stock from connected stores land in one list. You can connect stores any time from the Channels menu.",
  "onboarding.marketplace.connect": "Connect",
  "onboarding.marketplace.notYet": "Marketplace connections are being prepared. Skip this step; we will let you know when they are available.",
  "onboarding.warehouse.title": "Add a warehouse",
  "onboarding.warehouse.body": "Where your stock is kept. If you skip, we create one main warehouse you can edit later.",

  "nav.dashboard": "Home",
  "nav.orders": "Orders",
  "nav.aftersales": "Aftersales",
  "nav.promotions": "Promotions",
  "nav.catalog": "SKUs",
  "nav.inventory": "Stock overview",
  "nav.warehouse": "Warehouse",
  "nav.warehouse.inbound": "Receiving",
  "nav.warehouse.locations": "Locations & zones",
  "nav.warehouse.putaway": "Putaway",
  "nav.warehouse.pools": "Order pools",
  "nav.warehouse.waves": "Waves",
  "nav.warehouse.picking": "Picking",
  "nav.warehouse.packing": "Packing",
  "nav.warehouse.outbound": "Handover",
  "nav.warehouse.returns": "Returns receiving",
  "nav.warehouse.transfers": "Transfers",
  "nav.warehouse.counts": "Stock counts",
  "nav.warehouse.variance": "Variance",
  "nav.channels": "Channels",
  "nav.logs": "Activity Log",
  "nav.settings": "Settings",
  "nav.settings.company": "Company",
  "nav.settings.warehouses": "Warehouses",
  "nav.settings.labels": "Labels",
  "nav.settings.users": "Users & Roles",
  "nav.billing": "Subscription",
  "nav.finance": "Finance",
  "nav.analytics": "Analytics",

  "shell.trialBadge": "Trial: {days} days left",
  "shell.trialEnded": "Trial ended",
  "shell.switchWorkspace": "Switch company",
  "shell.logout": "Sign out",
  "shell.profile": "My profile",

  "dashboard.greeting": "Welcome, {name}",
  "dashboard.setup.title": "Next steps",
  "dashboard.setup.warehouse": "Add a warehouse",
  "dashboard.setup.marketplace": "Connect a marketplace store",
  "dashboard.setup.team": "Invite your team",
  "dashboard.card.newOrders": "New orders",
  "dashboard.card.lowStock": "Low stock",
  "dashboard.card.sync": "Sync status",
  "dashboard.card.emptyOrders": "Orders appear here once a marketplace store is connected.",
  "dashboard.card.emptyStock": "Stock appears here once products and warehouses are set up.",
  "dashboard.card.emptySync": "No stores connected yet.",

  "coming.title": "{feature} is coming soon",
  "coming.body": "This feature is not available yet. The menu is in place so you know where to find it.",

  "settings.company.title": "Company",
  "settings.company.name": "Company / brand name",
  "settings.company.locale": "Default language",
  "settings.company.localeHelp": "Used for invitation emails and new users.",
  "settings.company.plan": "Plan",
  "settings.company.trialUntil": "Your trial ends on {date}. After that the plan moves to Free automatically unless you subscribe.",
  "settings.company.saved": "Changes saved.",

  "settings.warehouses.title": "Warehouses",
  "settings.labels.title": "Labels",
  "settings.labels.intro": "Set up the labels printed from Orders. Labels keep the courier format from the marketplace; Zerpid adds information around it so it reads easily while packing.",
  "settings.labels.paper": "Paper",
  "settings.labels.paper.thermal": "Thermal label 100 × 150 mm",
  "settings.labels.paper.thermalHelp": "One label per sheet, for thermal printers.",
  "settings.labels.paper.a4": "A4, 4 labels per sheet",
  "settings.labels.paper.a4Help": "For office printers; cut each sheet in four.",
  "settings.labels.additions": "Added to the label",
  "settings.labels.showTracking": "Large tracking number + barcode at the top",
  "settings.labels.showTrackingHelp": "Readable from a distance while packing, and scannable.",
  "settings.labels.showOrderInfo": "Order number, store and courier",
  "settings.labels.showItems": "Item list at the bottom",
  "settings.labels.showItemsHelp": "SKU and quantity, up to 4 lines. Helps packers check the parcel.",
  "settings.labels.shrinkNote": "The marketplace label is scaled down proportionally so the additions fit on the same sheet. If it does not come out right, use Print the marketplace's original label in the order detail.",
  "settings.labels.preview": "Layout preview",
  "settings.labels.previewLabel": "Courier label from the marketplace",
  "settings.labels.saved": "Label settings saved.",
  "settings.warehouses.add": "Add warehouse",
  "settings.warehouses.edit": "Edit warehouse",
  "settings.warehouses.code": "Code",
  "settings.warehouses.codeHelp": "Capital letters, digits and hyphens. Leave empty to generate one.",
  "settings.warehouses.address": "Address",
  "settings.warehouses.city": "City",
  "settings.warehouses.postalCode": "Postal code",
  "settings.warehouses.default": "Main",
  "settings.warehouses.active": "Active",
  "settings.warehouses.archived": "Archived",
  "settings.warehouses.archive": "Archive",
  "settings.warehouses.restore": "Reactivate",
  "settings.warehouses.archiveConfirm": "Archive {name}? It cannot be chosen for receiving or new orders until reactivated.",
  "settings.warehouses.empty": "No warehouses yet.",
  "settings.warehouses.wms": "WMS",
  "settings.warehouses.wmsOn": "On",
  "settings.warehouses.wmsOff": "Off",
  "settings.warehouses.wmsSet": "Set up WMS",
  "settings.warehouses.wmsTitle": "WMS · {name}",
  "settings.warehouses.wmsBody": "With WMS, stock is kept per shelf location (zone › rack › level › bin), goods are received and put away through tasks, and orders are picked through pools and waves (single-pcs, batch, cluster) with FEFO.",
  "settings.warehouses.wmsEnable": "Run WMS in this warehouse",
  "settings.warehouses.wmsEnableNote": "When turned on, current stock moves to the TERIMA location. Create shelf locations, then run a putaway task to store it.",
  "settings.warehouses.wmsDisableNote": "Turning WMS off folds all bin stock back into the warehouse. Open tasks must be finished or cancelled first.",
  "settings.warehouses.shipMethod": "Shipping when the packing desk prints a label",
  "settings.warehouses.shipMethodHelp": "Orders not arranged yet are booked this way when their label prints.",
  "settings.warehouses.requireBoxScan": "Packing must scan the packaging's barcode",
  "settings.warehouses.requireBoxScanHelp": "The box cannot be clicked or taken from the suggestion; the packer scans the barcode of the one used, so packaging use is recorded exactly.",
  "settings.warehouses.recordPacking": "Record packing video",
  "settings.warehouses.recordPackingHelp": "The packing desk records each parcel with its camera (webcam) from when the order is opened until the parcel closes, with the order number and time on the video. Videos are kept in the Zerpid cloud and play from the order page as evidence for complaints.",
  "settings.warehouses.videoRetention": "Keep videos for",
  "settings.warehouses.videoRetentionHelp": "Then they are deleted. Match the marketplaces' complaint windows (usually 7–30 days after delivery).",
  "settings.warehouses.days": "days",
  "settings.warehouses.wmsSaved": "WMS settings saved.",
  "settings.warehouses.manageLocations": "Manage locations",

  "settings.users.warehouses": "Warehouse access",
  "settings.users.allWarehouses": "All warehouses",
  "settings.users.warehousesChanged": "{name}'s warehouse access changed. It applies from their next request.",
  "activity.member.warehouses_changed": "Changed a member's warehouse access",
  "error.warehouse_access": "You do not have access to this warehouse.",
  "list.total": "{n} items",
  "list.totalCapped": "More than {n} items. Narrow the filters to see the rest.",
  "list.export": "Export",
  "list.exportXlsx": "Excel (.xlsx)",
  "list.exportCsv": "CSV (UTF-8)",
  "list.exporting": "Preparing the export…",
  "list.from": "From",
  "list.to": "To",
  "list.allStatuses": "All statuses",
  "list.openOnly": "Still open",
  "list.searchNumber": "Search number…",
  "error.export_too_large": "More than 100,000 rows. Narrow the filters first, then export again.",
  "list.createdBy": "Created by",
  "list.createdAt": "Created",
  "list.completedAt": "Completed",
  "wms.in.search": "Search number, supplier, reference…",
  "wms.task.doneUnits": "Done (units)",
  "wms.task.units": "Total units",
  "common.yes": "Yes",
  "common.no": "No",
  "wms.count.countedLocations": "Locations counted",
  "wms.count.shortUnits": "Short (units)",
  "wms.count.overUnits": "Over (units)",
  "wms.wave.orderCount": "Orders",
  "wms.wave.removed": "Removed",
  "wms.wave.packed": "Packed",
  "wms.wave.search": "Search number, pool…",
  "wms.mf.search": "Search number, carrier, courier…",
  "wms.mf.handedBy": "Handed over by",
  "wms.tr.search": "Search number, note…",
  "wms.tr.shippedAt": "Shipped",
  "as.sourceLabel": "Source",
  "as.expectedUnits": "Units expected",
  "as.receivedUnits": "Units received",
  "as.resolutionLabel": "Resolution",
  "as.receivedAt": "Received",
  "as.resolvedAt": "Resolved",
  "as.filter.allResolutions": "All resolutions",
  "orders.carrierFilter": "Carrier",
  "orders.holdLabel": "Held",
  "orders.holdNoteLabel": "Hold note",
  "orders.lineCount": "Lines",
  "orders.unitCount": "Units",
  "orders.currency": "Currency",
  "orders.labelPrints": "Label prints",
  "inventory.stock.all": "Any stock",
  "inventory.stock.out": "Out (none available)",
  "inventory.stock.damaged": "Has damaged",
  "inventory.stock.quarantine": "Has quarantine",
  "inventory.stock.variance": "Has variance",
  "logs.allEntities": "All kinds",
  "logs.actionFilter": "Action code (e.g. order)",
  "logs.entityLabel": "Kind",
  "logs.entityId": "ID",
  "logs.entity.tenant": "Workspace",
  "logs.entity.member": "Member",
  "logs.entity.invitation": "Invitation",
  "logs.entity.warehouse": "Warehouse",
  "logs.entity.order": "Order",
  "logs.entity.product": "Product",
  "logs.entity.stock": "Stock",
  "logs.entity.channel": "Channel",
  "catalog.allKinds": "All types",
  "catalog.allTracking": "Any tracking",
  "catalog.updatedAt": "Updated",
  "catalog.platform.variant": "Variant",
  "catalog.platform.listingId": "Marketplace product ID",
  "catalog.platform.skuId": "Marketplace SKU ID",
  "catalog.platform.mappedCode": "Mapped to (code)",
  "catalog.platform.mappedName": "Mapped to (name)",
  "support.title": "Zerpid support session",
  "support.subtitle": "Opening the client's workspace, read-only…",
  "support.toLogin": "Go to sign in",
  "support.banner": "Zerpid support mode (read-only) by {name}. Every access is in the activity log.",
  "support.end": "End session",
  "shell.suspended.title": "This workspace is suspended",
  "shell.suspended.body": "Your data is safe. Contact the Zerpid team to reactivate it.",
  "settings.support.title": "Zerpid support access",
  "settings.support.help": "Let the Zerpid support team look inside this workspace (read-only, they cannot change anything) to help solve a problem. Every support session is in the activity log, and you can withdraw access any time.",
  "settings.support.openUntil": "Support access is open until {at}.",
  "settings.support.grant24": "Allow for 24 hours",
  "settings.support.grant72": "Allow for 72 hours",
  "settings.support.withdraw": "Withdraw access",
  "settings.support.granted": "Support access allowed.",
  "settings.support.withdrawn": "Support access withdrawn.",
  "error.support_read_only": "A support session can only look, not change anything.",
  "error.support_access_ended": "The owner withdrew support access.",
  "error.support_access_not_granted": "The owner has not allowed support access.",
  "error.workspace_suspended": "This workspace is suspended. Contact the Zerpid team.",
  "activity.support.session_started": "Zerpid support session started",
  "activity.support.access_granted": "Allowed support access",
  "activity.support.access_withdrawn": "Withdrew support access",
  "orders.hold.quota": "Month's order quota used up",
  "sub.title": "Subscription",
  "sub.plan": "Plan",
  "sub.trialEnds": "Trial ends",
  "sub.planUntil": "Valid until",
  "sub.noEnd": "No end date",
  "sub.trialHelp": "During the trial every module is open with no limits. After it ends the workspace moves to Free; your data stays.",
  "sub.manualHelp": "To upgrade, renew or add quota, contact the Zerpid team. The plan is activated once the payment is confirmed.",
  "sub.contact": "Contact the Zerpid team",
  "sub.contactText": "Contact the Zerpid team to upgrade.",
  "sub.usage": "Usage",
  "sub.unlimited": "{n} · no limit",
  "sub.kind.orders_month": "Orders processed this month",
  "sub.kind.skus": "Active SKUs",
  "sub.kind.warehouses": "Warehouses",
  "sub.kind.users": "Users (with invitations)",
  "sub.kind.stores": "Connected stores",
  "sub.ordersWaiting": "{n} orders wait for quota. They are processed by themselves once there is room (an upgrade or a new month).",
  "sub.warn.full": "Plan limit reached: {kinds}. Contact the Zerpid team to upgrade.",
  "sub.warn.near": "Usage is close to the plan limit (80%): {kinds}.",
  "sub.warn.trial": "The trial ends {date}. After that the workspace moves to Free.",
  "sub.warn.planEnding": "Your plan is valid until {date}. Contact the Zerpid team to renew.",
  "sub.warn.planExpired": "Your plan has expired. Contact the Zerpid team to renew.",
  "error.quota_exceeded": "Your plan's limit is reached. See Subscription to upgrade.",
  "activity.tenant.trial_ended": "Trial ended; plan is now Free",
  "settings.users.title": "Users & Roles",
  "settings.users.invite": "Invite user",
  "settings.users.inviteSubmit": "Send invitation",
  "settings.users.invited": "Invitation sent to {email}.",
  "settings.users.role": "Role",
  "settings.users.lastLogin": "Last sign-in",
  "settings.users.active": "Active",
  "settings.users.disabled": "Inactive",
  "settings.users.deactivate": "Deactivate",
  "settings.users.activate": "Activate",
  "settings.users.deactivateConfirm": "Deactivate {name}? Their access to this company stops immediately and their sessions here are ended.",
  "settings.users.roleChanged": "{name}'s role was changed. It applies from their next action.",
  "settings.users.pending": "Pending invitations",
  "settings.users.expires": "Valid until",
  "settings.users.revoke": "Revoke",
  "settings.users.revokeConfirm": "Revoke the invitation for {email}? The link in the email will stop working.",
  "settings.users.you": "You",

  "logs.title": "Activity Log",
  "logs.time": "Time",
  "logs.actor": "By",
  "logs.action": "Activity",
  "logs.detail": "Details",
  "logs.empty": "No activity you can view yet.",
  "logs.scopeWarehouse": "Showing warehouse and stock activity for your role.",
  "logs.scopeOrders": "Showing order activity for your role.",

  "activity.tenant.created": "Created the company",
  "activity.tenant.updated": "Updated company details",
  "activity.warehouse.created": "Added a warehouse",
  "activity.warehouse.updated": "Updated a warehouse",
  "activity.invitation.sent": "Sent an invitation",
  "activity.invitation.revoked": "Revoked an invitation",
  "activity.member.joined": "Joined",
  "activity.member.role_changed": "Changed a role",
  "activity.member.activated": "Activated a user",
  "activity.member.deactivated": "Deactivated a user",
  "activity.member.updated": "Updated a user",

  "platform.shopee": "Shopee",
  "platform.tokopedia": "Tokopedia",
  "platform.tiktok": "TikTok Shop",
  "platform.lazada": "Lazada",
  "platform.blibli": "Blibli",
  "channels.key.title": "Connect a Blibli store",
  "channels.key.modeBind": "Bind request (recommended)",
  "channels.key.modeKey": "API Seller Key",
  "channels.key.bindHelp": "Zerpid sends a bind request to Blibli. The store owner approves it in Blibli Seller Center (Profile > Seller API Manager) or from Blibli's email. The request lasts until the end of the next day; once approved, the store connects by itself.",
  "channels.key.keyHelp": "In Blibli Seller Center, open Profile > Seller API Manager > Manual Bind. Bind the API Client ID below, click Generate, then copy the API Seller Key here.",
  "channels.key.clientId": "Zerpid API Client ID",
  "channels.key.storeCode": "Store code",
  "channels.key.storeCodeHint": "Found in Profile > Store Info, for example TOQ-15126.",
  "channels.key.username": "Seller Center login email",
  "channels.key.sellerKey": "API Seller Key",
  "channels.key.signatureKey": "Signature Key",
  "channels.key.signatureHint": "Only if the store turned on Signature in Seller API Manager.",
  "channels.key.send": "Send request",
  "channels.key.bindSent": "Request sent. The store connects once its owner approves it in Blibli Seller Center.",
  "channels.bindPending": "Waiting for approval in Blibli Seller Center.",
  "error.key_rejected": "Blibli did not accept these store details. Check the store code, login email, and API Seller Key.",
  "error.already_connected": "This store is already connected.",
  "error.shop_taken": "This store is connected to another company on Zerpid.",
  "error.unknown_platform": "Unknown marketplace.",
  "error.channel_unavailable": "This marketplace cannot be connected yet.",
  "error.sync_in_progress": "A sync is already running for this store.",
  "error.reauth_required": "Reconnect this store before syncing.",
  "error.store_disconnected": "This store is disconnected. Reconnect it in Channels to act on its orders.",
  "error.sku_barcode_taken": "Another SKU already uses this barcode.",
  "error.sku_code_taken": "Another SKU already uses this code.",
  "error.sku_archived": "This SKU is archived. Restore it first.",
  "error.duplicate_group_changed": "This group changed since it was shown. Reload and review it again.",
  "error.no_seller_sku": "This platform SKU has no seller SKU to use as the code. Map it by hand.",
  "error.platform_sku_not_found": "Platform SKU not found.",
  "channels.title": "Channels",
  "channels.connect": "Connect store",
  "channels.pickPlatform": "Choose a marketplace",
  "channels.pickBody": "You will be taken to the marketplace to grant access, then brought back here.",
  "channels.unavailable": "Coming soon",
  "channels.empty": "No stores connected yet",
  "channels.emptyBody": "Connect your Shopee, Tokopedia, TikTok Shop or Lazada store to bring orders and stock into one place.",
  "channels.status.connecting": "Connecting",
  "channels.status.syncing": "Syncing",
  "channels.status.active": "Connected",
  "channels.status.reauth_required": "Reconnect needed",
  "channels.status.error": "Problem",
  "channels.status.disconnected": "Disconnected",
  "channels.colKind": "Type",
  "channels.colSource": "Source",
  "channels.colMarketplace": "Marketplace",
  "channels.shopId": "Store ID",
  "channels.lastSync": "Last sync",
  "channels.lastWebhook": "Last notification",
  "channels.lastError": "Last error",
  "channels.syncNow": "Sync now",
  "channels.syncQueued": "Sync scheduled. Status updates in a moment.",
  "channels.disconnect": "Disconnect",
  "channels.disconnectConfirm": "Disconnect {name}? Its access token is deleted and syncing stops. Order history and logs are kept.",
  "channels.disconnected": "{name} disconnected.",
  "channels.reconnect": "Reconnect",
  "channels.reauthHelp": "Zerpid's access to this store expired or was revoked at the marketplace. Reconnect to resume syncing.",
  "channels.detail": "Details",
  "channels.syncJobs": "Sync history",
  "channels.events": "Latest incoming data",
  "channels.eventCounts": "Data received in the last 30 days",
  "channels.jobResult": "{orders} orders, {listings} products, {new} new",
  "channels.job.initial": "Initial",
  "channels.job.manual": "Manual",
  "channels.job.poll": "Automatic",
  "channels.job.token_refresh": "Token renewal",
  "channels.job.running": "Running",
  "channels.job.succeeded": "Succeeded",
  "channels.job.failed": "Failed",
  "channels.source.webhook": "Notification",
  "channels.source.poll": "Automatic check",
  "channels.source.initial_sync": "Initial sync",
  "channels.source.manual_sync": "Manual sync",
  "channels.kind.order": "Order",
  "channels.kind.product": "Product",
  "channels.kind.return": "Return",
  "channels.kind.cancellation": "Cancellation",
  "channels.kind.auth": "Authorisation",
  "channels.kind.chat": "Chat",
  "channels.kind.other": "Other",
  "channels.result.connected": "{count} {platform} store connected. The initial sync is running.",
  "channels.result.cancelled": "Authorisation was cancelled. No store was saved.",
  "channels.result.expired": "The authorisation timed out. Start again from Connect store.",
  "channels.result.failed": "The store could not be connected. Please try again; contact us if it keeps happening.",
  "channels.result.shop_taken": "This store is already connected to another company on Zerpid.",
  "channels.warning.shop_taken": "Some stores were skipped because they are connected to another company.",
  "channels.banner": "{n} marketplace store(s) need attention.",
  "channels.bannerAction": "Open Channels",
  "catalog.title": "SKUs",
  "catalog.add": "Add SKU",
  "catalog.edit": "Edit SKU",
  "catalog.code": "SKU code",
  "catalog.codeHelp": "Your unique code, no spaces. It cannot be changed after saving.",
  "catalog.barcode": "Barcode",
  "catalog.weight": "Weight (grams)",
  "catalog.brand": "Brand",
  "catalog.dimensions": "Size (mm)",
  "catalog.dimensionsHelp": "Outer size of one unit: length × width × height. Used to suggest a box at packing.",
  "catalog.lengthMm": "Length",
  "catalog.widthMm": "Width",
  "catalog.heightMm": "Height",
  "catalog.tracking": "Tracking",
  "catalog.kind": "Type",
  "catalog.kind.single": "Single",
  "catalog.bundle": "Bundle",
  "catalog.bundleHelp": "A bundle has no stock of its own. The marketplaces see how many sets its components make; a bundle order is split into its components when processed, which are reserved, picked and packed.",
  "catalog.bundleMadeOf": "Bundle of: {items}",
  "catalog.components": "Components",
  "catalog.addComponent": "Add component",
  "catalog.tracking.none": "No tracking",
  "catalog.tracking.lot": "Lot + expiry date (FEFO)",
  "catalog.tracking.serial": "Serial number",
  "catalog.trackingHelp": "Lots and serial numbers are recorded per bin on receipt and scanned when picking (Warehouse module). Can only change while the SKU has no stock.",
  "catalog.trackingPlan": "Scale plan and above",
  "catalog.search": "Search code, name or barcode",
  "catalog.empty": "No SKUs yet.",
  "catalog.active": "Active",
  "catalog.archived": "Archived",
  "catalog.archive": "Archive",
  "catalog.restore": "Reactivate",
  "catalog.total": "{n} active SKUs",
  "catalog.view.platform": "Store listings",
  "catalog.import": "Sync listings",
  "catalog.importOne": "Sync one store only",
  "catalog.importStarted": "Syncing {n} stores. Listings update within a few minutes.",
  "catalog.importSkipped": "{n} stores skipped: already syncing or needing reconnection.",
  "catalog.importNone": "No store can be synced right now.",
  "catalog.duplicateBanner": "{n} groups of duplicate SKUs found",
  "catalog.duplicateBannerBody": "Platform SKUs that are likely the same product but not mapped to one internal SKU. Their stock can drift apart.",
  "catalog.review": "Review",
  "catalog.unmappedBanner": "{n} platform SKUs are not mapped",
  "catalog.unmappedBannerBody": "Orders for them reserve no stock, and their marketplace stock is not synced.",
  "catalog.platform.filter.all": "All",
  "catalog.platform.filter.unmapped": "Not mapped ({n})",
  "catalog.platform.filter.mapped": "Mapped",
  "catalog.platform.search": "Search seller SKU or product name",
  "catalog.platform.product": "Marketplace product",
  "catalog.platform.sellerSku": "Seller SKU",
  "catalog.platform.price": "Price",
  "catalog.platform.stock": "Marketplace stock",
  "catalog.platform.mappedTo": "Mapped to",
  "catalog.platform.empty": "No listings yet. Connect a store; its listings download by themselves.",
  "catalog.mapping.auto_code": "Automatic",
  "catalog.mapping.manual": "Manual",
  "catalog.mapping.untracked": "Not tracked",
  "catalog.mapping.untrackedHelp": "Marked as not tracked: stock is not synced and automatic mapping leaves it alone.",
  "catalog.map": "Map",
  "catalog.mapTo": "Map to an internal SKU",
  "catalog.mapSelected": "Map ({n})",
  "catalog.mapTitle": "Map {n} platform SKUs",
  "catalog.mapBody": "The platform SKUs will sell this internal SKU: its stock is sent and orders reserve it, open orders included.",
  "catalog.skuPick": "Internal SKU",
  "catalog.skuPickPlaceholder": "Search SKU code or name",
  "catalog.mapped": "Mapping saved. Stock reaches the marketplace within seconds.",
  "catalog.createFromPlatform": "Create internal SKUs ({n})",
  "catalog.createFromPlatformHelp": "The code is the seller SKU. An existing SKU with that code is used.",
  "catalog.createdFromPlatform": "{created} internal SKUs created, {reused} used existing ones.",
  "catalog.createdPartial": "{ok} done, {failed} failed (e.g. no seller SKU).",
  "catalog.unmap": "Unmap",
  "catalog.untrackSelected": "Mark as not tracked ({n})",
  "catalog.untrackTitle": "Mark {n} platform SKUs as not tracked?",
  "catalog.untrackBody": "Their marketplace stock is no longer synced and stays at the last number. New orders for them reserve no stock. Automatic mapping will not map them again.",
  "catalog.unmapTitle": "Unmap {name}?",
  "catalog.untracked": "Platform SKUs marked as not tracked.",
  "catalog.detail.mapping": "Mapping",
  "catalog.detail.log": "Log",
  "catalog.detail.mappingEmpty": "This SKU is not sold on any marketplace yet.",
  "catalog.detail.mapToPlatform": "Map to platform",
  "catalog.detail.mapToPlatformTitle": "Map {code} to platform SKUs",
  "catalog.detail.suggested": "Sorted by similarity to this SKU's name.",
  "catalog.detail.stock": "View stock",
  "catalog.dup.title": "Review duplicate SKUs",
  "catalog.dup.intro": "Each group holds platform SKUs that are likely the same product. Merge them to share one internal SKU (one stock), or dismiss when they really differ.",
  "catalog.dup.empty": "No duplicate SKUs to review.",
  "catalog.dup.reason.same_code": "Same seller SKU",
  "catalog.dup.reason.similar_title": "Similar name and variant",
  "catalog.dup.merge": "Merge",
  "catalog.dup.dismiss": "Dismiss",
  "catalog.dup.dismissed": "Group dismissed. It returns only if a new SKU joins it.",
  "catalog.dup.mergeTitle": "Merge {n} platform SKUs",
  "catalog.dup.target": "Use internal SKU",
  "catalog.dup.targetOther": "Another internal SKU",
  "catalog.dup.targetNew": "Create a new internal SKU",
  "catalog.dup.mergedPlain": "Merged. Every platform SKU in the group now uses one internal SKU.",
  "catalog.dup.more": "Showing {shown} of {total} groups. More appear as these are reviewed.",
  "catalog.dup.notMapped": "Not mapped",
  "price.tab": "Prices",
  "price.current": "Marketplace price",
  "price.new": "New price",
  "price.edit": "Edit",
  "price.save": "Send",
  "price.sync.pending": "Syncing",
  "price.sync.done": "Synced",
  "price.sync.failed": "Failed",
  "price.sync.pendingTo": "Sending {price}",
  "price.queued": "Price sent to the marketplace.",
  "price.empty": "No platform SKU sells this SKU yet, so there is no price to set.",
  "price.bulk": "Change prices ({n})",
  "price.bulkTitle": "Change prices of {n} SKUs",
  "price.rule": "Rule",
  "price.mode.set": "Set the price",
  "price.mode.percent": "Change by percent",
  "price.mode.amount": "Change by amount",
  "price.value.set": "New price (Rp)",
  "price.value.percent": "Percent (negative for a discount)",
  "price.value.amount": "Amount (Rp, negative to lower)",
  "price.round": "Rounding",
  "price.round.none": "No rounding",
  "price.round.to": "Multiples of Rp{n}",
  "price.roundMode.nearest": "Nearest",
  "price.roundMode.up": "Up",
  "price.roundMode.down": "Down",
  "price.stores": "Stores",
  "price.storesAll": "All stores",
  "price.preview": "Preview",
  "price.previewEmpty": "The selected SKUs are not mapped to any platform SKU yet.",
  "price.apply": "Apply ({n})",
  "price.problem.no_current_price": "No current price to work from",
  "price.problem.below_min": "Below Rp100, skipped",
  "price.problem.above_max": "Too large, skipped",
  "price.problem.big_change": "Changes by more than 50%",
  "price.mode.suggested": "Suggested price",
  "price.problem.no_cost": "No cost of goods yet, skipped",
  "price.suggestedHelp": "Each SKU's price is worked out from its cost of goods and its store's cost profile (Finance > Price margins), then rounded up so the margin does not fall below the target.",
  "price.summaryTitle": "Price change results",
  "price.summarySending": "Sending to marketplaces… {done} of {total} finished",
  "price.summaryDone": "{done} prices synced, {failed} failed.",
  "price.summarySuperseded": "{n} replaced by a newer change.",
  "activity.channel.connected": "Connected a store",
  "activity.channel.reconnected": "Reconnected a store",
  "activity.channel.disconnected": "Disconnected a store",
  "activity.channel.reauth_required": "Store access ended",
  "activity.product.created": "Added an SKU",
  "activity.product.updated": "Updated an SKU",
  "activity.product.mapped": "Mapped platform SKUs",
  "activity.product.unmapped": "Unmapped platform SKUs",
  "activity.product.duplicates_dismissed": "Dismissed a duplicate group",
  "activity.product.price_changed": "Changed prices",
  "onboarding.marketplace.connected": "Connected",

  "tabs.reload": "Reload tab",
  "tabs.close": "Close",
  "tabs.keep": "Keep tab open",
  "tabs.previewHelp": "Preview tab: the next detail from the same menu opens here. Double-click to keep it.",
  "tabs.closeOthers": "Close other tabs",
  "tabs.closeRight": "Close tabs to the right",
  "tabs.closeAll": "Close all",
  "tabs.autoClosed": "At most {max} tabs stay open. The least recently used tab was closed.",

  "status.awaiting_payment": "Awaiting payment",
  "status.new": "New",
  "status.processing": "Processing",
  "status.packed": "Packed",
  "status.shipped": "Shipped",
  "status.delivered": "Delivered",
  "status.completed": "Completed",
  "status.cancelled": "Cancelled",
  "status.returned": "Returned",
  "error.order_not_processable": "Only new orders without a pending cancellation can be processed.",
  "error.order_not_cancellable": "This order can no longer be cancelled.",
  "error.no_cancel_request": "There is no cancellation request to answer.",
  "error.marketplace_rejected": "The marketplace rejected this action.",
  "error.not_supported": "This marketplace does not support that action.",
  "error.sku_not_found": "SKU not found.",
  "error.warehouse_not_found": "Warehouse not found or archived.",
  "error.wms_bin_required": "This warehouse runs WMS. Change stock per location (bin) from the stock detail.",
  "error.wms_not_enabled": "Turn on WMS for this warehouse first.",
  "error.wms_tasks_open": "Warehouse tasks are still open. Finish or cancel them first.",
  "error.insufficient_bin_stock": "Not enough units in this location (some may be promised to another task).",
  "error.location_not_found": "Location not found in this warehouse.",
  "error.location_not_empty": "This location still holds stock.",
  "error.location_system": "System locations cannot be archived.",
  "error.location_code_taken": "This location code is already used.",
  "error.location_not_allowed": "These goods cannot be stored in that kind of location.",
  "error.lot_required": "This SKU is tracked by lot: give the lot number and expiry date.",
  "error.serials_required": "This SKU is tracked by serial number: scan one serial per unit.",
  "error.serial_in_stock": "This serial number is already in stock.",
  "error.serial_not_here": "This serial number is not in that location.",
  "error.serial_requires_scan": "Serial-tracked stock changes by scanning its units, not by count.",
  "error.receipt_closed": "This receipt is already done or cancelled.",
  "error.receipt_has_receipts": "Goods were already received on this receipt. Complete it instead.",
  "error.code_not_found": "Unknown code: not an SKU, barcode, location or serial number.",
  "error.task_closed": "This task is already done or cancelled.",
  "error.task_taken": "Someone else is working on this task.",
  "error.quantity_over_line": "More than this line still needs.",
  "error.nothing_to_putaway": "Nothing is waiting in the TERIMA location.",
  "error.order_not_arrangeable": "Shipment can be arranged for new or processing orders that are not arranged yet and have no pending cancellation.",
  "error.shipping_not_arranged": "Arrange shipment first. The label exists once the courier is booked.",
  "error.order_closed": "This order is cancelled or returned.",
  "error.label_not_ready": "The marketplace is still preparing the label. Try again in a moment.",
  "error.label_not_prepared": "The label is not prepared. Print again.",
  "error.label_no_original": "The original label is available for a single order with a marketplace label.",
  "error.label_unreadable": "The marketplace label cannot take the large tracking number. Print the marketplace's original label.",
  "error.insufficient_stock": "Not enough units in the source condition.",
  "error.insufficient_available": "Not enough available stock. Stock already allocated to orders cannot be held.",
  "error.hold_released": "This stock was already released.",
  "error.plan_feature_unavailable": "This feature is available from the Scale plan. Upgrade to turn it on.",
  "error.tracking_has_stock": "The tracking mode can only change while the SKU has no stock in any warehouse.",
  "error.location_counting": "This location is being counted (frozen) until its count is submitted.",
  "error.variance_location": "The VARIANCE location changes only through counts and variance confirmations.",
  "error.location_busy": "Units in this location are held by an open task. Finish it first, then count.",
  "error.sku_not_in_count": "This SKU is not part of this count.",
  "error.serial_other_warehouse": "This serial number is in stock in another warehouse.",
  "error.variance_changed": "The variance changed since it was shown. Reload and confirm again.",
  "error.nothing_to_count": "No location matches, or every match is already being counted.",
  "error.count_closed": "This count is already closed.",
  "error.location_not_pending": "This location is not waiting to be counted.",
  "error.count_started": "Locations were already counted. Close the count instead of cancelling it.",
  "error.counts_open": "Close the open stock counts of this warehouse first.",
  "error.shelf_short": "Not enough stock on the shelves for this order (it may still be in receiving or being counted).",
  "error.order_changed": "The order changed while the wave was planned.",
  "error.nothing_to_wave": "No order in this pool can be picked now (none waiting, or the shelves lack stock).",
  "error.wave_closed": "This wave is already done or cancelled.",
  "error.wrong_bin": "That is not the bin this line picks from.",
  "error.not_in_wave": "This order is not in a wave waiting for packing.",
  "error.not_picked": "This order is not fully picked yet.",
  "error.already_packed": "This order is already packed.",
  "error.not_packed": "This order is not packed yet.",
  "error.pack_mismatch": "What was scanned does not match the order.",
  "error.cancel_requested": "The buyer asked to cancel this order; answer the request first.",
  "error.order_combined": "TikTok ships this order in one package with another order. Pack them together and ship from TikTok Seller Center.",
  "error.serial_required": "Scan the serial number of this item.",
  "error.nothing_to_pack": "No picked single-pcs order needs this item.",
  "error.packaging_code_taken": "Another packaging in this warehouse uses this code.",
  "error.packaging_required": "This warehouse packs by scanning the packaging: scan it first.",
  "error.no_packaging": "No active packaging to print.",
  "error.manifest_closed": "This handover is already done or cancelled.",
  "error.manifest_empty": "Scan at least one parcel first.",
  "error.order_cancelled": "This order was cancelled; its parcel must not ship.",
  "error.already_handed_over": "This parcel was already handed over.",
  "error.already_scanned": "This parcel is already on this handover.",
  "error.in_other_manifest": "This parcel is on another open handover.",
  "error.wrong_carrier": "This parcel goes with another carrier.",
  "error.transfer_state": "This transfer cannot do that in its current state.",
  "error.nothing_to_ship": "Nothing of this transfer is left to ship.",
  "error.transfer_too_many": "More than was shipped for this SKU. Confirm first to receive more.",
  "error.not_on_transfer": "This SKU or serial number was not shipped on this transfer.",
  "error.transfer_not_picked": "Pick every unit of this transfer before it leaves.",
  "error.task_of_transfer": "This task picks a transfer: cancel the transfer instead.",
  "error.parcel_on_manifest": "This parcel is on a handover: take it off the handover first.",
  "error.order_held_oos": "Not enough stock; the order waits for stock.",
  "error.order_held_unmapped": "Some SKUs are not mapped.",
  "error.order_held_no_warehouse": "This order has no warehouse.",
  "error.order_held_cancel_request": "The buyer asked to cancel; answer the request first.",
  "error.order_held_rule": "A hold rule holds this order.",
  "error.order_held_manual": "This order is held by hand.",
  "error.order_held_arrange_failed": "Courier booking failed.",
  "error.order_in_wave": "The warehouse is already picking or packing this order.",
  "error.order_not_holdable": "Only orders not yet shipped can be held or moved.",
  "error.order_not_held": "This order is not held by a rule or by hand.",
  "error.bundle_no_stock": "A bundle has no stock of its own; change its components' stock.",
  "error.recording_off": "This warehouse does not record packing and unboxing videos.",
  "error.video_not_found": "No packing video for this order (not recorded, or past its retention).",
  "error.unboxing_video_not_found": "No unboxing video for this case (not recorded, or past its retention).",
  "error.case_closed": "This case is resolved or cancelled.",
  "error.case_state": "This case cannot do that in its current state.",
  "error.case_receiving": "The warehouse is unboxing this return; wait until it finishes.",
  "error.case_unlinked": "Match this parcel to its order first.",
  "error.case_no_units": "Record at least one item before finishing.",
  "error.serial_not_shipped": "This serial number did not leave with the order. Record it as a wrong item.",
  "error.serial_duplicate": "This serial number is already recorded on the case.",
  "error.warehouse_inactive": "That warehouse is not active.",
  "error.video_too_large": "The recording is too large.",
  "orders.title": "Orders",
  "orders.all": "All",
  "orders.heldTab": "On hold",
  "orders.filters": "Filters",
  "orders.filtersReset": "Reset filters",
  "orders.copyNumber": "Copy order number",
  "orders.copied": "Copied",
  "orders.stage.all": "All stages",
  "orders.stage.queue": "Not picked yet",
  "orders.stage.picking": "Picking",
  "orders.stage.packing": "Packing",
  "orders.wave": "Picklist no.",
  "orders.cod": "COD",
  "orders.search": "Search order number, buyer or tracking number",
  "orders.allStores": "All stores",
  "orders.number": "Order no.",
  "orders.store": "Store",
  "orders.buyer": "Buyer",
  "orders.items": "Items",
  "orders.itemsValue": "{units} units, {lines} SKUs",
  "orders.total": "Total",
  "orders.orderedAt": "Ordered",
  "orders.empty": "No orders yet.",
  "orders.emptyBody": "Orders from connected marketplace stores appear here.",
  "orders.cancelRequested": "Cancel requested",
  "orders.cancelRequestsOnly": "Cancel requests ({n})",
  "orders.unmapped": "SKU not mapped",
  "orders.unmappedHelp": "Some items are not linked to an internal SKU, so no stock is reserved for them. Click Map on the item to link it to an internal SKU (other orders with the same item follow), or create an SKU with the same code in Products & Prices.",
  "orders.process": "Process order",
  "orders.processSelected": "Process selected ({n})",
  "orders.processed": "{ok} orders processed.",
  "orders.processedPartial": "{ok} orders processed, {failed} could not be processed.",
  "orders.cancel": "Cancel",
  "orders.cancelTitle": "Cancel order {number}",
  "orders.cancelReason": "Reason",
  "orders.reason.out_of_stock": "Out of stock",
  "orders.reason.customer_request": "Buyer request",
  "orders.reason.other": "Other",
  "orders.reason.pickup": "courier pickup",
  "orders.reason.dropoff": "drop-off",
  "orders.cancelAtTitle": "The order is cancelled at {platform} too",
  "orders.cancelAtBody": "The buyer is told and the cancellation at {platform} cannot be undone. Reserved stock is let go once {platform} confirms.",
  "orders.cancelAtOk": "Cancel at {platform}",
  "orders.cancelZerpidTitle": "Closed in Zerpid only",
  "orders.cancelZerpidBody": "{shop} is no longer connected, so the marketplace is NOT told. The order is closed in Zerpid and its stock let go. Make sure it was cancelled or completed in Seller Center.",
  "orders.cancelZerpidOk": "Close in Zerpid only",
  "orders.cancelZerpidDone": "Order closed in Zerpid; the marketplace was not changed.",
  "orders.storeGoneTitle": "{shop} is no longer connected to Zerpid",
  "orders.storeGoneBody": "Marketplace actions (arrange shipment, labels, cancelling at the marketplace) no longer run. Settle this order in Seller Center, then close it in Zerpid with Cancel.",
  "orders.cancelDone": "Order cancelled at the marketplace.",
  "orders.cancelRequestTitle": "The buyer asked to cancel",
  "orders.cancelRequestBody": "This order is on hold until you answer. Accept to cancel and release the stock, or reject to continue.",
  "orders.acceptCancel": "Accept cancellation",
  "orders.rejectCancel": "Reject",
  "orders.responded": "Answer sent to the marketplace.",
  "orders.recipient": "Recipient",
  "orders.address": "Address",
  "orders.payment": "Payment",
  "orders.platformStatus": "Marketplace status",
  "orders.warehouse": "Warehouse",
  "orders.lines": "Items",
  "orders.qty": "Qty",
  "orders.unitPrice": "Price",
  "orders.lineCancelled": "Cancelled",
  "orders.packages": "Packages & tracking",
  "orders.carrier": "Carrier",
  "orders.trackingNo": "Tracking no.",
  "orders.noPackages": "No tracking number from the marketplace yet.",
  "orders.history": "History",
  "orders.history.marketplace": "Marketplace",
  "orders.history.user": "User",
  "orders.history.system": "System",
  "orders.note.cancel_requested": "Buyer asked to cancel",
  "orders.note.cancel_request_accepted": "Cancellation request accepted",
  "orders.note.cancel_request_rejected": "Cancellation request rejected",
  "orders.note.cancelled_by_seller": "Cancelled by seller",
  "orders.note.shipment_arranged": "Shipment arranged",
  "orders.note.held": "Order held",
  "orders.note.released": "Hold released",
  "orders.note.auto_processed": "Processed automatically: stock reserved",
  "orders.note.warehouse_changed": "Warehouse changed",
  "orders.hold.oos": "Out of stock",
  "orders.hold.unmapped": "Unmapped SKU",
  "orders.hold.no_warehouse": "No warehouse",
  "orders.hold.cancel_request": "Cancellation pending",
  "orders.hold.rule": "Held by a rule",
  "orders.hold.manual": "Held by hand",
  "orders.hold.combined": "Combined by TikTok",
  "orders.hold.arrange_failed": "Courier booking failed",
  "orders.holdHelp.oos": "Not enough stock for every item, so nothing is reserved. The order is processed by itself once stock arrives (inbound, return, transfer, adjustment), the most urgent first.",
  "orders.holdHelp.unmapped": "Some items are not mapped to an internal SKU. Map them and the order is processed by itself.",
  "orders.holdHelp.no_warehouse": "The store has no warehouse. Set the store's warehouse, or choose one for this order.",
  "orders.holdHelp.cancel_request": "The buyer asked to cancel; answer the request first.",
  "orders.holdHelp.rule": "A hold rule matched. Stock stays reserved; the warehouse does not take it until released.",
  "orders.holdHelp.manual": "Held by the team. Stock stays reserved; the warehouse does not take it until released.",
  "orders.holdHelp.combined": "TikTok ships this order in one package with other orders (see \"Same group as\" above). Zerpid packs a parcel per order, so this order does not go to picking and cannot be booked or have its label printed here. Pack them together and ship from TikTok Seller Center. Release it only if TikTok gives this order its own package.",
  "orders.holdHelp.quota": "The plan's order quota for this month is used up. No stock is reserved; the order is processed by itself once there is room (upgrade in Subscription, or a new month).",
  "orders.holdHelp.arrange_failed": "The marketplace refused or failed to book the courier. Passing trouble is retried by itself.",
  "orders.holdFilter.all": "All orders",
  "orders.holdFilter.any": "Held ({n})",
  "orders.holdRules": "Hold rules",
  "orders.heldDone": "Order held.",
  "orders.releasedDone": "Hold released; the order is processed.",
  "orders.movedDone": "Warehouse changed; the order is processed again there.",
  "orders.holdAction": "Hold",
  "orders.holdActionHelp": "The warehouse does not take the order until it is released. Stock stays reserved.",
  "orders.holdNote": "Reason (optional)",
  "orders.moveWarehouse": "Change warehouse",
  "orders.moveWarehouseHelp": "The reservation moves to the chosen warehouse and the order is processed again there. The change is in the activity log.",
  "orders.release": "Release",
  "orders.retryArrange": "Retry booking",
  "orders.processAgain": "Process again",
  "orders.heldSince": "since {at}",
  "orders.line.bundle": "Bundle",
  "orders.line.component": "Bundle item",
  "orders.line.gift": "Gift",
  "orders.lineNote.gift_out_of_stock": "Gift out of stock; shipped without it",
  "orders.warehouseSource.marketplace": "from the marketplace",
  "orders.warehouseSource.store": "the store's warehouse",
  "orders.warehouseSource.manual": "chosen by hand",
  "orders.rule.add": "New rule",
  "orders.rule.edit": "Edit rule",
  "orders.rule.help": "Orders matching a rule wait in Baru with their stock reserved, until released from the order page or the rule ends. A rule applies to orders processed after it was made.",
  "orders.rule.empty": "No hold rules.",
  "orders.rule.conditions": "Conditions",
  "orders.rule.conditionsHelp": "Every condition given must match.",
  "orders.rule.until": "Until",
  "orders.rule.untilHelp": "Then the rule stops and the orders it held go on.",
  "orders.rule.held": "Held",
  "orders.rule.active": "Active",
  "orders.rule.delete": "Delete rule",
  "orders.rule.deleteConfirm": "Delete rule {name}? The {n} orders it holds go on.",
  "orders.rule.saved": "Rule saved.",
  "orders.rule.note": "Note on the order",
  "orders.rule.noteHelp": "Shown on held orders, e.g. \"Verify COD\".",
  "orders.rule.allOrders": "Every order",
  "orders.rule.stores": "Stores",
  "orders.rule.platforms": "Marketplaces",
  "orders.rule.payment": "Payment method",
  "orders.rule.paymentHelp": "Matches when the payment method contains one of these, e.g. COD.",
  "orders.rule.minTotal": "Minimum total",
  "orders.rule.maxTotal": "Maximum total",
  "orders.rule.minUnits": "Minimum units",
  "orders.rule.minUnitsValue": "≥ {n} units",
  "orders.rule.skus": "Contains SKU",
  "orders.rule.skusHelp": "Matches when the order has one of these SKUs.",
  "orders.rule.skuCount": "{n} SKUs",
  "orders.rule.carriers": "Couriers",
  "orders.rule.orderedBefore": "Ordered before",
  "orders.rule.orderedBeforeHelp": "For older orders, e.g. a store's orders from before it was connected to Zerpid (made automatically when a new store connects).",
  "promo.title": "Gift promotions",
  "promo.add": "New promotion",
  "promo.edit": "Edit promotion",
  "promo.help": "The seller's own gifts, given once when an order is processed. The gift joins the order as a Rp0 line, reserved, picked and packed with it. Marketplace vouchers and discounts are not these.",
  "promo.empty": "No promotions.",
  "promo.examplesTitle": "Examples",
  "promo.examples": "Buy A get B: bought A, gift B. · Buy 1 get 2: bought A at least 1, gift A ×2. · Spend 100k get X: minimum total 100,000, gift X. · Spend 100k with Z get Y: minimum total 100,000, required Z, gift Y.",
  "promo.conditionsHelp": "Every condition given must match. Give at least one: bought SKUs, minimum total, or required SKUs.",
  "promo.buySkus": "Bought SKUs (any)",
  "promo.buyMinQty": "Minimum qty",
  "promo.minTotal": "Minimum order total",
  "promo.requiredSkus": "Required SKUs",
  "promo.gift": "Gift",
  "promo.giftSku": "Gift SKU",
  "promo.giftQty": "Qty",
  "promo.multiply": "Multiply by quantity bought",
  "promo.perQty": "Per units bought",
  "promo.perText": "per {n} bought",
  "promo.maxPerOrder": "Max per order",
  "promo.quota": "Total quota",
  "promo.used": "Given",
  "promo.whenOutLabel": "When the gift is out of stock",
  "promo.whenOut.skip": "Ship without it (noted)",
  "promo.whenOut.hold": "Hold the order until it is in stock",
  "promo.period": "Period",
  "promo.always": "No end",
  "promo.priority": "Priority",
  "promo.priorityHelp": "Lower goes first.",
  "promo.exclusive": "Exclusive",
  "promo.exclusiveHelp": "Not combined with other promotions.",
  "promo.saved": "Promotion saved.",
  "promo.delete": "Delete promotion",
  "promo.deleteConfirm": "Delete promotion {name}? Gifts already given stay on their orders.",
  "promo.buyText": "buy {n}× {skus}",
  "promo.spendText": "spend ≥ {amount}",
  "promo.withText": "with {skus}",
  "promo.storesText": "{n} stores",
  "orders.label": "Print label",
  "orders.printOriginal": "Print the marketplace's original label",
  "orders.printOriginalHelp": "The marketplace's file without the large tracking number. Use it when the printed label does not come out right.",
  "orders.arrange": "Arrange shipment",
  "orders.arrangeTitle": "Arrange shipment · {number}",
  "orders.arrangeTitleBulk": "Arrange shipment for {n} orders",
  "orders.arrangeBody": "The courier is booked on the marketplace. The tracking number is then issued and the label can be printed.",
  "orders.method": "Hand over to the courier",
  "orders.method.pickup": "Courier picks up",
  "orders.method.dropoff": "Drop off at a courier point",
  "orders.methodLazada": "For Lazada orders the handover follows the shop setting in Seller Center.",
  "orders.methodBlibli": "For Blibli orders the handover follows the store's delivery type at Blibli: Dropship (you bring the parcel to the courier) or Pickup (Blibli's courier collects it).",
  "orders.arranged": "Shipment arranged. The tracking number is issued.",
  "orders.arrangedBulk": "Shipment arranged for {ok} orders.",
  "orders.arrangedPartial": "{ok} orders arranged, {failed} failed.",
  "orders.arrangeSelected": "Arrange shipment ({n})",
  "orders.printSelected": "Print labels ({n})",
  "orders.selectedCount": "{n} orders selected",
  "orders.clearSelection": "Clear selection",
  "orders.preparing": "Preparing label {done} of {total}…",
  "orders.preparingTitle": "Preparing labels",
  "orders.labelFailedTitle": "{n} labels cannot be printed yet",
  "orders.labelFailedBody": "The other labels are ready. Fix the orders below, then print them again.",
  "orders.printReady": "Print the {n} ready labels",
  "orders.printing": "Preparing the print file…",
  "orders.popupBlocked": "The browser blocked the print window. Allow pop-ups for Zerpid and try again.",
  "orders.labelStatus": "Label",
  "orders.shipBy": "Ship by",
  "orders.shipBy.late": "Late",
  "orders.shipBy.inHours": "In {n} h",
  "orders.labelPrinted": "Printed {n}×",
  "orders.labelNotPrinted": "Not printed",
  "orders.outside": "Outside Zerpid",
  "orders.outsideHelp": "Shipped through another warehouse or system, not processed in Zerpid. Zerpid's stock did not change.",
  "orders.outsideBody": "This order was shipped through another warehouse or system, not processed in Zerpid, so Zerpid's stock did not change. If the goods came from a Zerpid warehouse, correct the stock with a stock adjustment.",
  "orders.handled.all": "Handled anywhere",
  "orders.labelFilter.all": "All labels",
  "orders.labelFilter.unprinted": "Label not printed",
  "orders.labelFilter.printed": "Label printed",
  "orders.lastPrinted": "Last printed {at} by {name}",
  "orders.labelSourceZerpid": "The marketplace offers no label for this order; Zerpid draws one from the order data.",
  "orders.parcel": "Warehouse parcel",
  "orders.parcelSuggested": "suggested {code}",
  "orders.parcelPacked": "Packed",
  "orders.playVideo": "Play packing video",
  "orders.videoKept": "Kept until {at}",
  "orders.noVideo": "No packing video",
  "orders.trackingPending": "The tracking number is issued once shipment is arranged.",
  "orders.trackParcel": "Track parcel",
  "orders.trackingEmpty": "The courier has not reported any step yet.",
  "orders.combinedTag": "Combined shipment (TikTok)",
  "orders.combinedHelp": "TikTok combines a LIVE buyer's orders into one shipment. Zerpid still packs per order: check the other orders of this group so nothing is packed twice, and handle the combination in Seller Center.",
  "orders.combinedWith": "Same group as:",
  "orders.splitTag": "Split (TikTok)",
  "orders.splitHelp": "TikTok split this order into several packages. Zerpid still packs one package per order: handle its shipment in Seller Center.",
  "orders.money.title": "Price and deductions",
  "orders.money.show": "Show breakdown",
  "orders.money.listPrice": "List price",
  "orders.money.sellerDiscount": "Store discount",
  "orders.money.platformDiscount": "Marketplace discount",
  "orders.money.shippingFee": "Shipping",
  "orders.money.shippingSeller": "Shipping subsidy by the store",
  "orders.money.shippingPlatform": "Shipping subsidy by the marketplace",
  "orders.money.tax": "Tax",
  "orders.money.buyerPaid": "Paid by the buyer",
  "orders.money.shippingCost": "Shipping cost",
  "orders.money.received": "Received by the store",
  "orders.money.estimate": "Not settled yet. Estimated to receive {amount}",
  "orders.money.estimateAt": "around {at}.",
  "orders.money.notSettled": "Not settled yet, and the marketplace gave no estimate.",
  "inventory.title": "Stock overview",
  "inventory.search": "Search code, name or barcode",
  "inventory.allWarehouses": "All warehouses",
  "inventory.lowOnly": "Low stock only ({n})",
  "inventory.onHand": "On hand",
  "inventory.allocated": "Allocated",
  "inventory.available": "Available",
  "inventory.pickWarehouse": "Pick a warehouse to see its bins, holds and movements.",
  "inventory.formula": "Available = Sellable − Variance − Allocated (orders) − Allocated (other). This Available number is what every store selling the SKU is told. Damaged and quarantined units stay in on hand but are not sold.",
  "inventory.low": "Low",
  "inventory.empty": "No SKUs yet. Add them in Products & Prices.",
  "inventory.adjust": "Adjust stock",
  "inventory.adjustBody": "Enter the correct physical count for the selected condition. The difference is recorded in the movement history with the reason.",
  "inventory.newQty": "New physical count",
  "inventory.reason": "Reason",
  "inventory.reasonPlaceholder": "e.g. recount of shelf A-01",
  "inventory.adjusted": "Stock updated. Stores receive the new number within seconds.",
  "inventory.levels": "Stock per warehouse",
  "inventory.soldOn": "Sold on",
  "inventory.soldOnEmpty": "No store sells this SKU yet.",
  "inventory.platformStock": "Stock on marketplace",
  "inventory.lastPushed": "Last sent",
  "inventory.pushError": "Send failed",
  "inventory.mapping": "Mapping",
  "inventory.mapping.auto_code": "Automatic (same code)",
  "inventory.mapping.manual": "Manual",
  "inventory.movements": "Stock movements",
  "inventory.movementsEmpty": "No movements yet.",
  "inventory.delta": "Change",
  "inventory.reason.count": "Stock count",
  "inventory.reason.count_loss": "Variance confirmed: missing",
  "inventory.reason.count_gain": "Variance confirmed: found",
  "inventory.reason.variance_netoff": "Variance netted between lots",
  "inventory.reason.short_pick": "Not in the bin (picking)",
  "inventory.reason.picking": "Picking",
  "inventory.reason.packing": "Packing",
  "inventory.reason.unpacking": "Unpacked",
  "inventory.reason.transfer": "Transfer",
  "inventory.reason.transfer_in": "Transfer in",
  "inventory.reason.transfer_out": "Transfer out",
  "inventory.reason.return": "Return",
  "inventory.reason.shipment_correction": "Shipment correction",
  "inventory.reason.vendor_return": "Return to supplier",
  "inventory.reason.vendor_replacement": "Supplier replacement",
  "inventory.reason.adjustment": "Adjustment",
  "inventory.reason.shipment": "Shipment",
  "inventory.reason.inbound": "Receiving",
  "inventory.reason.condition_change": "Condition change",
  "inventory.reason.putaway": "Putaway",
  "inventory.reason.bin_move": "Location move",
  "inventory.reason.wms_started": "WMS turned on",
  "inventory.reason.wms_stopped": "WMS turned off",
  "inventory.bins": "Stock per location",
  "inventory.bin.location": "Location",
  "inventory.bin.lot": "Lot",
  "inventory.bin.expires": "Expires",
  "inventory.bin.reserved": "Held by tasks",
  "inventory.bin.adjust": "Count",
  "inventory.bin.move": "Move",
  "inventory.bin.adjustTitle": "Recount {code} at {location}",
  "inventory.bin.moveTitle": "Move {code} from {location}",
  "inventory.bin.toLocation": "To location",
  "inventory.bin.toCondition": "Condition there",
  "inventory.bin.saved": "Location stock updated.",
  "inventory.wmsHint": "This warehouse runs WMS: change stock per location in the stock detail.",
  "inventory.sellable": "Sellable",
  "inventory.damaged": "Damaged",
  "inventory.quarantine": "Quarantine",
  "inventory.allocatedOrder": "Allocated (orders)",
  "inventory.allocatedOther": "Allocated (other)",
  "inventory.variance": "Variance",
  "inventory.varianceHelp": "Sellable units in the VARIANCE location, not yet confirmed. Positive: on the books but not found by a count, so not sold. Negative: found on a shelf but not yet on the books.",
  "inventory.allocatedOtherHelp": "Stock held manually, waiting for a transfer, or waiting to go back to a supplier.",
  "inventory.condition": "Condition",
  "inventory.condition.sellable": "Sellable",
  "inventory.condition.damaged": "Damaged",
  "inventory.condition.quarantine": "Quarantine",
  "inventory.current": "Current: {n}",
  "inventory.move": "Change condition",
  "inventory.moveBody": "Move units between conditions without changing on hand, for example units found damaged or the QC result of quarantined goods.",
  "inventory.from": "From",
  "inventory.to": "To",
  "inventory.quantity": "Quantity",
  "inventory.note": "Note",
  "inventory.moveNotePlaceholder": "E.g. torn packaging found during stock count",
  "inventory.moved": "Stock condition updated.",
  "inventory.hold": "Hold stock",
  "inventory.holdBody": "Held stock leaves Available and is not sold on marketplaces until released. Only available stock can be held.",
  "inventory.holdNotePlaceholder": "E.g. B2B order for Toko Sinar, live session 8 pm",
  "inventory.holdMax": "Up to {n} (available stock)",
  "inventory.held": "Stock held. Stores receive the new quantity within seconds.",
  "inventory.holds": "Other allocations",
  "inventory.holdsEmpty": "No stock is held.",
  "inventory.incoming": "+{n} in transit",
  "inventory.incomingHelp": "Incoming transfers: shipped here from another warehouse, not received yet. Not part of stock or available.",
  "inventory.incomingTitle": "Incoming transfers",
  "inventory.toReturn": "+{n} returning",
  "inventory.toReturnHelp": "To-return: goods aftersales cases expect back at this warehouse, not received yet. Not part of stock or available.",
  "inventory.toReturnTitle": "Returns on their way",
  "inventory.holdReason": "Type",
  "inventory.holdReason.hold": "Manual hold",
  "inventory.holdReason.transfer": "Outbound transfer",
  "inventory.holdReason.vendor_return": "Return to supplier",
  "inventory.release": "Release",
  "inventory.releaseConfirm": "Release {n} units back to available stock?",
  "inventory.released": "Stock released back to available.",
  "inventory.since": "Since",
  "wms.pickWarehouse": "Warehouse",
  "shell.activeWarehouse": "Active warehouse",
  "shell.activeWarehouseHelp": "Active warehouse: every warehouse screen works in it.",
  "wms.activeNoWms": "The active warehouse ({name}) does not run WMS. Switch the active warehouse above, or turn on WMS in Settings > Warehouses.",
  "wms.noWmsWarehouse": "No warehouse runs WMS yet. Turn it on in Settings > Warehouses.",
  "wms.openSettings": "Open warehouse settings",
  "wms.scanPlaceholder": "Scan or type a code, then Enter",
  "wms.loc.title": "Locations",
  "wms.loc.add": "Add location",
  "wms.loc.bulk": "Create in bulk",
  "wms.loc.bulkTitle": "Create locations in bulk",
  "wms.loc.bulkBody": "Every rack × level × bin of this zone is created as ZONE-RACK-LEVEL-BIN, e.g. A-01-02-03. Existing codes are skipped.",
  "wms.loc.bulkCount": "{n} locations will be created",
  "wms.loc.bulkDone": "{created} locations created, {skipped} existed.",
  "wms.loc.zone": "Zone",
  "wms.loc.rack": "Rack",
  "wms.loc.level": "Level",
  "wms.loc.bin": "Bin",
  "wms.loc.from": "From",
  "wms.loc.to": "To",
  "wms.loc.code": "Location code",
  "wms.loc.codeHelp": "Leave empty to use ZONE-RACK-LEVEL-BIN.",
  "wms.loc.kind": "Kind",
  "wms.loc.kind.storage": "Storage",
  "wms.loc.kind.receiving": "Receiving",
  "wms.loc.kind.returns": "Returns",
  "wms.loc.kind.packing": "Packing",
  "wms.loc.kind.outbound": "Outbound",
  "wms.loc.kind.damaged": "Damaged",
  "wms.loc.kind.quarantine": "Quarantine",
  "wms.loc.kind.variance": "Variance",
  "wms.loc.system": "System",
  "wms.loc.units": "Units",
  "wms.loc.skus": "SKUs",
  "wms.loc.empty": "No locations yet.",
  "wms.loc.search": "Search location code",
  "wms.loc.contents": "Contents of {code}",
  "wms.loc.contentsEmpty": "This location is empty.",
  "wms.loc.archive": "Archive",
  "wms.loc.restore": "Restore",
  "wms.loc.created": "Location created.",
  "wms.in.title": "Inbound",
  "wms.in.add": "New receipt",
  "wms.in.number": "Receipt no.",
  "wms.in.supplier": "Supplier",
  "wms.in.reference": "Reference (delivery note / PO)",
  "wms.in.note": "Note",
  "wms.in.lines": "Expected goods",
  "wms.in.addLine": "Add item",
  "wms.in.expected": "Expected",
  "wms.in.unitCost": "Unit cost",
  "wms.in.unitCostHelp": "Optional. The supplier's price per unit, used to work out stock cost later. Only warehouse managers see it.",
  "wms.in.received": "Received",
  "wms.in.difference": "Difference",
  "wms.in.status.open": "Waiting",
  "wms.in.status.receiving": "Partly received",
  "wms.in.status.done": "Done",
  "wms.in.status.cancelled": "Cancelled",
  "wms.in.empty": "No receipts yet.",
  "wms.in.scanTitle": "Receive goods",
  "wms.in.scanHelp": "Scan the SKU or barcode. Goods go to the TERIMA location, then to shelves through a putaway task.",
  "wms.in.quantity": "Quantity",
  "wms.in.lotNo": "Lot no.",
  "wms.in.expiresOn": "Expires",
  "wms.in.serials": "Serial numbers",
  "wms.in.serialsHelp": "Scan every unit. {n} scanned.",
  "wms.in.receive": "Receive",
  "wms.in.received1": "{qty} × {code} received.",
  "wms.in.complete": "Complete receipt",
  "wms.in.completeConfirm": "Complete receipt {number}? Differences are recorded and a putaway task is created.",
  "wms.in.completed": "Receipt completed. Putaway task created.",
  "wms.in.cancel": "Cancel",
  "wms.in.openPutaway": "Open putaway task",
  "wms.task.title.putaway": "Putaway",
  "wms.task.title.picking": "Picking",
  "wms.task.number": "Task no.",
  "wms.task.status.open": "Waiting",
  "wms.task.status.in_progress": "In progress",
  "wms.task.status.done": "Done",
  "wms.task.status.cancelled": "Cancelled",
  "wms.task.progress": "Progress",
  "wms.task.assigned": "Assignee",
  "wms.task.empty": "No tasks.",
  "wms.task.openOnly": "Open only",
  "wms.task.fromReceiving": "Create from TERIMA location",
  "wms.task.fromPacking": "Return from PACKING",
  "wms.task.fromOutbound": "Return from OUTBOUND",
  "wms.task.source": "Source",
  "wms.task.source.receiving": "Receiving",
  "wms.task.source.inbound": "Inbound",
  "wms.task.source.packing": "From packing",
  "wms.task.source.outbound": "From outbound",
  "wms.task.source.transfer": "Incoming transfer",
  "wms.task.source.move": "Move",
  "wms.move.title": "Move task",
  "wms.move.create": "Create task",
  "wms.move.help": "Pick the source location, how many of each item move and, optionally, where to. The units are reserved in their bin until staff move them by scanning, like a putaway. E.g. filling a pick face from reserve, emptying a bin.",
  "wms.move.from": "From location",
  "wms.move.to": "To location",
  "wms.move.toHelp": "Leave empty to suggest a bin already holding the SKU.",
  "wms.move.suggested": "Suggested",
  "wms.move.empty": "No free units in this location.",
  "wms.move.free": "Free",
  "wms.move.qty": "Move",
  "wms.task.start": "Start",
  "wms.task.cancel": "Cancel task",
  "wms.task.cancelConfirm": "Cancel task {number}? Goods not moved yet are released in their location.",
  "wms.task.from": "From",
  "wms.task.suggested": "Suggested",
  "wms.task.to": "Destination bin",
  "wms.task.confirm": "Save",
  "wms.task.lineDone": "Done",
  "wms.task.saved": "Stored in {code}.",
  "wms.task.allDone": "Task done.",
  "wms.count.title": "Stock counts",
  "wms.count.add": "New stock count",
  "wms.count.start": "Start count",
  "wms.count.empty": "No stock counts yet.",
  "wms.count.kind": "Type",
  "wms.count.kind.full": "Full stock take",
  "wms.count.kind.cycle": "Cycle count",
  "wms.count.fullHelp": "Every location of the warehouse is counted (optionally per zone). Locations are frozen until each is counted.",
  "wms.count.cycleHelp": "Part of the warehouse: by zone, chosen locations, chosen SKUs, or locations not counted for a while.",
  "wms.count.zones": "Zones",
  "wms.count.zonesHelp": "Leave empty for every zone.",
  "wms.count.allZones": "All zones",
  "wms.count.locations": "Locations",
  "wms.count.anyLocation": "Every matching location",
  "wms.count.skus": "Only SKUs",
  "wms.count.skusHelp": "Only locations holding these SKUs, and only these SKUs are compared.",
  "wms.count.notCountedDays": "Not counted for (days)",
  "wms.count.notCountedDaysHelp": "E.g. 30: only locations last counted more than 30 days ago.",
  "wms.count.blind": "Blind count",
  "wms.count.blindHelp": "Counters do not see the system quantity, so it cannot sway them.",
  "wms.count.freezeNote": "Locations in this count cannot be picked from, put away into, or changed by hand until they are counted.",
  "wms.count.progress": "Locations counted",
  "wms.count.difference": "Difference",
  "wms.count.createdBy": "Created by",
  "wms.count.createdAt": "Created",
  "wms.count.status.open": "In progress",
  "wms.count.status.done": "Done",
  "wms.count.status.cancelled": "Cancelled",
  "wms.count.loc.pending": "To count",
  "wms.count.loc.counted": "Counted",
  "wms.count.loc.skipped": "Skipped",
  "wms.count.frozen": "Being counted",
  "wms.count.scanLocation": "Choose a location",
  "wms.count.scanLocationHelp": "Scan the location code on the shelf, or click it in the list.",
  "wms.count.counting": "Counting {code}",
  "wms.count.scanItems": "Scan each item (each scan adds 1), or type the quantity in the table. For serial-tracked SKUs, scan every unit.",
  "wms.count.notInCount": "Location {code} is not part of this count.",
  "wms.count.alreadyCounted": "Location {code} is already counted.",
  "wms.count.round": "Round {n}",
  "wms.count.roundCol": "Round",
  "wms.count.books": "On the books",
  "wms.count.booksEmpty": "Empty",
  "wms.count.nothingYet": "Nothing scanned yet.",
  "wms.count.scanSerials": "Scan each unit's serial number",
  "wms.count.newLot": "New lot…",
  "wms.count.submit": "Submit count",
  "wms.count.empty1": "Location is empty",
  "wms.count.submitConfirm": "Submit the count of {code} ({n} lines)? Differences with the books move to the VARIANCE location; they are not yet a loss or a gain.",
  "wms.count.emptyConfirm": "Record {code} as empty? Everything on the books there moves to the VARIANCE location.",
  "wms.count.submitted": "Count of {code} submitted.",
  "wms.count.close": "Close count",
  "wms.count.closeConfirm": "{n} locations not counted will be skipped and released. Continue?",
  "wms.count.cancel": "Cancel count",
  "wms.count.toVariance": "{short} units moved into VARIANCE (not found) and {over} out of it (found). Confirm them in Warehouse > Variance.",
  "wms.count.countedBy": "Counted by",
  "wms.count.recount": "Recount",
  "wms.count.results": "Results per location",
  "wms.count.system": "Books",
  "wms.count.counted": "Counted",
  "wms.var.title": "Stock variance",
  "wms.var.help": "Differences from counts wait in the VARIANCE location and are not yet a loss or a gain. What was not found is not sold. Confirm to book them: not found becomes a loss, found becomes a gain.",
  "wms.var.missing": "Not found (units)",
  "wms.var.found": "Found extra (units)",
  "wms.var.empty": "No variance waiting.",
  "wms.var.quantity": "Variance",
  "wms.var.missingN": "Missing {n}",
  "wms.var.foundN": "Found {n}",
  "wms.var.serials": "Serial numbers",
  "wms.var.lastAt": "Last change",
  "wms.var.netOff": "Net across lots",
  "wms.var.netted": "{n} units netted across lots.",
  "wms.var.confirm": "Confirm ({n})",
  "wms.var.confirmTitle": "Confirm variance",
  "wms.var.confirmOk": "Book it",
  "wms.var.confirmBody": "{loss} units are booked as a loss and {gain} as a gain. On hand changes by these numbers.",
  "wms.var.reasonPlaceholder": "E.g. October stock take, approved by the warehouse lead",
  "wms.var.confirmed": "Booked: loss {loss}, gain {gain}.",
  "wms.pool.title": "Order pools",
  "wms.pool.add": "New pool",
  "wms.pool.editTitle": "Edit pool {name}",
  "wms.pool.saved": "Pool saved.",
  "wms.pool.deleteConfirm": "Delete pool {name}? Its orders move to the next pool that matches.",
  "wms.pool.other": "Other",
  "wms.pool.otherHelp": "Orders no pool takes",
  "wms.pool.inactive": "Inactive",
  "wms.pool.name": "Pool",
  "wms.pool.priority": "Priority",
  "wms.pool.priorityHelp": "Lower numbers are checked first. An order joins the first pool whose rules all match.",
  "wms.pool.active": "Active",
  "wms.pool.method": "Method",
  "wms.pool.orders": "Orders",
  "wms.pool.units": "{n} pcs",
  "wms.pool.split": "Split",
  "wms.pool.overdue": "{n} late",
  "wms.pool.dueSoon": "{n} < 24 h",
  "wms.pool.viewOrders": "View orders",
  "wms.pool.blocked": "{n} open orders cannot be waved yet: an SKU is unmapped or stock is not reserved.",
  "wms.pool.help": "Pools are worked out from open orders whenever you look; new orders and changed rules show at once.",
  "wms.pool.rules": "Rules",
  "wms.pool.rulesHelp": "Every rule given must match. Leave empty for no limit.",
  "wms.pool.any": "Any",
  "wms.pool.shipWithin": "Ship by within",
  "wms.pool.shipWithinHelp": "0 = only orders already late.",
  "wms.pool.hours": "h",
  "wms.pool.platforms": "Marketplaces",
  "wms.pool.stores": "Stores",
  "wms.pool.skus": "SKUs",
  "wms.pool.skuAny": "Orders holding any of these SKUs",
  "wms.pool.skuOnly": "Orders holding only these SKUs",
  "wms.pool.shapes": "Order shape",
  "wms.pool.shape.single_pcs": "1 pcs",
  "wms.pool.shape.single_sku": "1 SKU, several pcs",
  "wms.pool.shape.multi_sku": "Several SKUs",
  "wms.pool.maxUnits": "Max pcs per order",
  "wms.pool.waves": "Waves",
  "wms.pool.cartSlots": "Orders per cluster task",
  "wms.pool.cartSlotsHelp": "How many orders one cluster task fills at once: one tote per order, or one cart slot per order.",
  "wms.pool.batchMin": "Min orders per batch",
  "wms.pool.batchMinHelp": "A batch is made when this many orders have identical contents.",
  "wms.pool.maxOrders": "Max orders per wave",
  "wms.pool.rule.late": "Late",
  "wms.pool.rule.within": "Ship ≤ {n} h",
  "wms.pool.rule.stores": "{n} stores",
  "wms.pool.rule.skus": "Holds {n} SKUs",
  "wms.pool.rule.skusOnly": "Only {n} SKUs",
  "wms.pool.rule.maxUnits": "≤ {n} pcs",
  "wms.pool.rule.none": "Every order",
  "wms.method.auto": "Auto",
  "wms.method.autoHelp": "Single-pcs first, then batches of identical orders, the rest as cluster.",
  "wms.method.single": "Single-pcs",
  "wms.method.singleHelp": "One-pcs orders, any SKU. Picked together; at packing, scan an item and its label prints.",
  "wms.method.batch": "Batch",
  "wms.method.batchHelp": "Orders with identical contents from many buyers: picked once for all.",
  "wms.method.cluster": "Cluster",
  "wms.method.transfer": "Transfer",
  "wms.method.clusterHelp": "Mixed orders. One task fills several orders at once: one tote per order (pick and seed), or one cart slot per order.",
  "wms.wave.title": "Waves",
  "wms.wave.empty": "No waves yet.",
  "wms.wave.release": "Release wave",
  "wms.wave.releaseTitle": "Release wave · {pool}",
  "wms.wave.releaseBody": "{n} orders wait in this pool. Their stock is reserved on the shelves (FEFO, shortest route) and picking tasks are made.",
  "wms.wave.maxOrdersHelp": "Earliest ship-by first.",
  "wms.wave.skipped": "{n} orders skipped",
  "wms.wave.status.open": "Open",
  "wms.wave.status.done": "Done",
  "wms.wave.status.cancelled": "Cancelled",
  "wms.wave.picked": "Picked",
  "wms.wave.tasks": "Picking tasks",
  "wms.wave.orders": "Orders ({n})",
  "wms.wave.units": "Pcs",
  "wms.wave.slot": "Slot {n}",
  "wms.wave.cancel": "Cancel wave",
  "wms.wave.cancelConfirm": "Cancel {number}? Orders not fully picked go back to their pool, and items already picked for them wait in PACKING to go back to a shelf. Orders fully picked or packed stay in the wave to be packed and shipped.",
  "wms.wave.order.picking": "Picking",
  "wms.wave.order.picked": "Picked",
  "wms.wave.order.packed": "Packed",
  "wms.wave.order.shipped": "Shipped",
  "wms.wave.order.removed": "Removed",
  "wms.wave.note.short": "short on shelf",
  "wms.wave.note.order_changed": "order changed",
  "wms.wave.note.wave_cancelled": "wave cancelled",
  "wms.wave.note.task_cancelled": "picking task cancelled",
  "wms.pick.title": "Line {n} of {total}",
  "wms.pick.stepBin": "Bin",
  "wms.pick.stepItem": "Item",
  "wms.pick.stepQty": "Quantity",
  "wms.pick.goTo": "Go to",
  "wms.pick.take": "Take",
  "wms.pick.slots": "Put into (scan its code):",
  "wms.pick.confirm": "Pick",
  "wms.pick.saved": "{code} picked.",
  "wms.pick.short": "Not in the bin",
  "wms.pick.shortConfirm": "Are {n} pcs of {code} missing at {bin}? They move to VARIANCE and are looked for in other bins. Orders that cannot be filled leave the wave.",
  "wms.pick.skip": "Skip for now",
  "wms.pick.wrongBin": "Wrong bin. Go to {code}.",
  "wms.pick.wrongItem": "Wrong item. Pick {code}.",
  "wms.pick.for": "For",
  "wms.paper.title": "Picking without a PDA",
  "wms.paper.help": "Print the picklist (bins in order, quantities and their orders), then enter what the picker wrote on it.",
  "wms.paper.print": "Print picklist",
  "wms.paper.enter": "Enter paper results",
  "wms.paper.enterTitle": "Results of picklist {number}",
  "wms.paper.enterHelp": "Enter what was taken from each bin. Anything short is recorded as not in the bin and looked for in other bins (new lines on this task).",
  "wms.paper.asked": "Asked",
  "wms.paper.taken": "Taken",
  "wms.paper.short": "Short {n}",
  "wms.paper.scanSerial": "Scan serials",
  "wms.paper.bySlot": "This task uses a cart or a tote per order: each pick scans its slot, so record it by scanning.",
  "wms.paper.save": "Save results",
  "wms.paper.saved": "{n} bins recorded.",
  "wms.paper.failed": "{n} bins could not be recorded",
  "wms.pick.forOrders": "{n} orders",
  "wms.pkg.title": "Packaging",
  "wms.pkg.add": "New packaging",
  "wms.pkg.edit": "Edit packaging",
  "wms.pkg.saved": "Packaging saved.",
  "wms.pkg.help": "Boxes and mailers this warehouse packs with. The packing desk suggests the smallest that holds the order: every item fits turned any way, the goods take at most 85% of its volume, and the weight stays under its limit.",
  "wms.pkg.empty": "No packaging yet. Without any, the packing desk cannot suggest a box.",
  "wms.pkg.none": "No packaging in this warehouse yet, so no box is suggested.",
  "wms.pkg.code": "Code",
  "wms.pkg.kind": "Kind",
  "wms.pkg.kind.box": "Box",
  "wms.pkg.kind.mailer": "Mailer / envelope",
  "wms.pkg.inside": "Inside size (mm)",
  "wms.pkg.insideHelp": "Length × width × height of the space inside.",
  "wms.pkg.maxWeight": "Max weight",
  "wms.pkg.maxWeightHelp": "Leave empty for no limit.",
  "wms.pkg.ownWeight": "Own weight",
  "wms.pkg.deleteConfirm": "Delete packaging {code}? Parcels already packed keep its code.",
  "wms.pkg.printBarcodes": "Print barcodes",
  "wms.pkg.printThermal": "100×150 labels (3 per label)",
  "wms.pkg.printA4": "A4 sheet (14 per sheet)",
  "wms.pack.title": "Packing",
  "wms.pack.awaitingHandover": "Awaiting handover: {n}",
  "wms.pack.modeSingle": "Single-pcs (scan to pack)",
  "wms.pack.modeOrder": "Per order (batch, cluster)",
  "wms.pack.queue": "Open waves",
  "wms.pack.queueEmpty": "No open waves.",
  "wms.pack.queueHelp": "Ready = picked orders waiting to be packed. Pick a wave to keep the desk to it.",
  "wms.pack.wave": "Wave",
  "wms.pack.ready": "Ready",
  "wms.pack.pickWave": "Pick a wave on the left to pack order by order.",
  "wms.pack.singleAll": "Single-pcs · all waves",
  "wms.pack.singleInWave": "Single-pcs · {number}",
  "wms.pack.singleHelp": "Scan one item (code, barcode or serial). The one-unit order that needs it with the earliest ship-by is packed and its label prints.",
  "wms.pack.singleHelpBox": "Scan the tote, then the item, then the packaging. The packaging scan closes the parcel: the one-unit order that needs the item (earliest ship-by) is packed in that packaging and its label prints.",
  "wms.pack.scanBox": "Scan the packaging barcode",
  "wms.pack.scanBoxFirst": "Scan the packaging barcode to close the parcel.",
  "wms.pack.boxNeeded": "not scanned yet",
  "wms.pack.boxSuggestedEach": "suggested per parcel (scan a packaging barcode to keep using it)",
  "wms.pack.promptBox": "All items checked. Scan the packaging barcode, or press Enter for the suggested {code}.",
  "wms.pack.promptBoxNoSuggestion": "All items checked. Scan the packaging barcode, or press Enter to close without a listed packaging.",
  "wms.pack.promptBoxRequired": "All items checked. Scan the barcode of the packaging used to close the parcel.",
  "wms.pack.boxByScan": "chosen by scanning",
  "wms.pack.scanned": "Scanned",
  "wms.pack.scanItem": "Scan an item",
  "wms.pack.scanOrder": "Scan an order number or slot number",
  "wms.pack.recent": "Just packed",
  "wms.pack.box": "Packaging",
  "wms.pack.noBox": "No listed packaging",
  "wms.pack.reprint": "Reprint label",
  "wms.pack.labelFailed": "The label of {number} could not print.",
  "wms.pack.noSize": "No size: {codes}.",
  "wms.pack.noWeight": "No weight: {codes}.",
  "wms.pack.ordersOf": "Orders in {number}",
  "wms.pack.readyCount": "{n} orders ready to pack",
  "wms.pack.next": "Next order",
  "wms.pack.notReady": "{code} is not an order ready to pack in this wave.",
  "wms.pack.notPicked": "This order is not fully picked yet.",
  "wms.pack.checked": "Checked",
  "wms.pack.serialTwice": "Serial {serial} was already scanned.",
  "wms.pack.notInOrder": "{code} is not in this order.",
  "wms.pack.scanSerial": "{code}: now scan its IMEI / serial number.",
  "wms.pack.tooMany": "{code} is already complete.",
  "wms.pack.weight": "goods {g} g",
  "wms.pack.suggested": "Suggested",
  "wms.pack.reason.too_small": "{code} does not fit",
  "wms.pack.reason.volume": "Not enough room",
  "wms.pack.reason.weight": "Too heavy",
  "wms.pack.nothingFits": "No packaging holds this order. Pick the closest or add a new one.",
  "wms.pack.close": "Close parcel & print label",
  "wms.pack.restart": "Scan again",
  "wms.pack.unpack": "Unpack",
  "wms.pack.unpackConfirm": "Open parcel {number} again? Its units go back to the packing desk (still reserved for the order) and the order waits to be packed again. A label already printed stays valid for this order. In Zerpid only: the marketplace (tracking number, ready to ship) does not change.",
  "wms.pack.unpacked": "Parcel {number} opened again.",
  "wms.video.noCamera": "The camera cannot be used",
  "wms.video.noCameraHelp": "This warehouse records packing, but the browser has no camera permission or no camera is connected. Allow the camera for this site and reload. Packing still works without video.",
  "wms.video.standby": "Ready to record",
  "wms.video.starting": "Starting the camera…",
  "wms.video.help": "Each parcel is recorded from opening the order until it closes, then uploaded.",
  "wms.video.uploading": "Uploading {n} videos…",
  "wms.video.failed": "{n} videos failed to upload.",
  "wms.video.retry": "Retry {number}",
  "wms.video.unrecordedTitle": "This parcel will not be recorded",
  "wms.video.notReady": "The recorder is not ready.",
  "wms.video.unrecordedHelp": "Pack anyway? You will not be asked again until the recorder is ready.",
  "wms.video.packAnyway": "Pack anyway",
  "wms.desk.title": "Packing desks",
  "wms.desk.help": "A PDA packing on the floor scans a desk's QR first, so each parcel records the desk and when its packing started and ended: find it on that desk's CCTV.",
  "wms.desk.add": "Add desk",
  "wms.desk.edit": "Edit desk",
  "wms.desk.code": "Code",
  "wms.desk.codeHelp": "On the desk's QR, e.g. MEJA-01.",
  "wms.desk.name": "Name",
  "wms.desk.active": "In use",
  "wms.desk.inactive": "Not in use",
  "wms.desk.empty": "No desk yet.",
  "wms.desk.printQr": "Print desk QR",
  "wms.desk.deleteConfirm": "Delete desk {code}? Parcels packed there keep its name.",
  "wms.desk.packedAt": "Desk & time",
  "wms.video.singleLabel": "Single-pcs desk",
  "wms.pack.packed": "{number} packed.",
  "wms.mf.title": "Handover",
  "wms.mf.add": "New handover",
  "wms.mf.start": "Start handover",
  "wms.mf.waiting": "Parcels waiting for a courier",
  "wms.mf.noCarrier": "No carrier",
  "wms.mf.parcelsUnit": "parcels",
  "wms.mf.empty": "No handovers yet.",
  "wms.mf.parcels": "Parcels",
  "wms.mf.parcelsCount": "Parcels ({n})",
  "wms.mf.courier": "Courier name",
  "wms.mf.handedAt": "Handed over",
  "wms.mf.carrierHelp": "Only parcels for this carrier can be scanned onto this handover.",
  "wms.mf.status.open": "Open",
  "wms.mf.status.done": "Handed over",
  "wms.mf.status.cancelled": "Cancelled",
  "wms.mf.cancel": "Cancel handover",
  "wms.mf.cancelConfirm": "Cancel {number}? Its parcels stay packed for another handover.",
  "wms.mf.confirm": "Confirm handover ({n})",
  "wms.mf.confirmTitle": "Hand over {number}",
  "wms.mf.confirmBody": "{n} parcels go to the {carrier} courier. Their stock leaves the warehouse and the orders become Shipped.",
  "wms.mf.confirmOk": "Hand over",
  "wms.mf.handed": "{n} parcels handed over.",
  "wms.mf.handedSummary": "Handed to {courier} by {by}, {at}.",
  "wms.mf.print": "Print",
  "wms.mf.scanHelp": "Scan the tracking number (or order number) of each {carrier} parcel as it goes on the courier's vehicle.",
  "wms.mf.scanPlaceholder": "Scan tracking number",
  "wms.mf.scanned": "{code} added.",
  "wms.mf.tracking": "Tracking no.",
  "wms.mf.weight": "Weight",
  "wms.mf.remove": "Remove",
  "wms.tr.title": "Transfers between warehouses",
  "wms.tr.add": "New transfer",
  "wms.tr.create": "Create & reserve",
  "wms.tr.needTwo": "Transfers need at least two active warehouses. Add one in Settings > Warehouses.",
  "wms.tr.allWarehouses": "All warehouses",
  "wms.tr.out": "Outgoing",
  "wms.tr.in": "Incoming",
  "wms.tr.empty": "No transfers yet.",
  "wms.tr.route": "From → to",
  "wms.tr.unitsCol": "Pcs · shipped · received",
  "wms.tr.unitsHelp": "Shipped but not yet received is stock in transit: out of the source, not yet in the destination.",
  "wms.tr.from": "From warehouse",
  "wms.tr.to": "To warehouse",
  "wms.tr.reserveHelp": "Stock is reserved at the source at once (marketplaces stop selling it from there). A WMS source gets a picking task.",
  "wms.tr.status.picking": "Picking",
  "wms.tr.status.ready": "Ready to ship",
  "wms.tr.status.shipped": "In transit",
  "wms.tr.status.receiving": "Receiving",
  "wms.tr.status.done": "Done",
  "wms.tr.status.cancelled": "Cancelled",
  "wms.tr.step.pick": "Picking",
  "wms.tr.step.reserve": "Reserved",
  "wms.tr.step.ship": "Ship",
  "wms.tr.step.receive": "Receive",
  "wms.tr.step.done": "Done",
  "wms.tr.cancel": "Cancel transfer",
  "wms.tr.cancelConfirm": "Cancel {number}? The reservation is released. Units already picked wait in PACKING to go back to a shelf.",
  "wms.tr.openTask": "Open picking task {number}",
  "wms.tr.ship": "Ship",
  "wms.tr.shipBody": "The units leave the source and are in transit to {to}.",
  "wms.tr.shipNote": "Vehicle, driver or tracking number (optional)",
  "wms.tr.shipped1": "{number} shipped.",
  "wms.tr.shippedSummary": "Shipped by {by}, {at}",
  "wms.tr.completeShortWms": "Units that did not arrive are booked into the destination's SELISIH, to be confirmed in Warehouse > Variance (lost) or netted off when found.",
  "wms.tr.shortBooked": "{n} in SELISIH",
  "wms.tr.shortBookedHelp": "Did not arrive. Booked into the destination's SELISIH; confirm in Warehouse > Variance.",
  "wms.tr.complete": "Complete receiving",
  "wms.tr.count": "Count",
  "wms.tr.counted1": "{qty} × {code} counted. Stock is booked when receiving completes.",
  "wms.tr.overHint": "More than shipped: {left} left, {over} over. Check again before counting.",
  "wms.tr.overTitle": "Receive more than was shipped?",
  "wms.tr.overBody": "{code}: {left} left to receive, you counted {qty}. The {over} extra units are booked in as found when the transfer completes, and recorded on the transfer and in the activity log.",
  "wms.tr.overOk": "Yes, count {n}",
  "wms.tr.overTag": "Over",
  "wms.tr.scansTitle": "Counted (not booked yet)",
  "wms.tr.scansHelp": "Wrong or double scan? Take the row back and scan again. Stock is booked only when receiving completes.",
  "wms.tr.undoScan": "Undo",
  "wms.tr.scanUndone": "Scan taken back.",
  "wms.tr.completeBodyWms": "Everything matches what was shipped. The units are booked into TERIMA and a putaway task is created. Close this transfer?",
  "wms.tr.completeDiff": "What was counted differs from what was shipped:",
  "wms.tr.completeOver": "Extra units are booked in as found at the destination.",
  "error.transfer_differences": "What was counted differs from what was shipped. Check the differences, then confirm.",
  "error.serial_counted": "This serial number is counted already.",
  "wms.tr.completeBody": "Everything matches what was shipped and goes straight into this warehouse's stock. Close this transfer?",
  "wms.tr.completeShort": "Some units have not arrived. The difference stays on this transfer as units that never arrived. Close now?",
  "wms.tr.completed": "Transfer completed.",
  "wms.tr.putawayCreated": "A putaway task was made for what arrived.",
  "wms.tr.quantity": "Quantity",
  "wms.tr.shipped": "Shipped",
  "wms.tr.loweredHelp": "Struck-out quantity: the source shelves were short at picking, so the transfer carries what was found.",
  "wms.tr.receiveTitle": "Receive at {to}",
  "wms.tr.receiveHelp": "Scan an item, enter the quantity, then Count. The stock enters this warehouse when receiving completes.",
  "wms.tr.receiveHelpWms": "Scan an item, enter the quantity (or scan serial numbers), then Count. When receiving completes, the units are booked into TERIMA with the lots they left with, and a putaway task is created.",
  "wms.tr.scanSerial": "Scan serial numbers",
  "wms.tr.left": "{n} left",
  "dashboard.card.newOrdersValue": "{n} new orders",
  "dashboard.card.processingValue": "{n} in processing",
  "dashboard.card.cancelRequestsValue": "{n} cancellation requests waiting",
  "dashboard.card.lowStockValue": "{n} SKUs under threshold",
  "dashboard.card.storesActive": "{n} stores connected",
  "dashboard.card.storesProblem": "{n} stores need attention",
  "dashboard.card.open": "Open",
  "channels.warehouse": "Fulfilling warehouse",
  "channels.warehouseHelp": "This warehouse's stock is sent to the store, and the store's orders are fulfilled from it.",
  "channels.shipping": "Shipping",
  "channels.shippingSaved": "Shipping settings saved.",
  "channels.shipMethod": "Shipping method",
  "channels.arrangeAt": "Book the courier",
  "channels.arrangeAt.process": "Automatically when processed",
  "channels.arrangeAt.pack": "At the packing desk",
  "channels.arrangeAt.manual": "By hand",
  "channels.arrangeAtHelp.process": "The tracking number exists before the order reaches the warehouse pool.",
  "channels.arrangeAtHelp.pack": "The courier is booked when the packing desk prints the label.",
  "channels.arrangeAtHelp.manual": "The team books the courier from the order list.",
  "channels.marketplaceWarehouses": "Marketplace warehouses",
  "channels.marketplaceWarehousesHelp": "For shops with several marketplace warehouses: orders follow the warehouse the marketplace names. Choose the Zerpid warehouse for each.",
  "channels.marketplaceWarehousesEmpty": "No order has named a marketplace warehouse yet.",
  "channels.marketplaceWarehouse": "Marketplace warehouse",
  "channels.orders": "Orders",
  "channels.shipsFrom": "Ships from",
  "channels.warehouseOfStore": "The store's warehouse",
  "channels.warehouseDefault": "Main warehouse (default)",
  "channels.warehouseSaved": "Store warehouse saved.",
  "activity.order.cancelled_by_seller": "Cancelled an order",
  "activity.order.cancel_request_accepted": "Accepted a cancellation request",
  "activity.order.cancel_request_rejected": "Rejected a cancellation request",
  "activity.order.shipment_arranged": "Arranged shipment",
  "activity.order.labels_printed": "Printed labels",
  "activity.warehouse.wms_changed": "Changed WMS settings",
  "activity.warehouse.locations_created": "Created shelf locations",
  "activity.inbound.created": "Created a receipt",
  "activity.inbound.completed": "Completed a receipt",
  "activity.count.created": "Started a stock count",
  "activity.count.closed": "Closed a stock count",
  "activity.count.cancelled": "Cancelled a stock count",
  "activity.pool.created": "Created an order pool",
  "activity.pool.updated": "Changed an order pool",
  "activity.pool.deleted": "Deleted an order pool",
  "activity.wave.released": "Released a wave",
  "activity.wave.cancelled": "Cancelled a wave",
  "activity.packaging.created": "Added a packaging",
  "activity.packaging.updated": "Changed a packaging",
  "activity.packaging.deleted": "Deleted a packaging",
  "activity.manifest.handed_over": "Handed parcels to a courier",
  "activity.transfer.created": "Created a transfer",
  "activity.transfer.shipped": "Shipped a transfer",
  "activity.transfer.received": "Completed receiving a transfer",
  "activity.transfer.cancelled": "Cancelled a transfer",
  "activity.parcel.unpacked": "Opened a packed parcel again",
  "activity.order.warehouse_changed": "Changed an order's warehouse",
  "activity.order.held": "Held an order",
  "activity.order.released": "Released an order",
  "activity.order_hold_rule.created": "Created a hold rule",
  "activity.order_hold_rule.updated": "Updated a hold rule",
  "activity.order_hold_rule.deleted": "Deleted a hold rule",
  "activity.channel.shipping_updated": "Changed a store's shipping settings",
  "activity.channel.warehouse_mapped": "Mapped a marketplace warehouse",
  "activity.promotion.created": "Created a promotion",
  "activity.promotion.updated": "Updated a promotion",
  "activity.promotion.deleted": "Deleted a promotion",
  "activity.aftersales.opened": "Opened an aftersales case",
  "activity.aftersales.updated": "Updated an aftersales case",
  "activity.aftersales.received": "Finished unboxing a return",
  "activity.aftersales.resolved": "Decided an aftersales case",
  "activity.aftersales.cancelled": "Cancelled an aftersales case",
  "activity.aftersales.withdrawn": "The buyer withdrew the return request",
  "activity.aftersales.platform_status": "The return's marketplace status changed",
  "activity.aftersales.linked": "Matched a returned parcel to its order",
  "activity.aftersales.video": "Uploaded an unboxing video",

  "as.title": "Aftersales",
  "as.add": "New case",
  "as.empty": "No aftersales cases.",
  "as.number": "Case no.",
  "as.kindLabel": "Case type",
  "as.reasonLabel": "Actual reason",
  "as.counts": "{open} waiting for goods or a decision · {receiving} being unboxed · {received} waiting for CS",
  "as.filter.active": "Active",
  "as.filter.allKinds": "All types",
  "as.search": "Case no., return tracking no. or order no.",
  "as.col.order": "Order",
  "as.col.units": "Expected · received",
  "as.col.created": "Opened",
  "as.source.marketplace": "From the marketplace",
  "as.source.manual": "Opened by CS",
  "as.source.warehouse": "Opened at the dock",
  "as.kind.return_refund": "Return & refund",
  "as.kind.refund_only": "Refund only",
  "as.kind.failed_delivery": "Failed delivery",
  "as.kind.complaint": "Complaint",
  "as.kind.exchange": "Exchange",
  "as.kind.unidentified": "Unidentified parcel",
  "as.status.open": "Open",
  "as.status.receiving": "Being unboxed",
  "as.status.received": "Inspected",
  "as.status.resolved": "Resolved",
  "as.status.cancelled": "Cancelled",
  "as.reason.none": "Not set",
  "as.reason.wrong_item": "Wrong item sent",
  "as.reason.damaged": "Damaged",
  "as.reason.missing_item": "Missing item",
  "as.reason.not_as_described": "Not as described",
  "as.reason.defective": "Defective",
  "as.reason.changed_mind": "Buyer changed their mind",
  "as.reason.failed_delivery": "Failed delivery",
  "as.reason.other": "Other",
  "as.check.seal": "Seal / original packaging intact",
  "as.check.serial": "Serial number matches",
  "as.check.complete": "Contents complete",
  "as.check.function": "Item works",
  "as.gradeShort.good": "Good",
  "as.gradeShort.damaged": "Damaged",
  "as.gradeShort.incomplete": "Incomplete",
  "as.gradeShort.wrong_item": "Wrong item",
  "as.grade.good": "Sellable: back to sellable stock.",
  "as.grade.damaged": "Damaged: goes to damaged stock, not sold.",
  "as.grade.incomplete": "Incomplete: goes to damaged stock, not sold.",
  "as.grade.wrong_item": "Not what was sent: recorded, not counted as stock.",
  "as.resolution.refund": "Full refund",
  "as.resolution.partial_refund": "Partial refund",
  "as.resolution.rejected": "Rejected",
  "as.resolution.appealed": "Appealed to the marketplace",
  "as.resolution.replacement": "Replacement sent",
  "as.resolution.no_action": "No action",
  "as.resolution.restocked": "Back in stock",
  "as.instructions": "Instructions for the warehouse",
  "as.instructionsHelp": "What the warehouse should look for when unboxing, so it does not need to ask CS.",
  "as.checks": "Must check",
  "as.returnTracking": "Return tracking no.",
  "as.returnTrackingHelp": "Tracking number of the parcel the buyer sends back. Often not the outbound one.",
  "as.returnCarrier": "Return courier",
  "as.warehouse": "Destination warehouse",
  "as.create.title": "Aftersales case for order {number}",
  "as.create.lines": "Items",
  "as.create.linesHelp": "Enter how many units come back to the warehouse.",
  "as.create.linesClaimHelp": "Enter the items the claim is about. Nothing comes back to the warehouse.",
  "as.create.qty": "Quantity",
  "as.item": "Item",
  "as.created": "Case {number} opened.",
  "as.findOrder": "Order number",
  "as.findOrderHelp": "The marketplace order number.",
  "as.orderNotFound": "Order not found.",
  "as.openDock": "Open in returns receiving",
  "as.cancel": "Cancel case",
  "as.cancelConfirm": "Cancel case {number}? Use this when it was opened by mistake or the buyer withdrew before sending anything back.",
  "as.receivingNow": "Warehouse {warehouse} is unboxing this parcel.",
  "as.unidentifiedHelp": "This parcel arrived without a case and its order is not known yet. Match it to its order before deciding.",
  "as.buyer": "Buyer's request",
  "as.buyerReason": "Buyer's reason",
  "as.refundRequested": "Refund requested",
  "as.buyerNote": "Buyer's note",
  "as.platformStatus": "Marketplace status",
  "as.externalId": "Marketplace return ID",
  "as.expected": "Expected items",
  "as.noLines": "No items are expected back.",
  "as.expectedCol": "Expected",
  "as.receivedCol": "Received",
  "as.outboundTracking": "Outbound tracking no.",
  "as.orderNotInZerpid": "This order predates Zerpid's data, so its details are not available.",
  "as.result": "Warehouse inspection",
  "as.noResult": "Not inspected yet.",
  "as.receivedBy": "Inspected by {by} at {warehouse} · {at}",
  "as.unit.serial": "SN {sn}",
  "as.unit.lot": "Lot {lot}",
  "as.note": "Note",
  "as.by": "By",
  "as.noVideo": "No unboxing video.",
  "as.playVideo": "Play unboxing video",
  "as.saved": "Case saved.",
  "as.cs": "CS instructions",
  "as.decision": "Decision",
  "as.refundAmount": "Refund amount",
  "as.resolvedMsg": "Decision saved.",
  "as.waitReceiving": "The warehouse is unboxing. Decide once the inspection is done.",
  "as.linkFirst": "Match the parcel to its order first.",
  "as.goodsPending": "The goods have not reached the warehouse. Make sure the decision does not need the inspection.",
  "as.resolve": "Save decision",
  "as.link": "Match to an order",
  "as.linkHelp": "Find the order by its number. What the warehouse recorded stays; the order's items become the expected items.",
  "as.linked": "Parcel matched to its order.",
  "as.orderCard": "Aftersales",
  "as.orderCardEmpty": "No cases for this order.",
  "as.dock.title": "Returns receiving",
  "as.dock.videoHelp": "Each parcel is recorded from opening the case until the inspection ends, then uploaded.",
  "as.dock.scanTitle": "Scan the parcel",
  "as.dock.scan": "Scan the return or outbound tracking no., or the order no.",
  "as.dock.pick": "Several cases match. Pick the right one.",
  "as.dock.open": "Open",
  "as.dock.orderNoCase": "Order {number} has no aftersales case.",
  "as.dock.orderNoCaseHelp": "Choose how it arrived. CS sees the inspection on the new case.",
  "as.dock.asFailed": "Receive as failed delivery",
  "as.dock.asReturn": "Receive as a buyer return",
  "as.dock.notFound": "No case or order for {code}.",
  "as.dock.unknown": "Receive as unidentified",
  "as.dock.unknownHelp": "The parcel is unboxed and inspected now. CS matches it to its order later.",
  "as.dock.inProgress": "Being unboxed here",
  "as.dock.noneInProgress": "No parcel is being unboxed.",
  "as.dock.startUnboxing": "Start unboxing",
  "as.dock.tracking": "Tracking no. on the parcel",
  "as.dock.finished": "Inspection of {number} finished.",
  "as.dock.finishedWms": "Good units wait in receiving with a putaway task; damaged ones are in the damaged location.",
  "as.dock.finishedLevel": "Good units are back in sellable stock; damaged and incomplete ones are damaged stock.",
  "as.dock.next": "Next parcel",
  "as.dock.videoFailed": "The unboxing video failed to upload.",
  "as.dock.order": "Order {number}",
  "as.dock.leave": "Leave",
  "as.dock.finish": "Finish inspection",
  "as.dock.finishConfirm": "Good units go to sellable stock, damaged and incomplete ones to damaged stock; wrong items are not counted. Items cannot change afterwards.",
  "as.dock.finishConfirmWms": "Good units go to receiving with a putaway task, damaged and incomplete ones to the damaged location; wrong items are not counted. Items cannot change afterwards.",
  "as.dock.finishNote": "Inspection note (optional)",
  "as.dock.instructions": "CS instructions",
  "as.dock.noInstructions": "No special instructions from CS.",
  "as.dock.scanItem": "Scan items",
  "as.dock.grade": "Condition",
  "as.dock.quantity": "Qty",
  "as.dock.itemNote": "Item note (optional)",
  "as.dock.scanItemPlaceholder": "Scan the item's SKU code or barcode",
  "as.dock.notOurs": "Not our item",
  "as.dock.notOursHelp": "For anything that is not our product (another item, a substitute object). Recorded as evidence, not stocked.",
  "as.dock.notOursDesc": "Description",
  "as.dock.items": "Items found",
  "as.dock.serialTitle": "Serial number of {code}",
  "as.dock.lotTitle": "Lot of {code}",
  "as.dock.serial": "Scan the serial number",
  "as.dock.lotHelp": "The lot shipped was not recorded. Enter the lot and expiry from the packaging.",
  "as.dock.lotNo": "Lot no.",
  "as.dock.lotExpiry": "Expiry",
  "as.dock.closed": "This case's inspection is finished.",
  "activity.task.move_created": "Created a move task",
  "activity.settings.labels_updated": "Changed label settings",
  "activity.stock.adjusted": "Adjusted stock",
  "activity.stock.moved": "Changed stock condition",
  "activity.stock.held": "Held stock",
  "activity.stock.hold_released": "Released held stock",
  "activity.stock.variance_confirmed": "Confirmed stock variance",
  "activity.stock.variance_netted": "Netted variance across lots",
  "activity.channel.warehouse_changed": "Changed a store's warehouse",

  "page.forbidden.title": "Access denied",
  "page.forbidden.body": "Your role does not have access to this page. Contact the company owner if you need it.",
  "page.notFound.title": "Page not found",
  "page.notFound.body": "The address you opened does not exist or has moved.",
  "page.toHome": "Go to Home",
  "page.error.title": "Something went wrong",
  "nav.procurement": "Purchasing",
  "nav.procurement.orders": "Purchase Orders",
  "nav.procurement.suppliers": "Suppliers",
  "error.po_state": "This purchase order cannot do that in its current status.",
  "error.po_has_receipts": "Goods were already received on this order; close it instead.",
  "error.po_receipt_open": "A receipt for this order is still open in the warehouse.",
  "error.po_nothing_outstanding": "Everything on this order has been received.",
  "error.supplier_inactive": "That supplier is archived.",
  "error.supplier_code_taken": "Another supplier already has this code.",
  "error.supplier_not_found": "Supplier not found.",
  "error.sku_not_on_order": "This SKU is not on the purchase order.",
  "error.supplier_email_missing": "Add an email address to the supplier first.",
  "error.supplier_phone_missing": "Add a phone number to the supplier first.",
  "error.warehouse_without_wms": "This warehouse has no WMS: receive the goods on the order directly.",
  "error.warehouse_with_wms": "This warehouse uses WMS: open a receipt and scan the goods in.",
  "inventory.incomingPo": "+{n} ordered",
  "inventory.incomingPoHelp": "Incoming (PO): ordered from suppliers for this warehouse, not received yet. Not part of stock or available.",
  "inventory.incomingPoTitle": "Incoming purchase orders",
  "po.title": "Purchase Orders",
  "po.add": "New purchase order",
  "po.search": "Search number or supplier",
  "po.empty": "No purchase orders yet.",
  "po.number": "PO number",
  "po.supplier": "Supplier",
  "po.warehouse": "Ship to warehouse",
  "po.orderDate": "Order date",
  "po.expectedDate": "Expected",
  "po.paymentTerms": "Payment terms (days)",
  "po.paymentTermsValue": "{n} days",
  "po.cod": "Cash on delivery",
  "po.tax": "PPN",
  "po.taxNone": "No PPN",
  "po.taxRate": "PPN {rate}%",
  "po.note": "Note to supplier",
  "po.lines": "Items",
  "po.addLine": "Add item",
  "po.quantity": "Qty",
  "po.unitPrice": "Unit price",
  "po.unitPriceHelp": "Empty uses the supplier's contract price, else the last approved price.",
  "po.amount": "Amount",
  "po.received": "Received",
  "po.outstanding": "Outstanding",
  "po.subtotal": "Subtotal",
  "po.total": "Total",
  "po.createdBy": "Created by",
  "po.approvedBy": "Approved by",
  "po.status.draft": "Draft",
  "po.status.submitted": "Waiting approval",
  "po.status.approved": "Approved",
  "po.status.sent": "Sent",
  "po.status.partial": "Partly received",
  "po.status.received": "Received",
  "po.status.closed": "Closed",
  "po.status.cancelled": "Cancelled",
  "po.filter.open": "Open",
  "po.filter.waiting": "Waiting approval",
  "po.saveDraft": "Save draft",
  "po.submit": "Submit for approval",
  "po.submitted": "Sent to the owner for approval.",
  "po.approve": "Approve",
  "po.approved": "Purchase order approved.",
  "po.reject": "Send back",
  "po.rejectTitle": "Send back to draft",
  "po.rejectNote": "What should change?",
  "po.rejected": "Sent back with a note: {note}",
  "po.waitingApproval": "Waiting for the owner's approval.",
  "po.edit": "Edit",
  "po.send": "Send to supplier",
  "po.sendEmail": "Email with PDF",
  "po.sendWhatsApp": "WhatsApp",
  "po.downloadPdf": "Download PDF",
  "po.markSent": "Mark as sent",
  "po.emailTitle": "Email the purchase order",
  "po.emailTo": "To",
  "po.emailToHelp": "Empty sends to the supplier's email.",
  "po.emailMessage": "Message (optional)",
  "po.emailed": "Purchase order emailed with its PDF.",
  "po.whatsappOpened": "WhatsApp opened with the message and the PDF link.",
  "po.sentVia.email": "by email",
  "po.sentVia.whatsapp": "by WhatsApp",
  "po.sentVia.manual": "manually",
  "po.sentOn": "Sent {when} {via}",
  "po.receive": "Receive goods",
  "po.receiveTitle": "Receive goods",
  "po.receiveHelp": "Enter what arrived. Stock in {warehouse} goes up right away.",
  "po.receiveNone": "Enter at least one quantity.",
  "po.receivedOk": "Goods received into stock.",
  "po.openReceipt": "Open warehouse receipt",
  "po.openReceiptHelp": "{warehouse} uses WMS: the warehouse scans the goods on a receipt.",
  "po.receipts": "Receipts",
  "po.close": "Close order",
  "po.closeConfirm": "Close {number}? What is still outstanding will no longer be expected.",
  "po.cancel": "Cancel order",
  "po.cancelReason": "Reason (optional)",
  "po.cancelConfirm": "Cancel {number}?",
  "po.noSuppliers": "Add a supplier first.",
  "supplier.title": "Suppliers",
  "supplier.add": "New supplier",
  "supplier.edit": "Edit supplier",
  "supplier.search": "Search name, code, contact or phone",
  "supplier.empty": "No suppliers yet.",
  "supplier.code": "Code",
  "supplier.codeHelp": "Empty numbers it automatically (SUP-0001).",
  "supplier.name": "Name",
  "supplier.contact": "Contact person",
  "supplier.phone": "Phone / WhatsApp",
  "supplier.email": "Email",
  "supplier.address": "Address",
  "supplier.npwp": "NPWP",
  "supplier.pkp": "PKP (charges PPN)",
  "supplier.pkpHelp": "Purchase orders from a PKP supplier add PPN 11% by default.",
  "supplier.terms": "Payment terms (days)",
  "supplier.termsHelp": "0 is cash on delivery.",
  "supplier.method": "Payment method",
  "supplier.method.transfer": "Bank transfer",
  "supplier.method.cash": "Cash",
  "supplier.method.giro": "Giro",
  "supplier.method.other": "Other",
  "supplier.bank": "Bank",
  "supplier.bankAccountNo": "Account number",
  "supplier.bankAccountName": "Account name",
  "supplier.note": "Note",
  "supplier.status.active": "Active",
  "supplier.status.archived": "Archived",
  "supplier.archive": "Archive",
  "supplier.restore": "Restore",
  "supplier.openOrders": "Open orders",
  "supplier.saved": "Supplier saved.",
  "supplier.prices": "Prices per SKU",
  "supplier.pricesHelp": "The contract price fills new purchase order lines; without one, the price of the last approved order is used.",
  "supplier.contractPrice": "Contract price",
  "supplier.lastPrice": "Last price",
  "supplier.addPrice": "Add SKU price",
  "supplier.pricesSaved": "Prices saved.",
  "supplier.orders": "Purchase orders",
  "supplier.newOrder": "New purchase order",
  "po.filter.incoming": "Waiting for goods",
  "po.draftSaved": "Draft saved.",
  "nav.finance.costs": "Cost of goods",
  "cost.title": "Cost of goods & inventory value",
  "cost.totalValue": "Inventory value",
  "cost.totalUnits": "{n} units on hand, at average cost before PPN",
  "cost.units": "On hand",
  "cost.value": "Value",
  "cost.unitCost": "Average cost (HPP)",
  "cost.name": "Name",
  "cost.updatedAt": "Cost updated",
  "cost.missing": "No cost yet",
  "cost.missingAlert": "{n} units have no cost yet and are left out of the value. Set an opening cost for them, or receive them through a purchase order.",
  "cost.showMissing": "Show them",
  "cost.search": "Search SKU code or name",
  "cost.filter.all": "All SKUs",
  "cost.filter.missing": "Stock without a cost",
  "cost.filter.set": "Has a cost",
  "cost.empty": "No SKUs",
  "cost.setOpening": "Set opening cost",
  "cost.correct": "Correct the cost",
  "cost.setHelp": "For stock that was here before its purchase price was recorded, or to correct the average. Goods received later at a price are averaged from this value.",
  "cost.note": "Note (e.g. from the last stock count)",
  "cost.saved": "Cost saved.",
  "cost.history": "Cost history",
  "cost.historyEmpty": "No changes yet. The cost updates when goods arrive with a purchase price.",
  "cost.when": "When",
  "cost.source": "Source",
  "cost.in": "Received",
  "cost.change": "Average",
  "cost.by": "By",
  "cost.kind.manual": "Set by hand",
  "error.sku_not_single": "A bundle's cost is the sum of its components.",
  "cost.warehouse": "Warehouse",
  "nav.finance.bills": "Supplier bills",
  "bill.title": "Supplier bills",
  "bill.status.draft": "To check",
  "bill.status.open": "Unpaid",
  "bill.status.partial": "Partly paid",
  "bill.status.paid": "Paid",
  "bill.status.void": "Void",
  "bill.overdue": "Overdue",
  "bill.outstanding": "Owed",
  "bill.outstandingHelp": "Confirmed bills not yet paid",
  "bill.overdueTotal": "Overdue",
  "bill.overdueBills": "{n} bills past their due date",
  "bill.dueSoon": "Due in 7 days",
  "bill.dueSoonHelp": "Plan these payments",
  "bill.toCheck": "To check",
  "bill.toCheckHelp": "{n} drafts from goods received",
  "bill.aging": "Aging per supplier",
  "bill.agingCurrent": "Not due",
  "bill.aging1to30": "1–30 days",
  "bill.aging31to60": "31–60 days",
  "bill.aging61to90": "61–90 days",
  "bill.agingOver90": "> 90 days",
  "bill.search": "Search bill, invoice, PO or supplier",
  "bill.filter.unpaid": "Unpaid",
  "bill.filter.draft": "To check",
  "bill.number": "Bill no.",
  "bill.invoiceNo": "Supplier invoice no.",
  "bill.invoiceNoHelp": "The number on the supplier's invoice (faktur). Needed to confirm.",
  "bill.invoiceDate": "Invoice date",
  "bill.dueDate": "Due date",
  "bill.paid": "Paid",
  "bill.balance": "Left to pay",
  "bill.empty": "No bills",
  "bill.void": "Void",
  "bill.recordPayment": "Record payment",
  "bill.draftHelp": "Drafted from goods received. Check it against the supplier's invoice: invoice number, prices and PPN, then confirm.",
  "bill.voided": "Voided: {note}",
  "bill.source": "From",
  "bill.confirmedBy": "Confirmed by",
  "bill.payments": "Payments",
  "bill.noPayments": "No payments yet",
  "bill.paymentNo": "Payment no.",
  "bill.paidOn": "Paid on",
  "bill.reference": "Reference",
  "bill.referencePlaceholder": "e.g. bank transfer number",
  "bill.amount": "Amount",
  "bill.paymentVoid": "Voided",
  "bill.voidPaymentConfirm": "Void this payment? It stays in the history, struck out.",
  "bill.paymentVoided": "Payment voided.",
  "bill.paymentSaved": "Payment recorded.",
  "bill.balanceLeft": "Left to pay: {amount}",
  "bill.lines": "Goods received",
  "bill.check": "Check against the supplier's invoice",
  "bill.adjustment": "Adjustment",
  "bill.adjustmentHelp": "Outside PPN: shipping charged (+), a discount (−), rounding.",
  "bill.adjustmentNote": "Adjustment for",
  "bill.adjustmentPlaceholder": "e.g. Shipping",
  "bill.saveDraft": "Save draft",
  "bill.confirm": "Confirm bill",
  "bill.saved": "Draft saved.",
  "bill.confirmed": "Bill confirmed.",
  "bill.voidTitle": "Void this bill?",
  "bill.voidHelp": "For a bill that is not owed, such as a duplicate. Say why.",
  "bill.voidPlaceholder": "Reason",
  "error.bill_state": "This bill cannot do that in its current status.",
  "error.bill_has_payments": "Void the payments on this bill first.",
  "error.payment_exceeds_balance": "The payment is more than what is left to pay.",
  "error.bill_negative": "The bill total cannot be below zero.",
  "error.payment_void": "This payment was already voided.",
  "wms.task.scanSku": "Scan an SKU on this task",
  "wms.task.scanBin": "Scan the destination bin (Enter: use the suggestion)",
  "wms.task.scanSerial": "Scan each serial number",
  "wms.task.stepSku": "SKU",
  "wms.task.stepQty": "Quantity",
  "wms.task.stepBin": "Bin",
  "wms.task.changeBin": "Change",
  "wms.task.binHelp": "Scan the bin label, or pick it from the list. Scanning the bin saves the line.",
  "wms.task.pickBin": "Pick a bin",
  "wms.task.noBins": "No bins in this warehouse yet. Add them under Warehouse > Locations.",
  "wms.task.useSuggested": "Use suggested bin {code}",
  "orders.step.wave": "Added to wave",
  "orders.step.pick_started": "Picking started",
  "orders.step.picked": "Picked",
  "orders.step.packed": "Packed",
  "orders.step.label_printed": "Label printed",
  "orders.step.handed_over": "Handed to courier",
  "orders.step.prints": "{n}× printed",
  "mpStatus.other": "{status}",
  "mpStatus.UNPAID": "Unpaid",
  "mpStatus.ON_HOLD": "On hold (cancellation window)",
  "mpStatus.AWAITING_SHIPMENT": "Awaiting shipment",
  "mpStatus.PARTIALLY_SHIPPING": "Partly shipped",
  "mpStatus.AWAITING_COLLECTION": "Awaiting courier pickup",
  "mpStatus.IN_TRANSIT": "In transit",
  "mpStatus.DELIVERED": "Delivered",
  "mpStatus.COMPLETED": "Completed",
  "mpStatus.CANCELLED": "Cancelled",
  "mpStatus.CANCELED": "Cancelled",
  "mpStatus.READY_TO_SHIP": "Ready to ship",
  "mpStatus.PROCESSED": "Shipment arranged",
  "mpStatus.RETRY_SHIP": "Retry shipment",
  "mpStatus.SHIPPED": "Shipped",
  "mpStatus.TO_CONFIRM_RECEIVE": "Delivered, awaiting buyer",
  "mpStatus.IN_CANCEL": "Cancellation requested",
  "mpStatus.TO_RETURN": "Return requested",
  "mpStatus.INVOICE_PENDING": "Invoice pending",
  "mpStatus.PENDING": "Pending",
  "mpStatus.PACKED": "Packed",
  "mpStatus.CONFIRMED": "Received by buyer",
  "mpStatus.FAILED_DELIVERY": "Delivery failed",
  "mpStatus.RETURNED": "Returned",
  "mpStatus.LOST_BY_3PL": "Lost by courier",
  "mpStatus.DAMAGED_BY_3PL": "Damaged by courier",
  // Blibli item statuses (Order List V2; PC seen on staging).
  "mpStatus.C": "Awaiting Blibli's verification",
  "mpStatus.FP": "New order",
  "mpStatus.PC": "Package created",
  "mpStatus.PU": "Ready for courier pickup",
  "mpStatus.PF": "Partially fulfilled",
  "mpStatus.BP": "Ready, delivered by seller",
  "mpStatus.CR": "Buyer asked to cancel",
  "mpStatus.CX": "In delivery",
  "mpStatus.D": "Delivered",
  "mpStatus.DF": "Delivery failed",
  "mpStatus.OS": "Cancelled, out of stock",
  "mpStatus.X": "Cancelled",
  "wms.pool.auto.title": "Automatic release",
  "wms.pool.auto.help": "The pool releases its own wave on schedule, as Sistem. Times are in the workspace's time zone.",
  "wms.pool.auto.off": "Off (release by hand)",
  "wms.pool.auto.times": "At set times",
  "wms.pool.auto.interval": "Every N hours",
  "wms.pool.auto.timesLabel": "Release at",
  "wms.pool.auto.timesHelp": "Usually a little before each courier pickup cut-off.",
  "wms.pool.auto.everyLabel": "Every",
  "wms.pool.auto.hours": "hours",
  "wms.pool.auto.from": "From",
  "wms.pool.auto.until": "Until",
  "wms.pool.auto.minOrders": "At least (orders)",
  "wms.pool.auto.minOrdersHelp": "With fewer orders waiting the time is skipped; they wait for the next one.",
  "wms.pool.auto.atTimes": "Auto at {times}",
  "wms.pool.auto.every": "Auto every {n} h, {from}–{until}",
  "wms.pool.auto.next": "next {at}",
  "wms.pool.auto.lastReleased": "last: {wave}",
  "wms.pool.auto.last.nothing": "last: nothing to pick",
  "wms.pool.auto.last.below_min": "last: below the minimum",
  "wms.pool.auto.last.failed": "last: failed",
  "error.wave_below_minimum": "Fewer orders wait than the pool's minimum for an automatic wave.",
  "wms.task.takeNext": "Take next task",
  "wms.task.unassigned": "— Free —",
  "wms.task.mine": "You",
  "error.no_open_task": "No task is waiting in this warehouse.",
  "error.not_a_warehouse_worker": "That member cannot work in this warehouse.",
  "wms.pick.scanTote": "Scan an empty tote first: everything this task picks goes into it.",
  "wms.pick.scanCart": "Scan the tote for order #{n} of {total} (one tote per order, in order), or a cart's label.",
  "wms.pick.scanTotePlaceholder": "Scan tote (TOTE-...)",
  "wms.pick.scanCartPlaceholder": "Scan a tote (TOTE-...) or cart (CART-...)",
  "wms.pick.scanSlotPlaceholder": "Scan the order's tote or slot label (e.g. CART-01-03)",
  "wms.pick.stepSlot": "Slot",
  "wms.pick.intoTote": "Into tote",
  "wms.pick.intoCart": "Into cart",
  "wms.pick.useTote": "Use a tote",
  "wms.pick.useCart": "Use a cart",
  "wms.pick.containerBound": "{code} ready.",
  "wms.pick.wrongSlot": "No order in {code} needs these units.",
  "error.container_required": "Scan a tote (cluster: one tote per order, or a cart) for this task first.",
  "error.container_not_found": "No tote or cart has that code in this warehouse.",
  "error.container_kind": "Wrong container: cluster takes one tote per order or a cart; single and batch take one tote.",
  "error.container_busy": "That tote or cart is in use (another task, or not emptied yet).",
  "error.container_locked": "Picking has started into another tote or cart.",
  "error.cart_slot_scanned": "Scan the cart's own label, not one slot.",
  "error.cart_too_small": "This cart has fewer slots than the task has orders.",
  "error.slot_required": "Scan the slot the units go into.",
  "error.wrong_slot": "That slot's order does not need these units.",
  "wms.pack.fromTote": "From tote",
  "wms.pack.toteEmpty": "Nothing waits to be packed in {code}.",
  "wms.pack.scanTitle": "Scan a tote, a cart slot or an order",
  "wms.pack.pickWaveOrScan": "Scan a tote or cart slot, or pick a wave on the left.",
  "nav.warehouse.containers": "Totes & carts",
  "wms.ct.title": "Totes & carts",
  "wms.ct.helpRequired": "This warehouse picks into totes and carts: single and batch tasks fill one tote, cluster tasks one tote per order or a cart's slots. Print the labels and stick them on.",
  "wms.ct.helpOptional": "Picking into totes and carts is optional here (turn it on under Settings > Warehouses > WMS). Pickers can still choose one per task.",
  "wms.ct.addTote": "Add totes",
  "wms.ct.addCart": "Add cart",
  "wms.ct.print": "Print labels ({n})",
  "wms.ct.empty": "No totes or carts yet.",
  "wms.ct.code": "Code",
  "wms.ct.kind": "Kind",
  "wms.ct.tote": "Tote",
  "wms.ct.cartSlots": "Cart · {n} slots",
  "wms.ct.inTask": "In use: {task} ({who})",
  "wms.ct.holding": "Holding goods",
  "wms.ct.free": "Free",
  "wms.ct.units": "Units",
  "wms.ct.count": "How many",
  "wms.ct.countHelpTote": "Numbered on from the last: TOTE-0001, TOTE-0002, ...",
  "wms.ct.countHelpCart": "Numbered on from the last: CART-01, CART-02, ...",
  "wms.ct.slots": "Slots per cart",
  "wms.ct.slotsHelp": "Each slot gets its own label (CART-01-01, ...). A cluster wave puts one order in each.",
  "wms.ct.added": "Added: {codes}",
  "settings.warehouses.containerRequired": "Pick into totes and carts",
  "settings.warehouses.containerRequiredHelp": "Every picking task starts with a tote scan (single, batch), or one tote per order or a cart (cluster); the packing desk scans a tote or slot to call its order.",
  "common.refresh": "Refresh",
  "orders.refreshFromMarketplace": "Refresh from marketplace",
  "orders.refreshed": "Order refreshed from the marketplace.",
  "orders.arrangeOnHold": "The buyer can still cancel (cancellation window): the marketplace has no package to ship yet. Zerpid books the courier as soon as it can.",
  "error.package_not_ready": "The marketplace has not made this order's package yet: it is still in the buyer's cancellation window. Zerpid books the courier as soon as it can.",
  "error.marketplace_permission": "The marketplace has not given Zerpid permission for this yet.",
  "logs.entity.supplier": "Supplier",
  "logs.entity.purchase_order": "Purchase order",
  "logs.entity.sku_cost": "Cost of goods",
  "logs.entity.supplier_bill": "Supplier bill",
  "activity.aftersales.answered": "Answered the marketplace on a return",
  "activity.aftersales.reopened": "The marketplace reopened a return (appeal)",
  "activity.aftersales.evidence": "Added an evidence picture",
  "activity.aftersales.evidence_removed": "Removed an evidence picture",
  "activity.aftersales.corrected": "Corrected a shipment (stock and cost of goods)",
  "as.respond.overdue": "Answer overdue",
  "as.respond.inHours": "Answer within {n} h",
  "as.respond.until": "Answer by {at}",
  "as.respond.deadline": "The marketplace waits for your answer until {at}; without it, it usually decides for the buyer.",
  "as.kindShort.return_refund": "Return",
  "as.kindShort.refund_only": "Refund",
  "as.kindShort.failed_delivery": "Failed delivery",
  "as.kindShort.complaint": "Complaint",
  "as.kindShort.exchange": "Exchange",
  "as.kindShort.unidentified": "Return",
  "as.progress.respond": "needs your answer",
  "as.progress.receipt": "confirm the parcel",
  "as.progress.receiving": "being unboxed",
  "as.progress.received": "checked by the warehouse",
  "as.progress.resolved": "decided",
  "as.progress.cancelled": "cancelled",
  "as.progress.decide": "waiting for CS",
  "as.progress.awaitingShip": "buyer to ship back",
  "as.progress.inTransit": "on its way back",
  "as.progress.awaitingGoods": "waiting for the goods",
  "as.filter.respond": "Needs an answer",
  "as.filter.late": "Not back after 7 days",
  "as.col.respondBy": "Answer by",
  "as.countsRespond": "{n} need an answer ({urgent} urgent)",
  "as.ev.source.packing_video": "Packing video",
  "as.ev.source.unboxing_video": "Unboxing video",
  "as.ev.source.upload": "Photo",
  "as.ev.captured": "Picture taken at {at}.",
  "as.ev.capture": "Take evidence picture",
  "as.ev.captureHelp": "Pause where the item and the label show; the frame is saved with the order and the time.",
  "as.ev.packing": "Packing evidence",
  "as.ev.noParcel": "This order was not packed in a Zerpid warehouse: there is no packing record or video.",
  "as.ev.heldUntil": "Kept for this case until {at}, then deleted automatically.",
  "as.ev.title": "Evidence pictures",
  "as.ev.upload": "Upload photo",
  "as.ev.empty": "No pictures yet. Take them from the packing or unboxing video, or upload photos (chat screenshots, the parcel).",
  "as.ev.sent": "Sent to marketplace",
  "as.ev.remove": "Remove this picture?",
  "as.answer.title": "Answer the marketplace",
  "as.answer.ask.refund": "The buyer asks for a refund without returning the goods.",
  "as.answer.ask.return": "The buyer asks to return the goods.",
  "as.answer.askReceipt": "The returned parcel is on its way or has arrived: confirm it arrived as claimed, or reject it.",
  "as.answer.approve": "Approve",
  "as.answer.reject": "Reject",
  "as.answer.partial": "Offer a partial refund",
  "as.answer.approveReceipt": "Parcel as claimed",
  "as.answer.rejectReceipt": "Reject the parcel",
  "as.answer.effect.refund": "The marketplace refunds {amount} to the buyer. The goods do not come back and stock does not change. The case closes.",
  "as.answer.effect.return": "The buyer sends the goods back; the returns dock receives and grades them. The refund follows when you confirm the parcel.",
  "as.answer.effect.exchange": "The buyer sends the goods back for a replacement; the returns dock receives them.",
  "as.answer.effect.receipt": "The marketplace refunds {amount} to the buyer. The case closes.",
  "as.answer.goodsNotChecked": "The warehouse has not received or checked this parcel in Zerpid yet.",
  "as.answer.rejectEffect": "The marketplace gets the reason, your comment and the pictures. The buyer may still appeal to the marketplace; the case reopens if so.",
  "as.answer.reason": "Reason (the marketplace's list)",
  "as.answer.comment": "Comment",
  "as.answer.commentHelp": "What the evidence shows, e.g. the packing video shows 2 units going in.",
  "as.answer.pictures": "Pictures sent with it (at most {n})",
  "as.answer.tooMany": "At most {n} pictures.",
  "as.answer.noPictures": "No pictures yet: a rejection without evidence is often overturned. Take frames from the packing video first.",
  "as.answer.partialEffect": "The buyer keeps the goods and is offered part of the {amount} requested. The case closes when the marketplace pays it.",
  "as.answer.send": "Send to marketplace",
  "as.answer.sent": "Answer sent to the marketplace.",
  "as.answer.evidenceHint": "Rejecting? Take evidence pictures from the packing video first.",
  "as.answer.inSellerCenter": "The marketplace waits for the seller's answer on this case. This marketplace is answered in its seller center.",
  "as.records.title": "Marketplace history",
  "as.records.role.BUYER": "Buyer",
  "as.records.role.SELLER": "Seller",
  "as.records.role.PLATFORM": "Marketplace",
  "as.records.role.SYSTEM": "System",
  "as.fix.title": "Shipment correction",
  "as.fix.open": "Correct the shipment",
  "as.fix.shippedHelp": "What the books say left with this order. When the packing video shows otherwise, correct it: stock and the cost of goods follow.",
  "as.fix.shipped": "Booked out",
  "as.fix.done": "Corrected by {by} · {at}",
  "as.fix.back": "{n} did not leave (back to stock)",
  "as.fix.out": "{n} left without being booked",
  "as.fix.scenario": "What happened",
  "as.fix.scenario.wrong_item": "Wrong item sent",
  "as.fix.scenario.short": "Fewer sent",
  "as.fix.scenario.over": "More sent",
  "as.fix.help.wrong_item": "Another SKU went out instead of the ordered one: the ordered units go back to stock, the SKU sent comes off it, and the goods coming back are the SKU sent.",
  "as.fix.help.short": "Fewer units went out than booked: the missing units go back to stock (to the bin they were picked from).",
  "as.fix.help.over": "More units went out than booked: they come off stock.",
  "as.fix.orderedSku": "Ordered SKU (booked out)",
  "as.fix.shortSku": "SKU sent short",
  "as.fix.actualSku": "SKU actually sent",
  "as.fix.extraSku": "SKU sent on top",
  "as.fix.note": "Why",
  "as.fix.noteHelp": "What shows it, e.g. packing video 00:12: SKU-03 goes into the box.",
  "as.fix.effect": "Stock and the cost of goods sold change at once and marketplaces are told the new stock. A case is corrected once.",
  "as.fix.save": "Correct",
  "as.fix.saved": "Shipment corrected.",
  "orders.caseFilter.all": "All aftersales",
  "orders.caseFilter.respond": "Return: needs an answer",
  "orders.caseFilter.active": "Open return or complaint",
  "orders.caseFilter.any": "Has a return or complaint",
  "nav.products": "Master products",
  "activity.listing.created": "Created a marketplace product",
  "activity.listing.updated": "Updated a marketplace product",
  "activity.listing.image_added": "Added a product picture",
  "activity.listing.image_removed": "Removed a product picture",
  "activity.listing.channel_saved": "Set a product's store terms",
  "activity.listing.pushed": "Published a product to a store",
  "activity.listing.push_edited": "Updated a product in a store",
  "activity.listing.push_failed": "A store refused a product",
  "activity.listing.status": "A product's status in a store changed",
  "activity.listing.imported": "Imported a product from a store",
  "prod.title": "Master products",
  "prod.subtitle": "One product, published to every store in its terms. Variants are your SKUs: stock and orders stay with them.",
  "prod.add": "New product",
  "prod.import": "Pull from store listings",
  "prod.show.active": "Active",
  "prod.show.archived": "Archived",
  "prod.search": "Name or SKU code",
  "prod.filter.anyStatus": "Any status",
  "prod.empty": "No products yet. Create one, or import the ones already in a store.",
  "prod.col.product": "Product",
  "prod.col.price": "Price",
  "prod.col.stores": "Stores",
  "prod.col.updated": "Updated",
  "prod.col.variants": "Variants",
  "prod.col.mapping": "SKUs",
  "prod.variants": "{n} variants",
  "prod.notInStores": "Not in a store yet",
  "prod.imported": "{n} products imported.",
  "prod.importN": "Import {n}",
  "prod.importHelp": "Store listings become products in Zerpid, with their pictures, variants, category and attributes. A variant takes the SKU it is mapped to, or the SKU with its seller SKU as code (created if missing). When every variant is already a variant of another product (the same goods on another store), the listing joins that product.",
  "prod.importDone": "{ok} imported, {failed} failed.",
  "prod.importEmpty": "Every listing of this store is already a product.",
  "prod.unmappedN": "{n} not mapped",
  "prod.allMapped": "All mapped",
  "prod.status.not_published": "Not published",
  "prod.status.not_set": "Not set up",
  "prod.status.draft": "Draft",
  "prod.status.in_review": "In review",
  "prod.status.live": "Live",
  "prod.status.rejected": "Rejected",
  "prod.status.inactive": "Inactive",
  "prod.status.deleted": "Deleted",
  "prod.status.failed": "Push failed",
  "prod.saved": "Product saved.",
  "prod.info": "Product",
  "prod.name": "Title",
  "prod.nameHelp": "As buyers see it. Marketplaces prefer 25 characters or more: brand, type, key feature.",
  "prod.description": "Description",
  "prod.descriptionHelp": "Each line becomes a paragraph.",
  "prod.brand": "Brand",
  "prod.price": "Price",
  "prod.priceHelp": "For every variant and store, unless set lower down.",
  "prod.variantsTitle": "Variants",
  "prod.sku": "SKU",
  "prod.variantPrice": "Price",
  "prod.samePrice": "Product price",
  "prod.variantImage": "Picture",
  "prod.imageN": "Photo {n}",
  "prod.shipping": "Package",
  "prod.weight": "Weight",
  "prod.lengthCm": "Length",
  "prod.widthCm": "Width",
  "prod.heightCm": "Height",
  "prod.photos": "Photos",
  "prod.saveFirst": "Save the product first.",
  "prod.stores": "Stores",
  "prod.newSku": "New SKU",
  "prod.skuCode": "SKU code",
  "prod.skuCodeHelp": "Sent to the marketplace as the seller SKU, so orders map to it.",
  "prod.skuName": "SKU name",
  "prod.uploadPhoto": "Upload",
  "prod.noPhotos": "No photos yet",
  "prod.cover": "Cover",
  "prod.photosHelp": "Up to nine, JPEG or PNG, square and 600×600 or larger. The first is the cover.",
  "prod.noStores": "No TikTok Shop or Tokopedia store connected.",
  "prod.storesHelp": "Publishing to TikTok Shop also lists the product on Tokopedia. Shopee and Lazada follow.",
  "prod.variant": "Variant",
  "prod.basePrice": "Price",
  "prod.noPrice": "No price",
  "prod.store.setup": "Set up & publish",
  "prod.store.manage": "Manage",
  "prod.store.saved": "Store terms saved.",
  "prod.store.published": "Sent to the store.",
  "prod.store.publish": "Publish",
  "prod.store.update": "Update in store",
  "prod.store.status": "Status",
  "prod.store.check": "Check status",
  "prod.store.externalId": "Product id in store",
  "prod.store.pushed": "Last sent",
  "prod.store.inReview": "The marketplace reviews new products and changes, usually within a day. Check the status here.",
  "prod.store.rejected": "The marketplace refused it",
  "prod.ai.write": "Write with AI",
  "prod.ai.fix": "Fix with AI",
  "prod.ai.title": "Write with AI",
  "prod.ai.store": "For store",
  "prod.ai.storeHelp": "Uses the store's category and attributes, and its name length limit.",
  "prod.ai.anyStore": "Any marketplace",
  "prod.ai.notes": "What should it say?",
  "prod.ai.notesHelp": "Selling points, materials, who it is for, keywords. The AI only uses facts you give.",
  "prod.ai.fixNote": "The draft answers what the marketplace asked to correct.",
  "prod.ai.generate": "Write draft",
  "prod.ai.again": "Write again",
  "prod.ai.use": "Use this draft",
  "prod.ai.used": "Draft placed in the form. Check it, then save the product.",
  "prod.ai.placeholders": "Fill in every [LENGKAPI: ...] mark before saving: the AI did not have that information.",
  "prod.store.lastError": "The last push was refused",
  "prod.store.warnings": "Warnings from the marketplace",
  "prod.store.category": "Category",
  "prod.store.categoryHelp": "Products go in the last level. Let the marketplace suggest one from the title.",
  "prod.store.pickCategory": "Search or pick a category",
  "prod.store.recommend": "Suggest",
  "prod.store.brand": "Brand",
  "prod.store.brandHelp": "A brand the marketplace knows; leave empty for no brand.",
  "prod.store.noBrand": "No brand",
  "prod.store.attributes": "Attributes",
  "prod.store.attrRequired": "Required by the marketplace",
  "prod.store.attrCustom": "Pick, or type your own",
  "prod.store.salesAttrs": "Variants are listed by {names} where the names match your variant names.",
  "prod.store.sizeChart": "Size chart",
  "prod.store.sizeChartHelp": "A picture of the size chart.",
  "prod.store.uploadChart": "Upload size chart",
  "prod.store.replaceChart": "Replace",
  "prod.store.cod": "Cash on delivery",
  "prod.store.prices": "Prices in this store",
  "prod.store.pricesHelp": "Empty: the product or variant price. Later price changes can also go through Products & Prices.",
  "prod.store.price": "Store price",
  "prod.store.samePrice": "Same",
  "prod.missing.title": "What the store still needs",
  "prod.missing.categoryId": "A category (last level)",
  "prod.missing.images": "At least one photo",
  "prod.missing.weightG": "The package weight",
  "prod.missing.dimensions": "The package dimensions",
  "prod.missing.sizeChartId": "A size chart",
  "prod.missing.variants": "At least one variant",
  "prod.missing.attribute": "Attribute {name}",
  "prod.missing.variant.price": "A price for {sku}",
  "prod.missing.variant.sku": "{sku} is archived",
  "prod.discardTitle": "Discard changes?",
  "prod.discardText": "The changes on this page are not saved.",
  "prod.discard": "Discard",
  "prod.keepEditing": "Keep editing",
  "prod.var.groupN": "Variation {n}",
  "prod.var.namePlaceholder1": "e.g. Size",
  "prod.var.namePlaceholder2": "e.g. Colour",
  "prod.var.values": "Options (type, then Enter)",
  "prod.var.valuesPlaceholder1": "e.g. S, M, L, XL",
  "prod.var.valuesPlaceholder2": "e.g. Black, White",
  "prod.var.addGroup": "Add variation",
  "prod.var.noGroups": "No variation: the product is one SKU. Add a variation (size, colour) to sell more.",
  "prod.var.bulkPrice": "Price for all",
  "prod.var.applyAll": "Apply to all",
  "prod.var.autoSku": "Create SKUs ({n})",
  "prod.var.autoTitle": "Create SKUs for the combinations",
  "prod.var.autoHelp": "Each combination without an SKU gets one: the prefix and its options. An existing code is reused.",
  "prod.var.prefix": "Code prefix",
  "prod.var.autoOk": "Create {n}",
  "prod.var.autoDone": "{n} SKUs created.",
  "prod.var.pickSku": "Pick an SKU",
  "prod.var.problem.name": "Give every variation a name (e.g. Size).",
  "prod.var.problem.sameName": "Two variations have the same name.",
  "prod.var.problem.values": "Every variation needs at least one option.",
  "prod.var.problem.sku": "Every combination needs an SKU: pick one, or create them.",
  "prod.var.problem.sameSku": "An SKU is used by two combinations.",
  "nav.stockSync": "Stock sync",
  "activity.stock_sync.updated": "Changed the stock sync settings",
  "activity.stock_sync.pushed": "Pushed stock to the stores now",
  "activity.stock_sync.rule_saved": "Set an SKU sync rule",
  "activity.stock_sync.rule_removed": "Removed an SKU sync rule",
  "activity.stock_sync.campaign_created": "Locked stock for a campaign",
  "activity.stock_sync.campaign_cancelled": "Cancelled a campaign lock",
  "sync.title": "Stock sync",
  "sync.subtitle": "What every store is told of the available stock. The warehouse stock does not change here.",
  "sync.tab.stores": "Stores",
  "sync.tab.rules": "SKU rules",
  "sync.tab.campaigns": "Campaigns",
  "sync.tab.check": "Check an SKU",
  "sync.pushAll": "Sync all now",
  "sync.pushStore": "Sync now",
  "sync.pushed": "{n} SKUs queued for the stores.",
  "sync.saved": "Saved; the stores are being updated.",
  "sync.mode.shared": "Shared",
  "sync.mode.split": "Split",
  "sync.modeHelp.shared": "Every store sees its percent of all the available stock (100% = everything). Orders take from one pool; a percent below 100 keeps a buffer against oversell.",
  "sync.modeHelp.split": "The available stock is divided between the stores of a warehouse, unevenly if you like, 100% at most in total. Safest against oversell; a store may run out while another still has stock.",
  "sync.overSplit": "The stores of a warehouse share more than 100%.",
  "sync.noStores": "No store connected.",
  "sync.col.percent": "Shown",
  "sync.col.share": "Share",
  "sync.col.warehouseTotal": "Warehouse total",
  "sync.evenly": "Split evenly",
  "sync.rule.add": "Add SKU rule",
  "sync.rule.help": "An SKU rule replaces the store percent for that SKU: for every store, or one store (which wins).",
  "sync.rule.empty": "No SKU rules: every SKU follows the store settings.",
  "sync.rule.col": "Rule",
  "sync.rule.allStores": "All stores",
  "sync.rule.storeHelp": "A rule for one store wins over a rule for all.",
  "sync.rule.mode.fixed": "Fixed number (virtual stock)",
  "sync.rule.mode.percent": "Own percent",
  "sync.rule.mode.off": "Do not sync (the store keeps its number)",
  "sync.rule.fixed": "Number shown",
  "sync.rule.percent": "Percent of available",
  "sync.rule.fixedWarn": "A fixed number does not follow the real stock: orders beyond it go out of stock (pre-order, made to order).",
  "sync.rule.offText": "Not synced",
  "sync.rule.fixedText": "Always {n}",
  "sync.rule.percentText": "{n}% of available",
  "sync.rule.remove": "Remove this rule?",
  "sync.rule.removeText": "{sku} follows the store settings again.",
  "sync.camp.help": "A campaign keeps units for one store over a period (flash sale, a marketplace campaign). The other stores see the stock without them; what the store sells meanwhile comes off the lock. It starts and ends on its own.",
  "sync.camp.add": "Lock stock for a campaign",
  "sync.camp.empty": "No campaigns.",
  "sync.camp.name": "Campaign",
  "sync.camp.namePlaceholder": "e.g. Flash sale 11.11",
  "sync.camp.period": "Period",
  "sync.camp.locked": "Locked",
  "sync.camp.lockedN": "{units} units · {skus} SKUs",
  "sync.camp.lines": "SKUs and units to keep",
  "sync.camp.qty": "Units",
  "sync.camp.addLine": "Add SKU",
  "sync.camp.lock": "Lock",
  "sync.camp.created": "Campaign saved.",
  "sync.camp.overTitle": "The campaign locks more than is available",
  "sync.camp.overLine": "{sku}: {locked} locked in the period, {available} available",
  "sync.camp.state.scheduled": "Scheduled",
  "sync.camp.state.active": "Running",
  "sync.camp.state.ended": "Ended",
  "sync.camp.state.cancelled": "Cancelled",
  "sync.camp.cancel": "End the lock",
  "sync.camp.cancelText": "The units go back to every store now.",
  "sync.camp.cancelled": "Lock ended.",
  "sync.camp.sold": "Sold",
  "sync.camp.remaining": "Still locked",
  "sync.camp.soldHelp": "Sold: the store's orders that reached Zerpid since the campaign began.",
  "sync.check.pick": "Pick an SKU",
  "sync.check.help": "Pick an SKU to see what every store is told and why.",
  "sync.check.none": "This SKU is not mapped to any store.",
  "sync.check.available": "Available",
  "sync.check.told": "Store sees",
  "sync.check.notSynced": "Not synced",
  "sync.check.why": "Why",
  "sync.check.lastPush": "Last sent",
  "sync.why.all": "All available",
  "sync.why.off": "SKU rule: not synced",
  "sync.why.fixed": "SKU rule: fixed number",
  "sync.why.rulePercent": "SKU rule: {n}%",
  "sync.why.share": "Store: {n}%",
  "sync.why.split": "Split: {n}%",
  "sync.why.elsewhere": "{n} locked for other stores' campaigns",
  "sync.why.locked": "{n} locked for this store's campaign",
  "activity.stock_sync.promotion_created": "Made a marketplace promotion",
  "sync.camp.manual": "Stock lock",
  "sync.promo.add": "Make a TikTok promotion",
  "sync.promo.create": "Make on TikTok",
  "sync.promo.created": "Promotion made on the marketplace.",
  "sync.promo.type": "Promotion",
  "sync.promo.type.flash_sale": "Flash sale",
  "sync.promo.type.discount": "Discount",
  "sync.promo.type.fixed_price": "Fixed price",
  "sync.promo.noLock": "Stock not locked",
  "sync.promo.lock": "Keep the quota for this store",
  "sync.promo.lockHelp": "Other stores do not see the quota while it runs: no oversell.",
  "sync.promo.lines": "SKUs in the promotion",
  "sync.promo.discount": "Discount",
  "sync.promo.price": "Promo price",
  "sync.promo.quota": "Quota",
  "sync.promo.perUser": "Per buyer",
  "sync.promo.offer": "Offer",
  "sync.promo.used": "Sold in promo",
  "sync.promo.noStore": "Connect a TikTok Shop or Tokopedia store first.",
  "sync.promo.help": "The SKU must be on sale in the store (mapped). Quota: how many may sell at the promotion price. Ending the campaign here ends the promotion on TikTok.",
  "nav.finance.cash": "Cash & bank",
  "logs.entity.cash": "Cash & bank",
  "activity.cash.account_created": "Opened a cash or bank account",
  "activity.cash.account_updated": "Changed a cash or bank account",
  "activity.cash.entry_created": "Recorded money in or out",
  "activity.cash.entry_voided": "Voided a cash entry",
  "activity.cash.transfer": "Transferred between accounts",
  "cash.title": "Cash & bank",
  "cash.moneyIn": "Money in",
  "cash.moneyOut": "Money out",
  "cash.transfer": "Transfer",
  "cash.addAccount": "New account",
  "cash.editAccount": "Edit account",
  "cash.totalBalance": "Balance of all accounts",
  "cash.inThisMonth": "In this month",
  "cash.outThisMonth": "Out this month",
  "cash.noAccounts": "No accounts yet. Add your bank accounts and cash with their opening balance.",
  "cash.account": "Account",
  "cash.closed": "Closed",
  "cash.in": "In (this month)",
  "cash.out": "Out (this month)",
  "cash.balance": "Balance",
  "cash.allAccounts": "All accounts",
  "cash.allKinds": "All kinds",
  "cash.category": "Category",
  "cash.search": "Number, counterparty, reference",
  "cash.number": "Number",
  "cash.date": "Date",
  "cash.kindLabel": "Kind",
  "cash.counterparty": "Counterparty",
  "cash.entry": "Entry",
  "cash.noEntries": "No entries.",
  "cash.void": "Void",
  "cash.voidText": "{number} stays in the history, struck out, and leaves the balance.",
  "cash.voidTransferText": "Both sides of transfer {number} are voided.",
  "cash.voided": "Entry voided.",
  "cash.kind.bank": "Bank",
  "cash.kind.cash": "Cash",
  "cash.kind.ewallet": "E-wallet",
  "cash.kind.marketplace": "Marketplace balance",
  "cash.entryKind.income": "Income",
  "cash.entryKind.expense": "Expense",
  "cash.entryKind.adjustment": "Adjustment",
  "cash.entryKind.transfer_in": "Transfer in",
  "cash.entryKind.transfer_out": "Transfer out",
  "cash.entryKind.supplier_payment": "Supplier payment",
  "cash.entryKind.receipt": "Marketplace receipt",
  "cash.accountName": "Name",
  "cash.accountNamePlaceholder": "e.g. BCA Operational",
  "cash.bankName": "Bank",
  "cash.provider": "Provider",
  "cash.accountNo": "Account number",
  "cash.openingBalance": "Opening balance",
  "cash.openingOn": "As of",
  "cash.openingHelp": "Entries start from this date.",
  "cash.active": "Open",
  "cash.accountSaved": "Account saved.",
  "cash.new.income": "Money in",
  "cash.new.expense": "Money out",
  "cash.new.adjustment": "Adjustment",
  "cash.adjustHelp": "Negative to lower the balance.",
  "cash.from": "From",
  "cash.to": "Paid to",
  "cash.entrySaved": "{number} recorded.",
  "cash.fromAccount": "From account",
  "cash.toAccount": "To account",
  "cash.sameAccount": "Pick another account.",
  "cash.transferSaved": "Transfer recorded.",
  "bill.paidFrom": "Paid from",
  "bill.paidFromHelp": "The payment becomes an entry of this account.",
  "bill.paidFromNone": "Not from an account in Zerpid",
  "bill.noCashAccounts": "Add your bank accounts in Cash & bank to track payments from them.",
  "nav.finance.receivables": "Marketplace receivables",
  "activity.cash.payout_account": "Set where a store's payouts go",
  "ar.title": "Marketplace receivables",
  "ar.subtitle": "What the marketplaces still owe for shipped orders, what they settled (less fees) and paid out to your bank.",
  "ar.owed": "Still owed (shipped, not settled)",
  "ar.settledMonth": "Settled this month (net)",
  "ar.feesMonth": "Marketplace fees this month ({pct}%)",
  "ar.noStores": "No store connected.",
  "ar.unsupported": "Settlements are not read from this marketplace yet",
  "ar.syncFailed": "Last sync failed",
  "ar.syncedAt": "Read {at}",
  "ar.neverSynced": "Not read yet (hourly)",
  "ar.owedCol": "Not settled",
  "ar.ordersN": "{n} orders",
  "ar.settledCol": "Settled this month",
  "ar.feeLine": "fees {fees} ({pct}%)",
  "ar.balance": "Balance",
  "ar.payoutTo": "Payouts to",
  "ar.payoutPick": "Pick a bank account",
  "ar.payoutSaved": "Saved; payouts are posted to that account.",
  "ar.sync": "Read now",
  "ar.synced": "Settlements read.",
  "ar.noBank": "Add your bank account in Cash & bank, then pick it here: payouts are posted to it.",
  "ar.tab.orders": "Orders",
  "ar.tab.statements": "Settlements",
  "ar.tab.payouts": "Payouts",
  "ar.state.unsettled": "Not settled",
  "ar.state.settled": "Settled",
  "ar.ordered": "Ordered",
  "ar.total": "Order total",
  "ar.revenue": "Revenue",
  "ar.fees": "Fees",
  "ar.other": "Shipping & adj.",
  "ar.adjustment": "Adjustment",
  "ar.net": "Net",
  "ar.estimatedNet": "Estimated to settle {amount} ({n} orders, per the marketplace)",
  "ar.estimateNet": "Estimated net",
  "ar.estimateRelease": "Estimated release",
  "ar.estimateHelp": "The marketplace's estimate, not final",
  "ar.fee.platform_commission": "Platform commission",
  "ar.fee.transaction_fee": "Transaction fee",
  "ar.fee.affiliate_commission": "Affiliate commission",
  "ar.fee.referral_fee": "Referral fee",
  "ar.fee.sfp_service_fee": "Free shipping service fee",
  "ar.fee.voucher_xtra_service_fee": "Voucher Xtra service fee",
  "ar.fee.flash_sales_service_fee": "Flash sale service fee",
  "ar.fee.live_specials_fee": "LIVE Specials fee",
  "ar.fee.vat": "VAT",
  "ar.settledAt": "Settled",
  "ar.noOrders": "No orders.",
  "ar.noStatements": "No settlements read yet.",
  "ar.statement": "Settlement",
  "ar.linesN": "{n} lines",
  "ar.unmatchedN": "{n} orders not in Zerpid",
  "ar.noPayouts": "No payouts yet.",
  "ar.payout": "Payout",
  "ar.posted": "Cash & bank",
  "ar.postedYes": "Posted",
  "ar.postedWaiting": "Waiting for the bank account",
  "wms.wave.cancelAsked": "Buyer asked to cancel: set aside",
  "orders.note.cancel_request_auto_rejected": "Cancellation request rejected automatically (already packed)",
  "activity.order.cancel_auto_rejected": "Rejected a cancellation request automatically",
  "activity.order.settings_updated": "Changed the order settings",
  "orders.cancelSettings.title": "Buyer cancellation requests",
  "orders.cancelSettings.notPacked": "Not packed yet (in the pool, a wave, picking or packing): the order is held and waits for customer service to accept or reject the request.",
  "orders.cancelSettings.autoReject": "Reject automatically once packed",
  "orders.cancelSettings.autoRejectHelp": "Zerpid rejects the request through the marketplace with its reason for packed orders. If the marketplace refuses, the order is held for customer service.",
  "orders.cancelSettings.saved": "Saved.",
  "wms.zone.title": "Zones",
  "wms.zone.help": "Picking routes walk the zones in this order. A zone that a new location names is added at the end. With zone picking, each wave is cut into one task per zone; pickers given a zone take that zone's tasks.",
  "wms.zone.add": "Add zone",
  "wms.zone.edit": "Zone {code}",
  "wms.zone.empty": "No zones yet. Zones come from the locations you create (A-01-01-01 is in zone A).",
  "wms.zone.walk": "Order",
  "wms.zone.zone": "Zone",
  "wms.zone.code": "Code",
  "wms.zone.name": "Name",
  "wms.zone.namePlaceholder": "e.g. Fast movers, Mezzanine",
  "wms.zone.kind": "Kind",
  "wms.zone.kind.general": "General",
  "wms.zone.kind.fast_moving": "Fast moving",
  "wms.zone.kind.bulk": "Bulk",
  "wms.zone.kind.high_value": "High value",
  "wms.zone.kind.cold": "Cold",
  "wms.zone.locations": "Locations",
  "wms.zone.pickers": "Pickers",
  "wms.zone.pickersHelp": "They take this zone's picking tasks (and tasks not cut by zone). Leave empty: anyone.",
  "wms.zone.anyPicker": "Anyone",
  "wms.zone.up": "Earlier on the walk",
  "wms.zone.down": "Later on the walk",
  "wms.zone.inUse": "Locations still sit in this zone.",
  "wms.zone.deleteConfirm": "Delete zone {code}?",
  "wms.zone.saved": "Zone saved.",
  "wms.zone.planNote": "Zone order and kinds work on every plan. Cutting waves per zone and giving pickers zones is available from the Scale plan.",
  "wms.zone.tag": "Zone {code}",
  "wms.task.stops": "Stops",
  "wms.pack.waitingZones": "Waiting for zone {zones}: those units are not picked yet.",
  "wms.pool.zones": "Zones",
  "wms.pool.zonesHelp": "Orders whose stock sits in these zones.",
  "wms.pool.singleZone": "Only orders one zone can fill",
  "wms.pool.singleZoneHelp": "Every item is on one zone's shelves: picked by one picker, no joining at packing.",
  "wms.pool.rule.singleZone": "One zone",
  "wms.pool.rule.singleZoneOf": "One zone of {zones}",
  "wms.pool.rule.zones": "Zones {zones}",
  "settings.warehouses.pickByZone": "Pick per zone",
  "settings.warehouses.pickByZoneHelp": "Each wave is cut into one picking task per zone, worked side by side; the packing desk joins an order's units again.",
  "settings.warehouses.pickByZonePlan": "Available from the Scale plan.",
  "activity.warehouse.zone_created": "Added a zone",
  "activity.warehouse.zone_updated": "Changed a zone",
  "activity.warehouse.zone_deleted": "Deleted a zone",
  "activity.warehouse.zones_ordered": "Changed the walking order of the zones",
  "wms.zone.none": "No zone",
  "wms.loc.allZones": "All zones",
  "wms.loc.zoneHelp": "Pick a zone of this warehouse or type a new code; it decides the picking route.",
  "wms.loc.zoneAssign": "Set zone",
  "wms.loc.zoneSelected": "Set zone ({n})",
  "wms.loc.zoneAssignBody": "The location code stays as printed on its label; only the zone changes. Tasks already released keep their zone.",
  "wms.loc.zoneScopeSelected": "The {n} ticked locations",
  "wms.loc.zoneScopePrefix": "Every location whose code starts with…",
  "wms.loc.codePrefix": "Code starts with",
  "wms.loc.codePrefixHelp": "e.g. A-01 for every bin of rack A-01, or A for all of A.",
  "wms.loc.zoneTarget": "Zone",
  "wms.loc.zoneTargetHelp": "Pick a zone or type a new code; leave empty to take them out of any zone.",
  "wms.loc.zoneMoved": "{n} locations moved.",
  "inventory.locationSearch": "Location or zone (A-01)",
  "inventory.atLocation": "At location",
  "activity.warehouse.locations_zoned": "Put locations in a zone",
  "nav.procurement.returns": "Returns to supplier",
  "nav.finance.credits": "Supplier credit notes",
  "logs.entity.vendor_return": "Return to supplier",
  "logs.entity.supplier_credit": "Supplier credit note",
  "vr.title": "Returns to supplier",
  "vr.subtitle": "Goods sent back to a supplier: damaged, wrong, expired or excess. The supplier settles with money (a credit note) or replacement goods.",
  "vr.add": "New return",
  "vr.search": "Search number or supplier",
  "vr.filter.open": "In progress",
  "vr.number": "Number",
  "vr.empty": "No returns to supplier.",
  "vr.status.draft": "Draft",
  "vr.status.ready": "Ready to ship",
  "vr.status.shipped": "Shipped, to settle",
  "vr.status.closed": "Closed",
  "vr.status.cancelled": "Cancelled",
  "vr.reason": "Reason",
  "vr.reason.damaged": "Damaged / defective",
  "vr.reason.wrong_item": "Wrong item",
  "vr.reason.expired": "Expired",
  "vr.reason.quality": "Quality issue",
  "vr.reason.excess": "Excess delivery",
  "vr.reason.other": "Other",
  "vr.settlement": "Settlement",
  "vr.settlement.credit": "Credit note (money)",
  "vr.settlement.replace": "Replacement goods",
  "vr.shippedAt": "Shipped",
  "vr.fromWarehouse": "From warehouse",
  "vr.conditionHelp": "Which stock the units are taken from: damaged, quarantine, or sellable (then they stop being sold once ready).",
  "vr.unitPriceHelp": "What the supplier credits per unit. Empty: the supplier's price, else the average cost.",
  "vr.unitPriceAuto": "Auto",
  "vr.fromLocation": "Take from",
  "vr.fromLocationHelp": "The location to take the units from first; otherwise shelves and the damaged and quarantine areas.",
  "vr.total": "Total credited",
  "vr.makeReady": "Save and get ready",
  "vr.readyOk": "Return ready: its stock is set aside.",
  "vr.readyHelp": "Getting it ready sets the stock aside: sellable units stop being sold on the marketplaces. Then the warehouse gathers the goods and ships them.",
  "vr.readyNote": "The stock of this return is set aside in {warehouse}. Gather the goods, then ship.",
  "vr.cancel": "Cancel return",
  "vr.cancelConfirm": "Cancel {number}? Set-aside stock is for sale again.",
  "vr.unready": "Back to draft",
  "vr.ship": "Ship to supplier",
  "vr.shipTitle": "Ship {number}",
  "vr.shipHelp": "The units leave the warehouse stock now.",
  "vr.shipHelpWms": "The units leave their bins now: damaged ones from the damaged area, sellable ones from the shelves (or the chosen location).",
  "vr.shipNotePlaceholder": "Courier, tracking number, or who picked it up",
  "vr.shipNote": "Shipping",
  "vr.serialsFor": "Serial numbers of {code} ({n} units), separated by spaces or new lines",
  "vr.shipped": "Return shipped.",
  "vr.credit": "Credit note",
  "vr.creditNote": "Credit note {number}: {left} still to come from the supplier (as a bill payment or a refund).",
  "vr.openCredit": "Open credit note",
  "vr.replaceOwed": "{n} units of replacement still to come.",
  "vr.openReceipt": "Receive replacement (scan)",
  "vr.receive": "Receive replacement",
  "vr.receiveHelp": "The replacement goes into {warehouse}'s stock at the average cost.",
  "vr.owed": "Owed",
  "vr.replaced": "Replaced",
  "vr.receipts": "Replacement receipts",
  "vr.close": "Close return",
  "vr.closeConfirm": "Close {number}? Use it when the supplier will not settle the rest. A credit note left stays usable.",
  "supplier.method.credit": "Credit note",
  "bill.creditNote": "Credit note",
  "bill.creditNoteHelp": "Pays the bill from what the supplier owes back for returned goods; no money moves.",
  "bill.creditsOpen": "Suppliers still owe {amount} on {n} credit notes from returns.",
  "bill.creditsOpenView": "View credit notes",
  "cash.entryKind.supplier_refund": "Supplier refund",
  "credit.title": "Supplier credit notes",
  "credit.subtitle": "What suppliers owe back for goods returned to them: use it on their bills, or record the money they paid back.",
  "credit.status.open": "Open",
  "credit.status.used": "Used up",
  "credit.status.void": "Void",
  "credit.remainingTotal": "Still owed:",
  "credit.empty": "No credit notes.",
  "credit.number": "Number",
  "credit.return": "Return",
  "credit.issuedOn": "Issued",
  "credit.amount": "Amount",
  "credit.used": "Used",
  "credit.remaining": "Left",
  "credit.useHelp": "Use it on a bill from the bill's page (payment method: credit note), or record a refund below.",
  "credit.noUses": "Not used yet.",
  "credit.use": "Used for",
  "credit.use.bill": "Bill",
  "credit.use.refund": "Refund",
  "credit.on": "Date",
  "credit.voidRefund": "Void",
  "credit.voidRefundConfirm": "Void this refund? Its cash entry is voided too.",
  "credit.refundTitle": "Record a refund",
  "credit.into": "Received into",
  "credit.receivedOn": "Received on",
  "credit.recordRefund": "Record refund",
  "credit.refunded": "Refund recorded.",
  "activity.vendor_return.created": "Created a return to supplier",
  "activity.vendor_return.updated": "Changed a return to supplier",
  "activity.vendor_return.ready": "Got a return to supplier ready",
  "activity.vendor_return.unready": "Put a return to supplier back to draft",
  "activity.vendor_return.cancelled": "Cancelled a return to supplier",
  "activity.vendor_return.shipped": "Shipped a return to supplier",
  "activity.vendor_return.closed": "Closed a return to supplier",
  "activity.vendor_return.receipt_opened": "Opened a receipt for replacement goods",
  "activity.vendor_return.replaced": "Received replacement goods",
  "activity.supplier_credit.refunded": "Recorded a supplier refund",
  "activity.supplier_credit.refund_voided": "Voided a supplier refund",
  "nav.finance.reports": "Reports",
  "report.title": "Finance reports",
  "report.profit": "Gross profit",
  "report.cash": "Cash flow",
  "report.aging": "Payables & receivables aging",
  "report.by.store": "Store",
  "report.by.platform": "Marketplace",
  "report.by.sku": "SKU",
  "report.by.month": "Month",
  "report.profitHelp": "Counted from goods that left the warehouse in the period: the order line's price, the cost of goods fixed when they left, and the marketplace fees of orders whose settlement has been read. Cancelled and returned orders are left out.",
  "report.revenue": "Revenue",
  "report.cost": "Cost of goods",
  "report.grossProfit": "Gross profit",
  "report.margin": "Margin {pct}",
  "report.marginCol": "Margin",
  "report.fees": "Marketplace fees",
  "report.feesLine": "Marketplace fees {fees}",
  "report.afterFees": "After fees",
  "report.orders": "Orders",
  "report.units": "Units",
  "report.unmapped": "Not mapped to an SKU",
  "report.noCost": "{n} units left before their SKU had a cost: counted at 0. Set the costs in Cost of goods.",
  "report.profitEmpty": "Nothing shipped in this period.",
  "report.cashHelp": "Money in and out of every account. Moves between your own accounts (marketplace payouts to the bank, topping up cash) are not cash flow.",
  "report.cashHelpAccount": "Money in and out of this account, transfers included.",
  "report.opening": "Opening balance",
  "report.started": "incl. {amount} from accounts started in the period",
  "report.moneyIn": "Money in",
  "report.moneyOut": "Money out",
  "report.closing": "Closing balance",
  "report.net": "Net {amount}",
  "report.netCol": "Net",
  "report.byCategory": "By kind and category",
  "report.byMonth": "Per month",
  "report.byAccount": "Per account",
  "report.month": "Month",
  "report.cashEmpty": "No money moved in this period.",
  "report.apTotal": "Payables (to suppliers)",
  "report.apOverdue": "Overdue {amount}",
  "report.arTotal": "Receivables (marketplaces)",
  "report.arHelp": "Shipped orders not settled yet",
  "report.credits": "Supplier credit notes",
  "report.creditsHelp": "{n} notes from returns still owed",
  "report.apAging": "Payables by days past due",
  "report.apEmpty": "No unpaid bills.",
  "report.arAging": "Receivables by days since the order",
  "report.arEmpty": "Nothing owed by the marketplaces.",
  "report.ar.d7": "0-7 days",
  "report.ar.d14": "8-14 days",
  "report.ar.d30": "15-30 days",
  "report.ar.older": "Over 30 days",
  "report.total": "Total",
  "report.balance": "Store balance",
  "platform.manual": "Manual",
  "channels.manual.add": "Add manual channel",
  "channels.manual.added": "Channel added.",
  "channels.manual.help": "For sales outside the marketplaces: your website, Instagram, WhatsApp, a shop or B2B. Their orders are written in Zerpid and go through the same warehouse flow.",
  "channels.manual.namePlaceholder": "e.g. Website, Instagram, WhatsApp",
  "channels.manual.drawerHelp": "A manual channel has no marketplace behind it: write its orders from Orders > New manual order. Its stock is not pushed anywhere.",
  "manual.add": "New manual order",
  "manual.title": "New manual order",
  "manual.save": "Create order",
  "manual.noChannel": "Add a manual channel first (Stores > Add manual channel), e.g. Website or Instagram.",
  "manual.created": "Order {number} created.",
  "manual.createdUnpaid": "Order {number} created; its stock is kept and it waits for payment.",
  "manual.buyer": "Buyer and address",
  "manual.channel": "Sales channel",
  "manual.buyerName": "Buyer",
  "manual.recipientName": "Recipient",
  "manual.recipientHelp": "Empty: the buyer.",
  "manual.phone": "Phone",
  "manual.address": "Address",
  "manual.city": "City",
  "manual.postalCode": "Postal code",
  "manual.items": "Items",
  "manual.price": "Price",
  "manual.payment": "Payment",
  "manual.shippingFee": "Shipping fee",
  "manual.discount": "Discount",
  "manual.total": "Total",
  "manual.paymentStatus": "Payment status",
  "manual.pay.unpaid": "Unpaid",
  "manual.pay.paid": "Paid",
  "manual.pay.cod": "COD",
  "manual.unpaidHelp": "The stock is kept for the buyer; the order waits and is processed once you mark it paid.",
  "manual.paidInto": "Received into",
  "manual.paidIntoHelp": "Books the payment into Cash & bank (cash flow). Empty: not booked.",
  "manual.paidOn": "Paid on",
  "manual.shipping": "Shipping",
  "manual.carrier": "Courier",
  "manual.trackingNo": "Tracking number",
  "manual.trackingHelp": "Can be filled later; needed to print the label.",
  "manual.panel": "Manual order",
  "manual.noTracking": "Not filled in yet",
  "manual.markPaid": "Mark paid",
  "manual.markUnpaid": "Mark unpaid",
  "manual.payBody": "The buyer paid {amount}.",
  "manual.setTracking": "Fill in tracking number",
  "manual.editTracking": "Change tracking number",
  "manual.markShipped": "Mark shipped",
  "manual.markCompleted": "Mark completed",
  "manual.saved": "Saved.",
  "orders.hold.unpaid": "Waiting for payment",
  "orders.holdHelp.unpaid": "A manual order not paid yet. Its stock is kept; it is processed once marked paid.",
  "orders.note.manual_created": "Manual order created",
  "orders.note.payment": "Payment",
  "orders.note.tracking_set": "Tracking number set",
  "orders.note.manual_shipped": "Marked shipped",
  "orders.note.manual_completed": "Marked completed",
  "orders.reason.paid": "Paid",
  "orders.reason.unpaid": "Unpaid",
  "orders.reason.cod": "COD",
  "error.manual_tracking_required": "Fill in the tracking number of this manual order first.",
  "error.not_manual_order": "Only a manual order can do that.",
  "error.order_not_processed": "The order is not processed yet (unpaid, out of stock or held).",
  "error.manual_channel": "A manual channel has no marketplace to sync with.",
  "activity.order.manual_created": "Created a manual order",
  "activity.order.payment": "Changed a manual order's payment",
  "activity.order.tracking_set": "Set a manual order's tracking number",
  "activity.channel.manual_created": "Added a manual channel",
  "activity.channel.manual_renamed": "Renamed a manual channel",
  "wms.pack.trackingTitle": "Tracking number of {number}",
  "wms.agent.agent_unreachable": "The recorder agent is not running on this laptop.",
  "wms.agent.agent_not_paired": "The recorder agent is not paired with Zerpid yet.",
  "wms.agent.agent_other_warehouse": "This laptop's agent is paired with another warehouse.",
  "wms.agent.agent_no_camera": "The recorder agent has no camera chosen.",
  "wms.agent.agent_camera": "The recorder agent cannot open the camera.",
  "wms.agent.fixHelp": "Packing still works, but parcels are not recorded. Open the agent's settings, or install it on this laptop.",
  "wms.agent.openSetup": "Agent settings",
  "wms.agent.download": "Download agent",
  "wms.agent.on": "Recorder {name} · {camera}",
  "wms.agent.keptHere": "Videos are kept on this laptop.",
  "wms.agent.preview": "Camera preview",
  "wms.agent.onLaptop": "Kept in the warehouse (packing desk or hub): it plays on a PC there with the Zerpid agent; elsewhere, ask for it to come up.",
  "wms.agent.requestUpload": "Ask the laptop to send it",
  "wms.agent.requested": "Asked. The laptop sends it within a minute when it is on.",
  "wms.agent.waitingUpload": "Waiting for the warehouse PC to send it.",
  "settings.warehouses.recorder": "Recorder",
  "settings.warehouses.recorder.browser": "In the browser (uploaded to Zerpid)",
  "settings.warehouses.recorder.agent": "Recorder agent on the packing laptop (kept on the laptop)",
  "settings.warehouses.recorderHelp": "The agent records a webcam or CCTV camera and keeps the videos on the laptop's own disk; only those asked for come up to Zerpid.",
  "settings.warehouses.recorderPlan": "The recorder agent is available from the Scale plan.",
  "rec.manage": "Recorder devices",
  "rec.title": "Recorder devices · {name}",
  "rec.help": "Packing laptops running the Zerpid recorder agent. Each is paired once with a code; remove one here and its token stops working.",
  "rec.install": "Install the agent on the packing laptop",
  "rec.installSteps": "Download the zip on that laptop, extract it, double-click Install.cmd (no admin needed). Then click Add device here and type the code into the agent's page.",
  "rec.download": "Download agent {version}",
  "rec.add": "Add device",
  "rec.empty": "No recorder paired yet.",
  "rec.pending": "Code waiting to be used: {names}",
  "rec.device": "Device",
  "rec.online": "Online",
  "rec.offline": "Offline",
  "rec.revoked": "Removed",
  "rec.noCamera": "No camera chosen",
  "rec.lastSeen": "Last seen",
  "rec.clips": "Videos",
  "rec.clipsValue": "{n} videos · {free} free",
  "rec.uploadsWaiting": "{n} asked for, not sent yet",
  "rec.revoke": "Remove",
  "rec.hub": "Hub",
  "rec.hubHelp": "Hub: one PC on the warehouse network that keeps every desk's videos. Desks hand their videos to it; any PC with the agent (a desk, the manager's) plays them over the network. Videos come up to Zerpid only for an aftersales case or when asked for.",
  "rec.makeHub": "Make hub",
  "rec.makeHubConfirm": "Make {name} the warehouse's hub? Desks send their videos to it from their next check-in; keep this PC on, with enough disk.",
  "rec.unhub": "Stop being hub",
  "rec.unhubConfirm": "Stop {name} being the hub? Desks keep their new videos again; videos already on it stay there.",
  "rec.hubAddrs": "On the network: {addrs}",
  "rec.hubWaiting": "Waiting for the hub to report its network address",
  "rec.revokeConfirm": "Remove {name}? It stops recording for Zerpid; its videos stay on the laptop.",
  "rec.deviceName": "Device name",
  "rec.deviceNameHelp": "Which desk it records, as packers know it.",
  "rec.deviceNamePlaceholder": "e.g. Packing desk 1",
  "rec.makeCode": "Make code",
  "rec.codeTitle": "Pairing code · {name}",
  "rec.codeExpires": "Valid until {at}, once.",
  "rec.codeSteps": "On the packing laptop, open Zerpid Agen Perekam (Start menu), type this code and pick the camera.",
  "activity.warehouse.recorder_pairing": "Made a recorder pairing code",
  "activity.warehouse.recorder_paired": "Paired a recorder",
  "activity.warehouse.recorder_revoked": "Removed a recorder",
  "error.video_on_device": "The video is kept in the warehouse (packing desk or hub). Play it on a PC there with the Zerpid agent, or ask for it to be sent.",
  "error.clip_not_on_device": "This video is no longer on that recorder.",
  "error.no_hub": "This warehouse has no recorder hub.",
  "nav.section.sales": "Sales · OMS",
  "nav.section.warehouse": "Warehouse · WMS",
  "nav.section.purchasing": "Purchasing",
  "nav.section.finance": "Finance",
  "nav.section.system": "System",
  "nav.group.products": "Products",
  "nav.group.stock": "Stock",
  "nav.group.inbound": "Inbound",
  "nav.group.outbound": "Outbound",
  "nav.group.whStock": "Warehouse inventory",
  "nav.warehouse.stock": "Stock by location",
  "nav.favorites": "Favorites",
  "nav.favorite": "Add to favorites",
  "nav.unfavorite": "Remove from favorites",
  "nav.search": "Search menu",
  "nav.noMatch": "No menu matches.",
  "nav.collapse": "Collapse",
  "nav.expand": "Expand menu",
  "inventory.view.sku": "All warehouses (total)",
  "inventory.view.warehouse": "Per warehouse",
  "inventory.warehouses": "Warehouses",
  "bins.title": "Stock by location",
  "bins.subtitle": "{name}: every bin with its lot, condition and receipt date.",
  "bins.search": "Search SKU, barcode or lot number",
  "bins.allConditions": "All conditions",
  "bins.allKinds": "All location kinds",
  "bins.olderThan": "Received over {n} days ago",
  "bins.anyAge": "Any stock age",
  "bins.anyExpiry": "Any expiry",
  "bins.expired": "Expired (today or before)",
  "bins.expiringIn": "Expiring within {n} days",
  "bins.sort.location": "Walking order",
  "bins.sort.received": "Oldest received first",
  "bins.sort.expiry": "Expiring first",
  "bins.free": "Free",
  "bins.receivedAt": "Received",
  "bins.receivedHelp": "When the oldest units in this bin came into the warehouse. Kept when they move (putaway, moves, picking).",
  "bins.age": "Age",
  "bins.days": "{n} d",
  "bins.empty": "No stock in matching locations.",
  "nav.storage": "Cloud storage",
  "sub.kind.storage_mb": "Cloud storage",
  "storage.title": "Cloud storage",
  "storage.help": "Files Zerpid keeps for this workspace: uploaded packing videos, return unboxing videos, complaint evidence photos and product images. Videos the recorder agent keeps on packing laptops are not counted.",
  "storage.used": "Used",
  "storage.of": "of {limit}",
  "storage.unlimited": "No limit on your plan",
  "storage.full": "Storage is full: new packing videos and product images cannot be uploaded. Shorten video retention, record with the agent, or contact the Zerpid team.",
  "storage.warn": "Storage is nearly full ({pct}%).",
  "storage.byKind": "By kind",
  "storage.kind": "Kind",
  "storage.kind.packing_video": "Packing videos",
  "storage.kind.return_video": "Return unboxing videos",
  "storage.kind.evidence_photo": "Complaint evidence photos",
  "storage.kind.product_image": "Product images",
  "storage.files": "Files",
  "storage.size": "Size",
  "storage.share": "Share",
  "storage.byWarehouse": "Packing videos per warehouse",
  "storage.settings": "Set retention",
  "storage.recorder": "Recorder",
  "storage.recorder.browser": "Browser (uploaded)",
  "storage.recorder.agent": "Agent (on the laptop)",
  "storage.recorder.off": "Not recording",
  "storage.retention": "Retention",
  "storage.retentionDays": "{n} days",
  "storage.cloud": "In the cloud",
  "storage.local": "On packing laptops",
  "storage.sizeFiles": "{size} · {n} files",
  "storage.localNote": "On packing laptops: {size} ({n} videos), not counted. Each laptop's free disk shows under Recorder devices.",
  "storage.agentTip": "Warehouses recording in the browser upload every video to the cloud. The recorder agent (Scale plan) keeps them on the packing laptop and uploads only those asked for.",
  "pda.manage": "Android devices",
  "pda.title": "Android devices · {name}",
  "pda.help": "PDAs and phones running the Zerpid warehouse app here. Each is paired once with a code; staff then sign in on it with their ID and PIN (User & Role). Remove one here and whoever is signed in on it is signed out.",
  "pda.noWms": "Turn on WMS for this warehouse to use the warehouse app.",
  "pda.install": "Install the app on the PDA",
  "pda.installSteps": "Open this page on the PDA (or copy the APK to it), install it (allow installing from this source once), then click Add device here and type the code into the app.",
  "pda.download": "Download app {version}",
  "pda.add": "Add device",
  "pda.empty": "No device paired yet.",
  "pda.lastUser": "Last signed in",
  "pda.revokeConfirm": "Remove {name}? Its app stops working until paired again, and whoever is signed in is signed out.",
  "pda.nameHelp": "How the warehouse knows it, e.g. the number on its sticker.",
  "pda.namePlaceholder": "e.g. PDA 1",
  "pda.codeSteps": "Open Zerpid Gudang on the PDA, type this code, then staff sign in with their ID and PIN.",
  "pin.column": "App ID",
  "pin.set": "Set ID & PIN",
  "pin.title": "ID & PIN · {name}",
  "pin.help": "For the warehouse app: signing in on a PDA paired to a warehouse this member works in.",
  "pin.id": "Sign-in ID",
  "pin.idHelp": "3-32 small letters, numbers, dot, dash or underscore, e.g. budi or budi.g",
  "pin.idFormat": "Use 3-32 small letters, numbers, . _ or -",
  "pin.pin": "PIN",
  "pin.newPin": "New PIN",
  "pin.pinHelp": "6 digits, not all the same nor in a row (not 111111 or 123456). Five wrong tries lock it for 15 minutes.",
  "pin.pinFormat": "6 digits",
  "pin.saved": "ID & PIN saved for {name}.",
  "pin.clear": "Remove PIN",
  "pin.clearConfirm": "{name} will no longer sign in on PDAs.",
  "pin.noEmail": "Warehouse app only (ID & PIN)",
  "pin.staffAdd": "Add warehouse staff (ID & PIN)",
  "pin.staffTitle": "Warehouse staff without email",
  "pin.staffHelp": "For staff who only work on PDAs: no email, no web sign-in. They count as a user of your plan.",
  "pin.staffAdded": "{name} added. They can sign in on the PDA now.",
  "pin.warehousesHelp": "Empty: every warehouse.",
  "error.login_id_taken": "Another member already signs in with this ID.",
  "activity.warehouse.pda_pairing": "Made a PDA pairing code",
  "activity.warehouse.pda_paired": "Paired a PDA",
  "activity.warehouse.pda_revoked": "Removed a PDA",
  "activity.member.pin_set": "Set an app ID & PIN",
  "activity.member.pin_cleared": "Removed an app PIN",
  "activity.member.staff_created": "Added warehouse staff (ID & PIN)",
  "wms.pack.scanItemFirst": "Scan the item first, then its packaging.",
  "wms.pack.item": "Item",
  "wms.pack.boxNow": "scan the packaging now to pack",
  "wms.pack.boxAfterItem": "scanned after the item",
  "nav.warehouse.performance": "Staff performance",
  "perf.title": "Staff performance",
  "perf.help": "What each member did in this warehouse over the period, from the tasks, parcels, handovers, receipts and counts they did. Click a member for their days.",
  "perf.helpSelf": "Your work in this warehouse over the period. Click the row for your days.",
  "perf.empty": "No work recorded in this period.",
  "perf.member": "Member",
  "perf.daysOf": "{name} per day",
  "perf.pickTasks": "Picking tasks",
  "perf.pickUnits": "Units picked",
  "perf.putawayTasks": "Putaway tasks",
  "perf.putawayUnits": "Units put away",
  "perf.perHourHelp": "Units per hour of task time, from start to finish of each task (breaks included).",
  "perf.packed": "Parcels packed",
  "perf.handed": "Parcels handed over",
  "perf.received": "Units received",
  "perf.counted": "Locations counted",
  "perf.short": "Short units",
  "perf.shortHelp": "Units found missing in their bin while picking.",
  "perf.pickPerHour": "Picked/hour",
  "perf.putawayPerHour": "Put away/hour",
  "an.staffLink": "Staff performance",
  "an.tab.sales": "Sales",
  "an.tab.fulfillment": "Order fulfilment",
  "an.revenue": "Revenue",
  "an.revenueHint": "Orders neither cancelled nor returned",
  "an.orders": "Orders",
  "an.units": "Units",
  "an.avgOrder": "Per order",
  "an.cancelled": "Cancelled",
  "an.returned": "Returned",
  "an.revenuePerDay": "Revenue per day",
  "an.ordersPerDay": "Orders per day",
  "an.chart": "Chart",
  "an.table": "Table",
  "an.empty": "No orders in this period.",
  "an.day": "Day",
  "an.byStore": "Per store",
  "an.store": "Store",
  "an.topSkus": "Best sellers",
  "an.sku": "SKU",
  "an.name": "Name",
  "an.packed": "Packed",
  "an.handed": "Handed over",
  "an.late": "Shipped late",
  "an.overdue": "Past ship-by",
  "an.overdueHint": "Not handed over yet",
  "an.toPacked": "to packed",
  "an.packedToHanded": "packed to courier",
  "an.toHanded": "Order to courier",
  "an.hours": "{n} h",
  "an.perDay": "Per day (orders placed that day)",
  "an.heldNow": "Held now",
  "an.reason": "Reason",
  "an.noHeld": "No orders held.",
  "an.fulfillmentHelp": "Of the orders placed in the period (not cancelled). Packing and handover times come from warehouses running WMS; times are medians.",
  "an.tab.products": "Products & stock",
  "an.ins.title": "Insight",
  "an.ins.help": "Patterns found in the last weeks, most urgent first.",
  "an.ins.none": "Nothing unusual lately.",
  "an.ins.seeProducts": "See products",
  "an.ins.seeOrders": "See orders",
  "an.ins.seeFulfillment": "See fulfilment",
  "an.ins.sales_up": "Revenue in the last 7 days is up {pct}% on the week before ({revenue} vs {previous}).",
  "an.ins.sales_down": "Revenue in the last 7 days is down {pct}% on the week before ({revenue} vs {previous}).",
  "an.ins.orders_spike": "Order spike on {day}: {orders} orders, about {usual} on a usual day.",
  "an.ins.orders_drop": "Orders dropped on {day}: {orders}, about {usual} on a usual day. Check the stores' sync.",
  "an.ins.sku_returns": "{code} is returned a lot: {pct}% ({returned} of {sold} units in 30 days).",
  "an.ins.store_failures": "{store}: {pct}% of orders cancelled or returned in 30 days ({cancelled} cancelled, {returned} returned).",
  "an.ins.orders_overdue": "{orders} orders are past their ship-by and not handed over yet.",
  "an.ins.shipping_late": "{pct}% of parcels in the last 7 days went to the courier after their ship-by ({late} of {handed}).",
  "an.ins.orders_held": "{orders} orders are held; most for: {reason} ({reasonOrders}).",
  "an.ins.stock_out": "{skus} SKUs that sell are out of stock.",
  "an.ins.stock_low": "{skus} SKUs run out within a week at the current rate.",
  "an.ins.stock_dead": "{skus} SKUs ({units} units) have stock but did not sell in {days} days.",
  "an.classA": "Class A",
  "an.classB": "Class B",
  "an.classC": "Class C",
  "an.skus": "{n} SKUs",
  "an.ofRevenue": "{pct}% of revenue",
  "an.runningOut": "Running out",
  "an.runningOutHint": "Lasts under 2 weeks",
  "an.runningOutHelp": "Available stock at the last 30 days' sales rate.",
  "an.noRunningOut": "Nothing runs out within two weeks.",
  "an.dead": "Not selling",
  "an.deadHint": "{units} units, no sale in 60 days",
  "an.pareto": "Pareto (ABC)",
  "an.paretoHelp": "A: the SKUs making the first 80% of revenue; B: the next 15%; C: the rest.",
  "an.share": "Share",
  "an.cumShare": "Cumulative",
  "an.available": "Available",
  "an.soldPerDay": "Sold/day",
  "an.cover": "Lasts",
  "an.empty0": "Out",
  "an.days": "{n} d",
  "an.incoming": "On PO",
  "nav.couriers": "Couriers",
  "courier.title": "Couriers",
  "courier.help": "Stores spell couriers their own way (J&T at Shopee is not J&T at TikTok). Map every name to one courier here: orders, pools, waves and handovers use the mapped name. Instant and same-day couriers are booked only when the parcel is packed, so the driver does not come before it is ready.",
  "courier.add": "Add courier",
  "courier.edit": "Edit courier",
  "courier.list": "Courier list",
  "courier.kind": "Service",
  "courier.kindHelp": "Instant and same day: booked when packed, not when the order is processed.",
  "courier.kind.regular": "Regular",
  "courier.kind.instant": "Instant",
  "courier.kind.sameday": "Same day",
  "courier.kind.cargo": "Cargo",
  "courier.names": "Store names",
  "courier.delete": "Remove courier",
  "courier.deleteConfirm": "Remove {name}? Its {n} store names become unmapped.",
  "courier.storeNames": "Names from stores",
  "courier.unmapped": "{n} not mapped",
  "courier.unmappedOnly": "Not mapped only",
  "courier.noNames": "No courier names from stores yet",
  "courier.platform": "Marketplace",
  "courier.storeName": "Name sent",
  "courier.orders30": "Orders (30 days)",
  "courier.mappedTo": "Courier",
  "courier.pick": "Pick a courier",
  "wms.pool.courierKindsHelp": "E.g. a pool for instant and same day, with a high priority.",
  "error.courier_name_taken": "Another courier already has this name.",
  "wms.count.source.spot": "On the spot",
  "wms.count.source.schedule": "Scheduled",
  "wms.cycle.title": "Cycle count schedule",
  "wms.cycle.help": "Every day the system makes one cycle count of the locations that are due, by the class of what they hold (A, B, C by revenue over the last 90 days; unsold is C). Locations where picking found less than the books go first. 0 days: never for that class.",
  "wms.cycle.saved": "Schedule saved.",
  "wms.cycle.days": "days",
  "wms.cycle.classA": "Class A every",
  "wms.cycle.classB": "Class B every",
  "wms.cycle.classC": "Class C every",
  "wms.cycle.max": "Locations per day at most",
  "wms.cycle.maxHelp": "What one day's count holds; the rest waits for the next day.",
  "wms.cycle.status": "{due} locations are due now. Last run: {last}.",
  "wms.var.missingValue": "Missing (at cost)",
  "wms.var.foundValue": "Found (at cost)",
  "wms.var.value": "Value (cost)",
  "wms.var.confirmValue": "Effect on profit: {value}",
  "report.stockVariance": "Stock variance",
  "report.stockVarianceHelp": "Variance confirmed in the period, at average cost, for the whole workspace (not per store). Unconfirmed variance is not counted yet.",
  "report.varianceLoss": "Loss (missing)",
  "report.varianceGain": "Gain (found)",
  "report.afterVariance": "After fees and variance",
  "report.varianceNoCost": "{n} units confirmed while their SKU had no cost; they are not in this value.",
  "nav.finance.books": "Closing the books",
  "logs.entity.books": "Books",
  "books.title": "Closing the books",
  "books.help": "At a month's end its figures become final: Zerpid keeps what the books said (profit and loss, stock value, cash, payables) and nothing dated in that month can be recorded or voided any more. A mistake found later is corrected in an open month. Months close in order, once over; only the owner can reopen the latest closed month.",
  "books.closedThrough": "The books are closed through {date}. Entries, payments and bills dated up to then are locked.",
  "books.neverClosed": "No month is closed yet. The first close also locks every month before it.",
  "books.month": "Month",
  "books.status.current": "Running",
  "books.status.open": "Open",
  "books.status.closed": "Closed",
  "books.closedBy": "Closed by",
  "books.close": "Close the books",
  "books.view": "View",
  "books.reopen": "Reopen",
  "books.reopenTitle": "Reopen {month}?",
  "books.reopenBody": "Its dates become editable again. The close stays on record; close the month again when done.",
  "books.reopenReason": "Reason (required)",
  "books.reasonRequired": "Give a reason.",
  "books.reopened": "Month reopened.",
  "books.closeTitle": "Close the books of {month}",
  "books.closeBody": "Zerpid rebuilds the month's end from the stock ledger and keeps it. Then nothing dated in this month can be recorded or voided.",
  "books.closeOk": "Close the books",
  "books.closed": "{month} is closed.",
  "books.blockers": "Must be done",
  "books.warnings": "Good to check",
  "books.blocked": "Finish what must be done first.",
  "books.note": "Note (optional)",
  "books.check.open_counts": "Stock counts begun by the month's end still open: {n}",
  "books.check.open_counts.help": "Close or cancel them in Warehouse > Stock count.",
  "books.check.open_variance": "Variance from the month not confirmed: {n}",
  "books.check.open_variance.help": "Confirm or net it in Warehouse > Variance: stock lost belongs to this month's profit and loss.",
  "books.check.units_no_cost": "Units sold without a cost: {n}",
  "books.check.units_no_cost.help": "Their cost of goods counts as zero; set the cost in Finance > Cost of goods.",
  "books.check.draft_bills": "Supplier bills still drafts: {n}",
  "books.check.draft_bills.help": "Goods received but not yet billed: payables are understated.",
  "books.check.unsettled_orders": "Marketplace orders shipped but not settled yet: {n}",
  "books.check.unsettled_orders.help": "Normal at the month's start; their fees are not in the profit yet.",
  "books.check.open_transfers": "Transfers between warehouses not finished: {n}",
  "books.check.open_transfers.help": "Goods on the road are counted in the warehouse they left.",
  "books.viewTitle": "Books of {month}",
  "books.wasReopened": "This month was reopened: {reason}. The figures below are from its last close.",
  "books.pnl": "Profit and loss",
  "books.fees": "Marketplace fees",
  "books.variance": "Stock variance",
  "books.net": "Profit after fees and variance",
  "books.position": "At the month's end",
  "books.stockValue": "Stock value",
  "books.cash": "Cash and bank",
  "books.payables": "Owed to suppliers",
  "books.stockNoCost": "{n} units had no cost and are not in the stock value.",
  "books.stock": "Stock at the month's end",
  "books.qty": "Qty",
  "books.unitCost": "Average cost",
  "books.value": "Value",
  "books.history": "History",
  "books.closedLine": "Closed by {name}, {at}",
  "books.reopenedLine": "Reopened by {name}, {at}: {reason}",
  "books.closedWithWarnings": "Closed with: {list}",
  "error.period_closed": "The books of this date are closed. Record it in an open month.",
  "error.close_order": "Months close in order: close the month after the last closed one.",
  "error.month_not_over": "A month closes once it is over.",
  "error.close_blocked": "Finish the checklist first.",
  "error.reopen_latest_only": "Only the latest closed month can be reopened.",
  "error.month_not_closed": "This month is not closed.",
  "wms.in.condition.sellable": "Good",
  "wms.in.condition.damaged": "Damaged",
  "wms.in.condition.quarantine": "Check first",
  "wms.in.damagedN": "{n} damaged",
  "wms.in.quarantineN": "{n} to check",
  "po.settings": "Receiving settings",
  "po.settingsSaved": "Receiving settings saved.",
  "po.overReceipt": "Receiving more than ordered",
  "po.overReceiptHelp": "Checked per SKU on every receipt of a purchase order, from the web or the PDA.",
  "po.overReceipt.none": "Not allowed: at most what was ordered",
  "po.overReceipt.tolerance": "Allowed up to",
  "po.overReceipt.any": "Allowed, any amount",
  "error.over_receipt": "More than the purchase order allows for this SKU.",
  "nav.warehouse.pallets": "Pallets",
  "wms.plt.title": "Pallets",
  "wms.plt.add": "Print new labels",
  "wms.plt.addOk": "Make & print",
  "wms.plt.added": "{n} pallets made: {first} to {last}.",
  "wms.plt.countHelp": "Numbered on from the last (PLT-000001...). Stick them on pallets at receiving.",
  "wms.plt.size": "Label size",
  "wms.plt.helpOn": "This warehouse receives on pallets: scan a pallet before the goods. Each pallet gets its own putaway, found by scanning it.",
  "wms.plt.helpOff": "Receiving on pallets is off for this warehouse (Settings > Warehouses > Set up WMS). Labels can be printed ahead anyway.",
  "wms.plt.empty": "No pallets yet",
  "wms.plt.waiting": "Waiting for putaway ({task})",
  "wms.plt.current": "Pallet {code}",
  "wms.plt.scanFirst": "Scan a pallet first (at most {n} SKUs on one pallet).",
  "wms.plt.change": "Change",
  "wms.plt.wholeTitle": "Pallet {code}: put away whole",
  "wms.plt.wholeHelp": "Everything still on it goes into one bin, without scanning the goods again. To split it, put away line by line below.",
  "wms.plt.placeAll": "Put all here",
  "wms.plt.placed": "Pallet put away in {code}.",
  "settings.warehouses.palletReceiving": "Receive on pallets",
  "settings.warehouses.palletReceivingHelp": "Goods are scanned onto a pallet when received; each pallet is put away whole or split. Off: everything goes to TERIMA as before.",
  "settings.warehouses.palletMaxSkus": "SKUs per pallet at most",
  "settings.warehouses.palletLabel": "Pallet label size",
  "error.pallet_full": "This pallet already holds the most SKUs it may; use another pallet.",
  "error.pallet_busy": "This pallet is waiting for putaway; use another one.",
  "error.pallet_not_found": "No pallet with this code in this warehouse.",
  "error.pallet_no_putaway": "This pallet has nothing waiting for putaway.",
  "error.not_a_pallet": "Only a pallet is put away whole.",
  "orders.tracking": "Tracking no.",
  "orders.priority.all": "All priorities",
  "orders.priority.urgent": "Priority (all)",
  "orders.priority.instant": "Instant & same day",
  "orders.priority.due_soon": "Due within a day",
  "orders.priority.late": "Late",
  "orders.priority.lateTag": "Late",
  "orders.priority.soonTag": "{n}h left",
  "orders.courierFilter.all": "All couriers",
  "orders.dateBy.ordered": "Order date",
  "orders.dateBy.created": "Came into Zerpid",
  "orders.dateBy.shipped": "Shipped date",
  "error.order_picking_started": "Picking has started on this order; take it out of the wave first.",
  "orders.dateBy.shipBy": "Ship-by (SLA)",
  "orders.edit.swap": "Switch item",
  "orders.edit.swapTitle": "Switch item",
  "orders.edit.swapOk": "Switch",
  "orders.edit.swapHelp": "{name} ({code}, {n} pcs) ships as the SKU chosen below, e.g. another colour or a substitute when it is out of stock. The marketplace order itself does not change; tell the buyer in chat.",
  "orders.edit.note": "Note (optional), e.g. buyer asked for black",
  "orders.edit.swapped": "Item switched; stock reserved for the new SKU.",
  "orders.edit.switchedTo": "Switched to {code}",
  "orders.edit.lines": "Change items",
  "orders.edit.linesTitle": "Change the order's items",
  "orders.edit.linesHelp": "Quantities, items added or removed; the total follows (shipping fee and discount stay). Only until picking starts.",
  "orders.edit.addLine": "Add item",
  "orders.edit.linesSaved": "Items saved.",
  "orders.note.line_swapped": "Item switched",
  "orders.note.lines_edited": "Items changed",
  "error.order_not_editable": "Only an order not packed yet can be edited.",
  "error.line_not_swappable": "Only an item of the order can be switched.",
  "orders.peek.prev": "Previous order",
  "orders.peek.next": "Next order",
  "orders.peek.keys": "↑ ↓ to move",
  "orders.peek.openTab": "Open in tab",
  "views.title": "Saved views:",
  "views.save": "Save these filters",
  "views.saveHelp": "The filters on screen now are saved under a name; one click brings them back.",
  "views.namePlaceholder": "e.g. Late J&T Shopee",
  "views.share": "Share with the whole team",
  "views.saved": "Saved: {name}",
  "views.sharedBy": "Shared by {name}",
  "views.deleteTitle": "Delete “{name}”?",
  "orders.due.title": "Ship-by deadline:",
  "orders.due.all": "All",
  "orders.due.expired": "Late",
  "orders.due.0_3": "0–3 hours",
  "orders.due.3_6": "3–6 hours",
  "orders.due.6_12": "6–12 hours",
  "orders.due.12_48": "12–48 hours",
  "orders.due.48_plus": "> 48 hours",
  "orders.due.none": "No deadline",
  "orders.bulk.button": "Search many numbers at once",
  "orders.bulk.title": "Search many orders",
  "orders.bulk.help": "Paste order numbers or tracking numbers, one per line (or separated by commas or spaces), up to 500.",
  "orders.bulk.ok": "Search",
  "orders.bulk.active": "{n} numbers searched · {found} found",
  "nav.section.tools": "Tools",
  "nav.tools.barcodes": "Print barcodes",
  "bc.title": "Print barcodes",
  "bc.kind.location": "Locations",
  "bc.kind.tote": "Totes & carts",
  "bc.kind.pallet": "Pallets",
  "bc.kind.packaging": "Packaging",
  "bc.kind.sku": "SKUs",
  "bc.kind.custom": "Free text",
  "bc.needWms": "Locations, totes, pallets and packaging belong to a warehouse that runs WMS. Choose one at the top of the page.",
  "bc.setup": "Label",
  "bc.symbology": "Code type",
  "bc.symHelp.code128": "Reads with any scanner. Best for SKU codes and short location codes.",
  "bc.symHelp.qr": "Reads from further away and at an angle: good for high racks and long codes.",
  "bc.symHelp.ean13": "12–13 digits, for goods sold in shops. Letters cannot be EAN-13.",
  "bc.size": "Label size",
  "bc.sizeCustom": "My own size…",
  "bc.paper": "Printed on",
  "bc.paperRoll": "Label roll (label printer)",
  "bc.paperA4": "A4 sheet (office printer)",
  "bc.copies": "Copies",
  "bc.print": "Print ({n})",
  "bc.printAll": "Print every one shown ({n})",
  "bc.printTip": "Print at 100% (Actual size)",
  "bc.printTipRoll": "In the print dialog choose “Actual size” or scale 100%, not “Fit to page”, and set the printer’s paper to {w} × {h} mm. Otherwise the barcode shrinks and may not scan.",
  "bc.printTipA4": "Choose “Actual size” or scale 100% in the print dialog. The labels are laid edge to edge from the centre of the sheet.",
  "bc.cartSlots": "Cart · {n} slots (their labels come along)",
  "bc.addSku": "Add an SKU (code, name or barcode)",
  "bc.generate": "Make barcodes for {n} SKUs without one",
  "bc.generated": "{n} barcodes made and saved to the SKUs",
  "bc.skuEmpty": "Add the SKUs to label",
  "bc.sku": "SKU",
  "bc.barcode": "Barcode",
  "bc.noBarcode": "none (the SKU code prints)",
  "bc.customHelp": "One code per line. To print a note under it, add a tab or “;” after the code (pasting two columns from Excel works).",
  "nav.finance.margins": "Price margins",
  "logs.entity.cost_profile": "Store cost profile",
  "activity.cost_profile.set": "Changed a store cost profile",
  "margin.title": "Price margins",
  "margin.subtitle": "Whether today's prices still make money, and what price gives the target margin. Worked out from the cost of goods and each store's cost profile.",
  "margin.belowAlert": "Some SKUs sell below break-even.",
  "margin.skus": "{n} SKUs",
  "margin.profiles": "Cost profile per store",
  "margin.noStores": "No stores connected yet.",
  "margin.fee": "Marketplace deduction",
  "margin.fixedFee": "Fixed fee per order",
  "margin.fixedFeeHelp": "E.g. an order processing fee. Charged in full to every unit.",
  "margin.ads": "Ads",
  "margin.adsHelp": "Ad spend divided by revenue, from the ads dashboard (ROAS 10× = 10%). Enter 0 without ads.",
  "margin.target": "Target margin",
  "margin.realFee": "Real deduction, 90 days",
  "margin.realFeeNone": "Not enough data yet ({n} orders, 30 needed)",
  "margin.realFeeIs": "Real deduction over 90 days: {pct} from {n} orders",
  "margin.useReal": "Use",
  "margin.default": "Default",
  "margin.edit": "Cost profile of {store}",
  "margin.saved": "Cost profile saved.",
  "margin.totalTooHigh": "Deduction, ads and margin must be under 95% of the price.",
  "margin.search": "Search SKU or product name",
  "margin.filter.all": "All platform SKUs",
  "margin.filter.below_break_even": "Below break-even",
  "margin.filter.below_target": "Margin below target",
  "margin.filter.no_cost": "No cost of goods",
  "margin.filter.unmapped": "Not mapped",
  "margin.sellerSku": "Seller SKU",
  "margin.price": "Current price",
  "margin.cost": "Cost of goods",
  "margin.deduction": "Deduction per unit",
  "margin.margin": "Current margin",
  "margin.breakEven": "Break-even price",
  "margin.suggested": "Suggested price",
  "margin.empty": "No platform SKUs",
  "margin.problem.no_cost": "No cost of goods",
  "margin.problem.unmapped": "Not mapped",
  "fillCost.button": "Use today's cost",
  "fillCost.done": "{sold} units sold and {counted} units of stock variance now use today's cost of goods.",
  "fillCost.none": "Nothing to fill.",
  "fillCost.open": "{n} units are still without a cost: set their SKU's cost in Cost of goods first.",
  "fillCost.closed": "{n} units in closed months were left as reported.",
  "activity.sku_cost.filled": "Used today's cost for units that had none",
  "nav.warehouse.serials": "IMEI / SN",
  "wms.pack.scanItemBeforeSerial": "Several items take an IMEI: scan the item's barcode first, then its IMEI.",
  "serial.title": "IMEI / serial numbers",
  "serial.help": "Every IMEI of this warehouse: where it came in and where it went. Phones are received, put away, picked and moved by count; the IMEI is imported after receiving and named at packing.",
  "serial.search": "IMEI or SKU code",
  "serial.number": "IMEI / SN",
  "serial.status.in_stock": "In stock",
  "serial.status.shipped": "Sold / sent",
  "serial.status.removed": "Out (lost or returned to supplier)",
  "serial.status.all": "All statuses",
  "serial.inDate": "Came in",
  "serial.outDate": "Left",
  "serial.inRef": "Receipt",
  "serial.outKind": "Left as",
  "serial.outRef": "Order / document",
  "serial.empty": "No IMEI yet. Import them from a receipt (Penerimaan) or with “Import IMEI stok lama”.",
  "serial.inWarehouse": "Warehouse: {name}",
  "serial.kind.received": "Received",
  "serial.kind.shipped": "Shipped with order",
  "serial.kind.returned": "Returned by buyer",
  "serial.kind.transfer_out": "Sent to another warehouse",
  "serial.kind.transfer_in": "Arrived from another warehouse",
  "serial.kind.count_loss": "Missing at a count",
  "serial.kind.count_gain": "Found at a count",
  "serial.kind.vendor_return": "Returned to supplier",
  "serial.noSerial": "{n} no IMEI",
  "serial.noSerialHelp": "Units on hand whose IMEI is not imported yet: not for sale until it is (Penerimaan > Import IMEI).",
  "serial.receiptCount": "IMEI {n}/{of}",
  "serial.receive.count": "Quantity only",
  "serial.receive.scan": "Scan each IMEI now",
  "serial.import.button": "Import IMEI",
  "serial.import.titleFor": "Import IMEI · {code}",
  "serial.import.help": "Paste the list (one per line), scan into the box, or pick an Excel (.xlsx) or CSV file: the first column is read.",
  "serial.import.receiptHelp": "The supplier's IMEI list for the units this receipt brought. Until their IMEI is in, those units are not for sale.",
  "serial.import.file": "Pick Excel / CSV file",
  "serial.import.count": "{n} IMEI",
  "serial.import.countOf": "{n} of {max} units without IMEI",
  "serial.import.ok": "Import {n}",
  "serial.import.done": "{n} IMEI registered.",
  "serial.register.button": "Import IMEI (old stock)",
  "serial.register.pick": "Which SKU? For stock that was here before, or a receipt without its list.",
  "serial.register.help": "IMEIs of units already on hand without one. Only as many as there are such units.",
  "serial.audit.button": "Audit IMEI",
  "serial.audit.title": "Audit IMEI",
  "serial.audit.help": "Scan every IMEI on the floor (or paste the list). Nothing changes: you see what matches, what is missing and what the books do not know. Settle the differences with a count.",
  "serial.audit.allSkus": "All SKUs",
  "serial.audit.run": "Compare {n} IMEI",
  "serial.audit.matched": "{n} match the books",
  "serial.audit.missing": "On the books, not scanned",
  "serial.audit.unknown": "Scanned, not on the books",
  "serial.audit.elsewhere": "Scanned, but the books say sold or elsewhere",
  "serial.audit.after": "Missing or found units go through a count (Hitung stok); confirming the difference asks for these IMEIs.",
  "serial.var.needed": "{n} IMEI to name",
  "serial.var.missingHelp": "IMEI of the {n} missing units",
  "serial.var.foundHelp": "IMEI of the {n} units found",
  "serial.var.placeholder": "One IMEI per line",
  "serial.transfer.help": "IMEI of the {n} units leaving",
  "serial.return.title": "IMEI {serial} did not leave with this order",
  "serial.return.help": "The buyer returned a unit with another IMEI than the one shipped. Take it in only with a reason: the case is marked for checking (unboxing video, the buyer).",
  "serial.return.placeholder": "e.g. Buyer sent another unit, checked on the unboxing video",
  "serial.return.ok": "Take it in anyway",
  "serial.return.overridden": "IMEI not the one shipped: {reason}",
  "activity.inbound.serials_registered": "Imported IMEIs for a receipt",
  "activity.stock.serials_registered": "Imported IMEIs of stock on hand",
  "wms.var.tab.open": "Waiting",
  "wms.var.tab.history": "History",
  "wms.var.historySearch": "SKU code or name",
  "wms.var.historyAt": "Confirmed at",
  "wms.var.historyQty": "Difference",
  "wms.var.unitCost": "Average cost / unit",
  "wms.var.historyBy": "Confirmed by",
  "wms.var.historyEmpty": "No confirmed difference yet.",
  "wms.var.lost": "Lost {n}",
  "wms.var.noCost": "No cost then",
  "sync.safety.title": "Safety stock per warehouse",
  "sync.safety.help": "Units kept back from every store, so orders that arrive before the stores are updated do not oversell. Each store is told available minus this (never below 0). An SKU can have its own number: Products & Prices, tick the SKUs, Safety stock.",
  "sync.why.safety": "{n} kept as safety stock",
  "catalog.safety": "Safety stock",
  "catalog.safety.follow": "Warehouse's",
  "catalog.safety.own": "Own number",
  "catalog.safety.bulk": "Safety stock ({n})",
  "catalog.safety.title": "Safety stock for {n} SKUs",
  "catalog.safety.help": "Units kept back from every store for these SKUs, in every warehouse. Pick “Warehouse's” to follow the number set per warehouse in Stock sync.",
  "catalog.safety.saved": "Safety stock of {n} SKUs saved; the stores are being updated.",
  "nav.warehouse.damaged": "Damaged goods",
  "damage.title": "Damaged goods",
  "damage.noWarehouse": "Pick a warehouse first.",
  "damage.tab.stock": "Damaged stock",
  "damage.tab.history": "Disposals",
  "damage.help": "Damaged units are never sold. Decide each: Back to sellable (repaired or passed a second check, no loss), Return to supplier, or Dispose: the warehouse asks, finance approves, then the warehouse disposes of the goods with photos; their worth at average cost becomes the loss “Damaged goods”.",
  "damage.search": "SKU code or name",
  "damage.total": "{n} damaged units",
  "damage.disposeN": "Ask to dispose ({n})",
  "damage.toSupplierN": "Return to supplier ({n})",
  "damage.empty": "No damaged stock in this warehouse.",
  "damage.qty": "Qty",
  "damage.locations": "Locations",
  "damage.value": "Value",
  "damage.restore": "Back to sellable",
  "damage.dispose": "Ask to dispose",
  "damage.restoreTitle": "{code} back to sellable",
  "damage.restoreHelp": "Repaired, or passed a second check: the units are sold again. No loss is booked.",
  "damage.restoreHelpWms": "Repaired, or passed a second check: the units go to TERIMA as sellable, to be put away. No loss is booked.",
  "damage.restoreNote": "What was done (required)",
  "damage.restored": "Back to sellable.",
  "damage.restoredWms": "Back to sellable, waiting in TERIMA for putaway.",
  "damage.disposeHelp": "The units leave the stock for good and their worth at average cost becomes a loss (Damaged goods). This cannot be undone.",
  "damage.serials": "IMEI / SN of the {n} units, one per line",
  "damage.disposeValue": "Worth at average cost: {value}",
  "damage.reason": "Reason",
  "damage.reasonPlaceholder": "e.g. broken screen, cannot be repaired; thrown away with the warehouse rubbish",
  "damage.photos": "Photos",
  "damage.photosHelp": "At least one photo of the goods being disposed of, 10 at most. They go into the disposal record (berita acara).",
  "damage.disposed": "Disposed and booked as Damaged goods.",
  "damage.historySearch": "SKU or reason",
  "damage.historyEmpty": "Nothing disposed of yet.",
  "damage.at": "When",
  "damage.goods": "Goods",
  "damage.noPhoto": "No photo",
  "damage.addPhoto": "Add photo",
  "damage.ba.title": "Disposal record (Berita acara pemusnahan barang)",
  "damage.ba.intro": "On {date} the following damaged goods were disposed of:",
  "damage.ba.total": "Total",
  "damage.ba.approved": "Approved by",
  "damage.ba.print": "Print record",
  "report.damaged": "Damaged goods (disposed)",
  "cost.damaged": "Of which {n} damaged units ({value}) wait for a decision.",
  "cost.damagedLink": "Damaged goods",
  "inventory.reason.disposed": "Disposed (damaged)",
  "serial.kind.disposed": "Disposed (damaged)",
  "activity.stock.disposed": "Disposed of damaged goods",
  "activity.stock.restored": "Returned damaged goods to sellable",
  "damage.held": "{n} held",
  "damage.request": "Ask",
  "damage.requestTitle": "Ask to dispose of damaged goods",
  "damage.requestHelp": "The units are held (no return to supplier, no back to sellable) until finance approves or rejects. Photos are taken when the goods are disposed of.",
  "damage.requested": "Asked; waiting for finance to approve.",
  "damage.executeTitle": "Dispose of the goods now",
  "damage.execute": "Dispose now",
  "damage.takeFrom": "Take from: {locations}",
  "damage.photosHave": "{n} photo(s) already added",
  "damage.approve": "Approve",
  "damage.approved": "Approved; the warehouse can dispose of the goods.",
  "damage.reject": "Reject",
  "damage.rejectTitle": "Reject this disposal?",
  "damage.rejectReason": "Why (required); the units are free again",
  "damage.rejectedBy": "Rejected by",
  "damage.waitFinance": "Waiting for finance",
  "damage.people": "People",
  "damage.status.all": "All",
  "damage.status.requested": "Waiting for approval",
  "damage.status.approved": "Ready to dispose",
  "damage.status.done": "Done",
  "damage.status.rejected": "Rejected",
  "damage.ba.asked": "Asked by",
  "damage.ba.done": "Disposed by",
  "activity.stock.disposal_requested": "Asked to dispose of damaged goods",
  "activity.stock.disposal_approved": "Approved a disposal of damaged goods",
  "activity.stock.disposal_rejected": "Rejected a disposal of damaged goods",
  "nav.aftersalesClaims": "Claims",
  "claim.title": "Claims",
  "claim.pageTitle": "Claims",
  "claim.add": "Claim",
  "claim.submit": "Submit claim",
  "claim.none": "No claim yet. Claim from the marketplace or the courier when the goods came back damaged, incomplete or were lost.",
  "claim.update": "Update",
  "claim.against": "Claimed from",
  "claim.against.marketplace": "Marketplace",
  "claim.against.courier": "Courier",
  "claim.carrier": "Courier",
  "claim.amount": "Amount claimed",
  "claim.reference": "Ticket / claim no.",
  "claim.note": "Note",
  "claim.paidAmount": "Paid",
  "claim.paidOnLabel": "Paid on",
  "claim.paidOn": "{amount} paid on {date}",
  "claim.final": "A rejected or paid claim cannot be changed afterwards.",
  "claim.status.submitted": "Submitted",
  "claim.status.approved": "Approved",
  "claim.status.rejected": "Rejected",
  "claim.status.paid": "Paid",
  "claim.filter.open": "Waiting",
  "claim.filter.all": "All",
  "claim.search": "Case, order, ticket or courier",
  "claim.openSummary": "{n} waiting · {amount}",
  "claim.empty": "No claims.",
  "claim.at": "Submitted",
  "claim.case": "Case",
  "damage.claimOpen": "Claim open",
  "damage.claimOpenWarn": "Some of these came back in a return whose claim is still open. The marketplace or courier may ask for the goods or photos: dispose of them after the claim is settled.",
  "report.recovered": "Claims paid",
  "activity.aftersales.claim_created": "Submitted a claim",
  "activity.aftersales.claim_updated": "Updated a claim",
  "wms.task.source.vendor_return": "Return to supplier",
  "vr.pickingNote": "Picking {number}: {done} of {total} lines brought to KIRIM. The return ships once the picking is done.",
  "vr.pickedNote": "Picked: everything is in KIRIM ({number}). Ready to ship.",
  "vr.openPicking": "Open picking",
  "damage.tab.sources": "Causes",
  "damage.age": "Damaged for",
  "damage.days": "{n} days",
  "damage.sourcesHelp": "Units that became damaged in this warehouse over the period (the last 30 days unless dates are picked), by where they came from: buyer returns per store, receipts per supplier, handling per person. Moving damaged goods between locations does not count.",
  "damage.sourcesEmpty": "Nothing became damaged in this period.",
  "damage.source": "Cause",
  "damage.sourceWho": "Store / supplier / person",
  "damage.source.return": "Buyer return",
  "damage.source.supplier": "From the supplier",
  "damage.source.handling": "Handling",
  "damage.source.transfer": "Transfer",
  "damage.source.other": "Other",
  "report.basis": "A marketplace sale counts when its order is completed (the buyer confirmed, the return window closed); a manual order when its goods leave. A return after completion takes the sale back in the month it came.",
  "report.inTransit": "Now {n} orders are shipped but not completed: their goods ({value} at cost) are in transit, not sold yet.",
  "books.stockInTransit": "Goods in transit",
  "cost.inTransit": "Not in the value: {units} units of {n} orders shipped but not completed ({value}), in transit.",
  "error.task_started": "Work was done on this task (picked, or totes taken): give it to someone instead of freeing it.",
  "error.tote_twice": "This tote is already one of this task's.",
  "error.totes_full": "Every order of this task has its tote already.",
  "error.totes_incomplete": "Scan a tote for every order of this task first.",
  "error.task_of_return": "This task picks a return to supplier: change or cancel the return instead.",
  "error.damaged_held": "Some of these damaged units are held (a disposal or a return to supplier on its way).",
  "error.disposal_state": "This disposal is not at that step any more. Refresh the page.",
  "error.photo_required": "Add at least one photo first.",
  "error.photo_invalid": "The photo must be a JPEG or PNG picture.",
  "error.photos_full": "10 photos at most.",
  "error.photo_too_large": "The photo is too large (5 MB at most).",
  "error.claim_state": "A rejected or paid claim does not change.",
  "error.return_not_picked": "Finish the picking of this return (its units to KIRIM) first.",
  "nav.warehouse.cancelled": "Cancelled goods",
  "cx.title": "Cancelled goods",
  "cx.help": "Goods of cancelled orders still in the warehouse. A parcel packed for a cancelled order: find it at the outbound area. Still here: its goods go back to the shelves. Already with the courier: it comes back as a return (Aftersales, through the return dock with its recording). Goods picked for cancelled orders wait in totes, cart slots, PACKING or KIRIM until put back.",
  "cx.parcels": "Parcels to check ({n})",
  "cx.scanParcel": "Scan the parcel's tracking number or order number",
  "cx.noParcels": "No parcel of a cancelled order to check.",
  "cx.order": "Order",
  "cx.tracking": "Tracking no.",
  "cx.shop": "Store · courier",
  "cx.units": "Goods to put back",
  "cx.cancelledAt": "Cancelled",
  "cx.inHouse": "Still here: put back",
  "cx.withCourier": "Taken by the courier",
  "cx.checkTitle": "Parcel of order {order}",
  "cx.checkHelp": "Is the parcel still in the outbound area? Still here: unpack it, a task puts its goods back on the shelves. Already with the courier: it comes back as a return.",
  "cx.notFound": "No parcel of a cancelled order has {code}.",
  "cx.noUnits": "Nothing waits to go back to the shelves.",
  "cx.location": "Where",
  "cx.qty": "Qty",
  "cx.exOrders": "Ex orders",
  "cx.putBack": "Put back on shelves",
  "cx.taskMade": "Task made: put the goods back on the shelves.",
  "cx.toReturn": "Return opened: the goods come back through the return dock.",
  "cx.checked": "Checked.",
  "as.resolution.lost": "Lost by the courier",
  "as.dock.damagedPlace": "Damaged goods to (RUSAK when empty)",
  "report.lost": "Lost by the courier",
  "books.lost": "Lost by the courier",
  "error.parcel_checked": "This parcel was checked already.",
  "error.not_lost": "Only a case still waiting for its goods (nothing received) closes as lost.",
  "error.location_kind": "That location is not of the right kind.",
  "error.ai_unavailable": "AI writing is not set up for Zerpid yet.",
  "error.ai_quota": "Today's AI drafts for this workspace are used up. Try again tomorrow.",
  "error.ai_failed": "The AI could not write a draft this time. Try again.",
  "nav.listings": "Store listings",
  "nav.prices": "Prices",
  "listings.title": "Store listings",
  "listings.subtitle": "Everything your connected stores sell. Downloaded when a store connects, and mapped by itself when the seller SKU equals an SKU code.",
  "listings.product": "Zerpid product",
  "listings.openProduct": "Open product",
  "listings.makeProduct": "Make product",
  "listings.makeProductN": "Make products ({n})",
  "listings.makeProductTip": "This listing becomes a master product. When every variant is already a variant of another product (the same goods on another store), it joins that product.",
  "listings.productUnsupported": "Products from listings are not supported for this marketplace yet.",
  "listings.madeProducts": "{n} listings became products.",
  "listings.madeProductsFailed": "{n} failed: {error}",
  "listings.createAll": "Create SKUs for all",
  "listings.createAll.tip": "Create internal SKUs for every unmapped listing the filters show, after a preview.",
  "listings.createAll.title": "Create SKUs for everything unmapped",
  "listings.createAll.help": "The SKU code is the seller SKU. Listings with the same seller SKU map to one SKU. Those marked as not tracked are left out.",
  "listings.createAll.summary": "{fresh} new SKUs, {reused} use an existing SKU, {skipped} skipped.",
  "listings.createAll.truncated": "Only the first 2,000 listings are handled. Run it again afterwards for the rest.",
  "listings.createAll.empty": "No unmapped listings for these filters.",
  "listings.createAll.listings": "Listings",
  "listings.createAll.action": "Action",
  "listings.createAll.new": "New SKU",
  "listings.createAll.reuse": "Existing SKU",
  "listings.createAll.skip.invalid": "Skipped: invalid code",
  "listings.createAll.skip.archived": "Skipped: SKU archived",
  "listings.createAll.ok": "Create & map ({n})",
  "listings.createAll.done": "{n} listings mapped.",
  "listings.createAll.failed": "{n} could not be mapped, for example over the plan's SKU limit.",
  "pricing.title": "Prices",
  "pricing.subtitle": "Base and minimum price per SKU, each store's markup, and the price on every store.",
  "pricing.margins": "Price margins",
  "pricing.promos": "Marketplace promotions",
  "pricing.markups": "Markup per store",
  "pricing.markupsHelp": "The Base price rule of bulk price changes uses the SKU's base price plus the store's markup. Example: a base of Rp100,000 with Shopee +8% becomes Rp108,000.",
  "pricing.markupEdit": "Markup of {store}",
  "pricing.markupDialogHelp": "Percent of the base price, -50% to +100%. Store prices change only when you apply the Base price rule.",
  "pricing.markupSaved": "Markup saved.",
  "pricing.problem.all": "All SKUs",
  "pricing.problem.below_min": "Under the minimum price",
  "pricing.problem.failed": "Sync failed",
  "pricing.problem.no_base": "No base price",
  "pricing.setBaseN": "Set base price ({n})",
  "pricing.setBaseOne": "Base & minimum price of {code}",
  "pricing.setBaseMany": "Base & minimum price of {n} SKUs",
  "pricing.base": "Base price",
  "pricing.min": "Minimum price",
  "pricing.baseHelp": "The price before each store's markup. Saving it does not change store prices.",
  "pricing.minHelp": "No price change, one by one or in bulk, may go under this.",
  "pricing.from.value": "Enter",
  "pricing.from.store": "From a store's price",
  "pricing.from.clear": "Clear",
  "pricing.from.keep": "Keep",
  "pricing.baseSaved": "Base and minimum prices saved.",
  "pricing.onStores": "Price on the stores",
  "pricing.notSold": "Not sold on any store yet",
  "pricing.empty": "No SKUs yet.",
  "pricing.storePrice": "Price of {code} on {store}",
  "price.mode.base": "Base price + store markup",
  "price.baseHelp": "Each store's price = the SKU's base price plus the store's markup (Prices menu), then rounded. SKUs without a base price are skipped.",
  "price.problem.no_base_price": "No base price, skipped",
  "price.problem.below_floor": "Under the minimum price, skipped",
  "price.problem.below_break_even": "Under break-even",
  "price.belowFloor": "The price is under this SKU's minimum price.",
  "price.belowFloorIs": "Under the minimum price of {min}.",
  "field.below_floor": "Under the minimum price",
  "activity.product.base_price_set": "Changed base/minimum price",
  "activity.channel.price_markup_set": "Changed a store's markup",
  "activity.listing.linked": "Linked a store listing to a product",
  "error.import_store_taken": "This product is already another listing of the same store.",
};
